// App.js
// Created: 2026-03-09 21:43:03
// Updated: 2026-03-13 15:24:48 EDT
// Project: Giving Words to Voices
// Author: Michael Schwarz
// Version: 1.7.96
// 1. React ecosystem
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Settings, Sun, Moon, BookOpen, Palette, Folder, Keyboard, Type,
  Download, DownloadCloud, CheckSquare, Trash2, RotateCcw, Wand2, PenLine,
  AlertTriangle, FolderOpen, Film, FileText, File, ChevronRight,
  ChevronUp, ChevronDown, ArrowRight, X, Check, Clock, Loader, PackageOpen,
  ZoomIn, Server, HardDrive, RefreshCw, Upload, Sliders, Plus,
  Copy, Edit3, Save, Search, Headphones, QrCode, History,
  Bot, Megaphone, MapPin, User, Lock, Hammer, Skull, Train,
  Bird, Home, Navigation, AlertCircle, Sparkles, Edit2, Radio,
  Users, Heart, Baby, UserCheck, GitBranch, GitMerge, Link2,
  Activity, ListChecks, Radio as RadioIcon,
  Scissors, Play, ThumbsUp, ThumbsDown, Flag, MessageSquare, Video, FastForward,
  GripVertical, Pin, SkipBack, SkipForward,
  Shield, ListOrdered, GitFork, HelpCircle,
  LogIn, LogOut, UserPlus, KeyRound, Mail, Eye, EyeOff, UserCog, ArrowLeft, Timer, LayoutDashboard, Star, Globe, Mic,
  Maximize2, Minimize2, Terminal,
} from 'lucide-react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import WaveSurfer from 'wavesurfer.js';

// 2. Internal — base
import { AppLogoMark } from './LogoEditor';
import { IconStrokeContext, Icon } from './components/Icon';

// 4. Constants
import { BACKEND_URL, VIDEO_SERVER_URL } from './constants';
import { COLOR_SCHEMES, FONT_OPTIONS, resolveScheme, contrastText } from './constants/theme';
import { Z } from './constants/tokens';
import Button from './components/ui/Button';
import Badge from './components/ui/Badge';
import Input from './components/ui/Input';
import Modal from './components/ui/Modal';
import { CLIP_CATEGORIES, CLIP_STATUS_STYLE } from './constants/clips';
import { getLanguageCode, getLanguageFlag, getLanguageMeta, getLanguageName, normalizeLanguageKey } from './constants/languages';
import { useI18n } from './i18nContext';

// 5. Utils
import { encodeVideoPath, buildMp4Candidates, getProjectRoot as getVideoProjectRoot } from './utils/videoUtils';
import { fmtSecs } from './utils/clipsUtils';
// applyLogoMask moved to QRDesignerPanel
import './utils/axiosConfig';

// 6. Components
// ActivityLog removed — replaced by toast notification history
import AdminDashboardPage from './components/AdminDashboardPage';
import AccessRequestsPage from './components/AccessRequestsPage';
import ConversionQueue from './components/ConversionQueue';
import ClearableField from './components/ClearableField';
import LoadFailure from './components/LoadFailure';
import DashboardPanel from './components/DashboardPanel';
import ProjectKanbanPanel from './components/ProjectKanbanPanel';
import QRDesignerPanel from './components/QRDesignerPanel';
import PromptLibraryPanel from './components/PromptLibraryPanel';
import MergeReviewPanel from './components/MergeReviewPanel';
import LoginModal from './components/LoginModal';
import ProfileModal from './components/ProfileModal';
import QAPanel from './components/QAPanel';
import RightColumn from './components/RightColumn';
import SubjectEditForm from './components/SubjectEditForm';
import ToastBar from './components/ToastBar';
import { ToastProvider } from './toastContext';
import { ConfirmProvider } from './confirmContext';
import ConfirmDialog from './components/ConfirmDialog';
import { PromptProvider } from './promptContext';
import PromptDialog from './components/PromptDialog';
import UsersPanel from './components/UsersPanel';
import WorkflowGuide from './components/WorkflowGuide';
import PageHelp from './components/PageHelp';
import LanguageSelector from './components/LanguageSelector';

const SHORTCUT_ACTIONS = [
  { id: 'playPause', label: 'Play / Pause', hint: 'Toggle the current video' },
  { id: 'markError', label: 'Mark Error', hint: 'Create an error mark at the playhead' },
  { id: 'markQuote', label: 'Mark Quote', hint: 'Mark current cue as a memorable quote' },
  { id: 'seekForward', label: 'Skip Forward', hint: 'Jump ahead 5 seconds' },
  { id: 'seekBack', label: 'Skip Back', hint: 'Jump back 5 seconds' },
];

const DEFAULT_SHORTCUT_BINDINGS = {
  playPause:   { code: 'Space', shift: false, alt: false, ctrl: false, meta: false },
  markError:   { code: 'KeyE', shift: true,  alt: false, ctrl: false, meta: false },
  markQuote:   { code: 'KeyQ', shift: true,  alt: false, ctrl: false, meta: false },
  seekForward: { code: 'ArrowRight', shift: false, alt: false, ctrl: false, meta: false },
  seekBack:    { code: 'ArrowLeft', shift: false, alt: false, ctrl: false, meta: false },
};

function getWorkingDirLanguageLabel(item) {
  const raw = String(item?.video_id || item?.subtitle_file || '').replace(/\.(vtt|srt)$/i, '');
  const match = raw.match(/[._]([a-z]{2,3})$/i);
  if (!match) return null;
  const name = getLanguageName(match[1].toLowerCase());
  // getLanguageName returns the code itself (capitalized) if unknown — treat as no match
  return name.length <= 3 ? null : name;
}

function isModifierOnlyCode(code = '') {
  return ['ShiftLeft', 'ShiftRight', 'ControlLeft', 'ControlRight', 'AltLeft', 'AltRight', 'MetaLeft', 'MetaRight'].includes(code);
}

function shortcutCodeLabel(code = '') {
  const map = {
    Space: 'Space',
    ArrowLeft: 'Left',
    ArrowRight: 'Right',
    ArrowUp: 'Up',
    ArrowDown: 'Down',
    Escape: 'Esc',
    Enter: 'Enter',
    Backspace: 'Backspace',
    Delete: 'Delete',
    Tab: 'Tab',
  };
  if (map[code]) return map[code];
  if (/^Key[A-Z]$/.test(code)) return code.slice(3);
  if (/^Digit\d$/.test(code)) return code.slice(5);
  if (/^Numpad\d$/.test(code)) return `Num ${code.slice(6)}`;
  if (/^F\d{1,2}$/.test(code)) return code;
  return code || 'Key';
}

function sanitizeShortcutBinding(binding) {
  if (!binding || typeof binding !== 'object' || typeof binding.code !== 'string' || !binding.code.trim()) {
    return null;
  }
  return {
    code: binding.code,
    shift: !!binding.shift,
    alt: !!binding.alt,
    ctrl: !!binding.ctrl,
    meta: !!binding.meta,
  };
}

function normalizeShortcutBindings(raw) {
  const next = { ...DEFAULT_SHORTCUT_BINDINGS };
  if (!raw || typeof raw !== 'object') return next;
  Object.keys(DEFAULT_SHORTCUT_BINDINGS).forEach((id) => {
    if (raw[id] === null) {
      next[id] = null;
      return;
    }
    const sanitized = sanitizeShortcutBinding(raw[id]);
    if (sanitized) next[id] = sanitized;
  });
  return next;
}

function shortcutBindingKey(binding) {
  const sanitized = sanitizeShortcutBinding(binding);
  if (!sanitized) return '';
  return [
    sanitized.ctrl ? 'ctrl' : '',
    sanitized.alt ? 'alt' : '',
    sanitized.shift ? 'shift' : '',
    sanitized.meta ? 'meta' : '',
    sanitized.code,
  ].filter(Boolean).join('|');
}

function formatShortcutBinding(binding) {
  const sanitized = sanitizeShortcutBinding(binding);
  if (!sanitized) return 'Unassigned';
  const parts = [];
  if (sanitized.ctrl) parts.push('Ctrl');
  if (sanitized.alt) parts.push('Alt');
  if (sanitized.shift) parts.push('Shift');
  if (sanitized.meta) parts.push('Cmd');
  parts.push(shortcutCodeLabel(sanitized.code));
  return parts.join('+');
}

function bindingMatchesEvent(binding, event) {
  const sanitized = sanitizeShortcutBinding(binding);
  if (!sanitized) return false;
  return event.code === sanitized.code
    && event.shiftKey === sanitized.shift
    && event.altKey === sanitized.alt
    && event.ctrlKey === sanitized.ctrl
    && event.metaKey === sanitized.meta;
}

function captureShortcutBinding(event) {
  if (!event || isModifierOnlyCode(event.code)) return null;
  return {
    code: event.code,
    shift: event.shiftKey,
    alt: event.altKey,
    ctrl: event.ctrlKey,
    meta: event.metaKey,
  };
}

const _ts = () => { const d = new Date(); return `${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}_${String(d.getHours()).padStart(2,'0')}${String(d.getMinutes()).padStart(2,'0')}${String(d.getSeconds()).padStart(2,'0')}`; };

// Header icon controls (Help / Shortcuts / Gear) — real <button>s with the
// browser's default chrome reset so they render identically to the 28px
// bordered boxes they replaced (content-box keeps the 1px border outside).
const headerIconButtonStyle = {
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '28px',
  width: '28px',
  borderRadius: '6px',
  border: '1px solid transparent',
  background: 'none',
  padding: 0,
  margin: 0,
  color: 'inherit',
  boxSizing: 'content-box',
};

// ── Whisper server log viewer (admin only) ─────────────────────────────────
function WhisperLogsModal({ axiosAuth, onClose, theme, whisperStatus }) {
  const [logs, setLogs] = useState(null);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const termRefStdout = useRef(null);
  const termRefStderr = useRef(null);

  const fetchLogs = useCallback(async () => {
    try {
      const r = await axiosAuth.get('/transcribe/logs', { params: { lines: 300 } });
      if (r.data?.error) {
        setError(r.data.error);
        setLogs(null);
      } else {
        setLogs(r.data);
        setError(null);
      }
    } catch (e) {
      setError(e.message || 'Failed to fetch logs');
    }
  }, [axiosAuth]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(fetchLogs, 3000);
    return () => clearInterval(t);
  }, [autoRefresh, fetchLogs]);

  // Auto-scroll terminals to bottom when logs update
  useEffect(() => {
    if (termRefStdout.current) termRefStdout.current.scrollTop = termRefStdout.current.scrollHeight;
    if (termRefStderr.current) termRefStderr.current.scrollTop = termRefStderr.current.scrollHeight;
  }, [logs]);

  // Escape-to-close is handled by the Modal primitive.

  const statusColor = whisperStatus === 'ok' ? '#4ade80' : whisperStatus === 'down' ? '#ef4444' : '#a0a0b0';
  const statusLabel = whisperStatus === 'ok' ? 'Online' : whisperStatus === 'down' ? 'Down' : 'Checking…';

  return (
    <Modal
      open
      onClose={onClose}
      theme={theme}
      width={960}
      zIndex={Z.modal}
      backdropStyle={{ background: 'rgba(0,0,0,0.85)' }}
      bodyStyle={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
      header={(
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: statusColor, boxShadow: `0 0 8px ${statusColor}` }} />
            <strong style={{ color: theme.text, fontSize: '0.95rem' }}>Whisper Server</strong>
            <span style={{ fontSize: '0.75rem', color: statusColor }}>{statusLabel}</span>
            <span style={{ fontSize: '0.7rem', color: theme.textSub, fontFamily: 'monospace' }}>host.docker.internal:8200</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: theme.textSub, cursor: 'pointer' }}>
              <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
              Auto-refresh
            </label>
            <Button theme={theme} variant="outline" size="sm" onClick={fetchLogs}>Refresh</Button>
          </div>
        </div>
      )}
    >
          {error && (
            <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 6, color: '#fca5a5', fontSize: '0.8rem' }}>
              {error}
            </div>
          )}

          {logs && Object.entries(logs).map(([filename, content], idx) => (
            <div key={filename}>
              <div style={{ fontSize: '0.7rem', color: theme.textSub, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {filename}
              </div>
              <pre
                ref={idx === 0 ? termRefStdout : termRefStderr}
                style={{
                  margin: 0, padding: '12px 14px',
                  background: '#0a0a12', color: '#d4d4d4',
                  border: '1px solid #2a2a45', borderRadius: 6,
                  fontFamily: "'SF Mono', Menlo, Consolas, monospace",
                  fontSize: '0.72rem', lineHeight: 1.5,
                  maxHeight: '280px', overflow: 'auto',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                }}
              >
                {content || '(empty)'}
              </pre>
            </div>
          ))}

          {!logs && !error && (
            <div style={{ color: theme.textSub, fontSize: '0.85rem', textAlign: 'center', padding: '40px 0' }}>
              Loading logs…
            </div>
          )}
    </Modal>
  );
}


function App() {
  const { locale: i18nLocale, setLocale: setI18nLocale, t, languages: i18nLanguages } = useI18n();

  // ============================================================
  // [0] AUTH STATE — must be first so axiosAuth is available to all effects
  // ============================================================
  const [authToken,   setAuthToken]   = useState(() => sessionStorage.getItem('gwv_token') || '');
  const [currentUser, setCurrentUser] = useState(() => {
    try { const t = sessionStorage.getItem('gwv_token');
      if (!t) return null;
      const b64 = t.split('.')[1]; const pad = b64 + '='.repeat(-b64.length % 4);
      return JSON.parse(atob(pad.replace(/-/g,'+').replace(/_/g,'/')));
    } catch { return null; }
  });
  const [authChecked, setAuthChecked] = useState(false);
  const [authEnabled, setAuthEnabled] = useState(true);
  const [googleClientId, setGoogleClientId] = useState('');
  const [cloudTtsAvailable, setCloudTtsAvailable] = useState(false);
  const [showLogin,   setShowLogin]   = useState(false);
  const isDevEnvironment = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const { hostname, port } = window.location;
    return hostname.startsWith('dev.')
      || hostname === 'localhost'
      || hostname === '127.0.0.1'
      || port === '3000';
  }, []);

  // ── Access request notifications ──
  const [accessRequests,     setAccessRequests]     = useState([]); // pending join requests

  // ── Role permissions (from Access Control Board) ──
  const [userPermissions, setUserPermissions] = useState(null);

  // ── Idle timeout & session clock ──
  const IDLE_OPTIONS = [5, 10, 15, 20, 30]; // minutes
  const [idleMinutes,      setIdleMinutes]      = useState(() => {
    const saved = sessionStorage.getItem('gwv_idle_minutes');
    return saved ? parseInt(saved) : 15;
  });
  const [showIdleWarning,  setShowIdleWarning]  = useState(false);
  const [idleCountdown,    setIdleCountdown]    = useState(60); // seconds remaining in warning
  const [loginTime,        setLoginTime]        = useState(null); // Date when session started
  const [sessionDisplay,   setSessionDisplay]   = useState(''); // "5m" / "1h 23m"
  const [sessionSummary,   setSessionSummary]   = useState({ total_seconds: 0, total_hours: 0, session_count: 0, latest_session: null });
  const [appShellOffset,   setAppShellOffset]   = useState(98);
  const [showUserMenu, setShowUserMenu]   = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showUsersModal, setShowUsersModal]     = useState(false);
  const idleTimerRef    = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownRef    = useRef(null);
  const sessionIdRef    = useRef(null); // backend user_sessions.id
  const trackedActivityRef = useRef({ id: null, key: '', startedAt: 0 });
  const warningActiveRef = useRef(false); // mirror of showIdleWarning for use inside callbacks
  const doLogoutRef     = useRef(null);  // always-current doLogout reference
  const headerShellRef  = useRef(null);

  // Axios instance that always includes the Bearer token
  const axiosAuth = React.useMemo(() => {
    const instance = axios.create({ baseURL: BACKEND_URL });
    instance.interceptors.request.use(cfg => {
      const tok = sessionStorage.getItem('gwv_token');
      if (tok) cfg.headers['Authorization'] = `Bearer ${tok}`;
      return cfg;
    });
    instance.interceptors.response.use(r => r, err => {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem('gwv_token');
        setAuthToken(''); setCurrentUser(null); setShowLogin(true);
      }
      return Promise.reject(err);
    });
    return instance;
  }, []);

  // Reliably close the user_sessions row when the tab is closed/hidden.
  // Without this, sessions are only closed via the explicit Logout button —
  // tab closes, network drops, and refreshes leave them open forever.
  // Uses fetch+keepalive (allows Authorization header, fires during pagehide).
  useEffect(() => {
    if (!authToken) return;
    const fireSessionEnd = () => {
      const sid = sessionIdRef.current;
      const tok = authToken;
      if (!sid || !tok) return;
      const dur = loginTime ? Math.floor((Date.now() - loginTime.getTime()) / 1000) : null;
      try {
        fetch(`${BACKEND_URL}/auth/session/end`, {
          method: 'POST',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tok}`,
          },
          body: JSON.stringify({ session_id: sid, duration_seconds: dur }),
        }).catch(() => {});
      } catch {}
    };
    const onPageHide = () => fireSessionEnd();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') fireSessionEnd();
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [authToken, loginTime]);

  const doLogout = (durationSeconds) => {
    endTrackedActivity(durationSeconds);
    // Record session end before clearing token
    if (sessionIdRef.current && authToken) {
      const dur = durationSeconds ?? (loginTime ? Math.floor((Date.now() - loginTime.getTime()) / 1000) : null);
      axios.post(`${BACKEND_URL}/auth/session/end`,
        { session_id: sessionIdRef.current, duration_seconds: dur },
        { headers: { Authorization: `Bearer ${authToken}` } }
      ).catch(() => {});
      sessionIdRef.current = null;
    }
    sessionStorage.removeItem('gwv_token');
    sessionStorage.removeItem('gwv_dismissedNoSubtitles'); setNoSubtitlesDismissed(false);
    localStorage.setItem('appMode', 'home');
    setAppMode('home');
    setAuthToken(''); setCurrentUser(null); setShowLogin(true);
    setUserPrefs(null); setAssignedSubjects([]);
    setShowShortcutsPalette(false); setListeningShortcutId(null);
    setLoginTime(null); setSessionDisplay(''); setShowIdleWarning(false);
    setSessionSummary({ total_seconds: 0, total_hours: 0, session_count: 0, latest_session: null });
    if (idleTimerRef.current)    clearTimeout(idleTimerRef.current);
    if (countdownRef.current)    clearInterval(countdownRef.current);
  };

  const doLogin = (token, user) => {
    sessionStorage.setItem('gwv_token', token);
    setAuthToken(token); setCurrentUser(user); setShowLogin(false);
    setLoginTime(new Date());
    // Always land on dashboard on fresh login
    setAppMode('home');
    localStorage.setItem('appMode', 'home');
    // Save boot_id on fresh login
    axios.get(`${BACKEND_URL}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.data.boot_id) localStorage.setItem('gwv_boot_id', r.data.boot_id); })
      .catch(() => {});
    // Start backend session record
    axios.post(`${BACKEND_URL}/auth/session/start`, {},
      { headers: { Authorization: `Bearer ${token}` } }
    ).then(r => { sessionIdRef.current = r.data.session_id; }).catch(() => {});
    // Admin: fetch pending access requests and show notification modal
    if (user?.role === 'admin') {
      axios.get(`${BACKEND_URL}/admin/access-requests`,
        { headers: { Authorization: `Bearer ${token}` } }
      ).then(r => {
        if (r.data?.length > 0) {
          setAccessRequests(r.data);
        }
      }).catch(() => {});
    }
    // Load role permissions for access control
    axios.get(`${BACKEND_URL}/admin/permissions/my`,
      { headers: { Authorization: `Bearer ${token}` } }
    ).then(r => setUserPermissions(r.data?.permissions || null)).catch(() => {});
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${BACKEND_URL}/user/preferences`, authHeaders).then(r => {
      const prefs = r.data || {};
      const rawAssigned = prefs.assigned_subjects || [];
      const assignments = rawAssigned.map(a => typeof a === 'string' ? { project_root: a } : a);
      const assigned = assignments.map(a => a.project_root);
      setUserPrefs(prefs);
      setSubjectAssignments(assignments);
      setAssignedSubjects(assigned);

      // Everyone lands on the dashboard after login.
      // Pre-load working directory in the background so it's ready when they navigate to editor.
      setAppMode('home');
      localStorage.setItem('appMode', 'home');

      if (user?.role !== 'admin' && assigned.length > 0) {
        // Non-admin with assignments: pre-load working directory in background
        if (prefs.working_directory) {
          axios.get(`${BACKEND_URL}/working-dir`, authHeaders).then(wd => {
            setWorkingDirInfo(wd.data || null);
            setActiveConfig(prev => ({
              ...prev,
              active_display_path: wd.data?.display_path || prefs.working_directory,
              active_subdir: wd.data?.working_subdir || prefs.working_directory,
            }));
          }).catch(() => {});
        } else {
          // Auto-set working directory from first assignment
          axios.get(`${BACKEND_URL}/pipeline/projects`, authHeaders).then(pr => {
            const projects = pr.data || [];
            const defaultProj = prefs.default_project;
            const match = defaultProj && projects.find(p => p.project_root === defaultProj);
            const proj = match || projects[0];
            if (proj) {
              const subdir = proj.file_subdir || '';
              axios.put(`${BACKEND_URL}/user/preferences`, { working_directory: subdir }, authHeaders).then(() => {
                axios.get(`${BACKEND_URL}/working-dir`, authHeaders).then(wd => {
                  setWorkingDirInfo(wd.data || null);
                  setActiveConfig(prev => ({ ...prev, active_display_path: wd.data?.display_path || subdir, active_subdir: wd.data?.working_subdir || subdir }));
                }).catch(() => {});
              }).catch(() => {});
            }
            if (projects.length > 1) setProjectChooserList(projects);
          }).catch(() => {});
        }
      } else {
        // Admin or user with no assignments: pre-load default project if set,
        // otherwise fetch the project list and open the chooser so the admin
        // lands on a working project instead of an empty editor.
        const defaultProj = prefs.default_project;
        axios.get(`${BACKEND_URL}/pipeline/projects`, authHeaders).then(pr => {
          const projects = pr.data || [];
          const match = defaultProj ? projects.find(p => p.project_root === defaultProj) : null;
          if (match) {
            const subdir = match.file_subdir || '';
            axios.put(`${BACKEND_URL}/user/preferences`, { working_directory: subdir }, authHeaders).then(() => {
              axios.get(`${BACKEND_URL}/working-dir`, authHeaders).then(wd => {
                setWorkingDirInfo(wd.data || null);
                setActiveConfig(prev => ({ ...prev, active_display_path: wd.data?.display_path || subdir, active_subdir: wd.data?.working_subdir || subdir }));
              }).catch(() => {});
            }).catch(() => {});
          } else if (projects.length > 0) {
            // No valid default — surface the chooser so the admin picks one.
            setProjectChooserList(projects);
          }
        }).catch(() => {});
      }
    }).catch(() => {
      setAppMode('home');
      localStorage.setItem('appMode', 'home');
    });
  };

  const refreshSessionSummary = useCallback(() => {
    const tok = sessionStorage.getItem('gwv_token');
    if (!tok) return;
    axios.get(`${BACKEND_URL}/auth/session/summary`, {
      headers: { Authorization: `Bearer ${tok}` }
    }).then(res => {
      setSessionSummary({
        total_seconds: res.data?.total_seconds || 0,
        total_hours: res.data?.total_hours || 0,
        session_count: res.data?.session_count || 0,
        latest_session: res.data?.latest_session || null,
      });
    }).catch(() => {});
  }, []);

  // ── Save idle preference ──
  const setIdleMinutesPersist = (mins) => {
    setIdleMinutes(mins);
    sessionStorage.setItem('gwv_idle_minutes', String(mins));
  };

  // ── Fetch pending access requests whenever admin is authenticated ──
  useEffect(() => {
    if (!currentUser || currentUser.role !== 'admin' || !authToken) return;
    axiosAuth.get('/admin/access-requests')
      .then(r => {
        if (r.data?.length > 0) {
          setAccessRequests(r.data);
          // Only auto-show modal on first fetch (requests array was empty before)
        } else {
          setAccessRequests([]);
        }
      }).catch(() => {});
  }, [currentUser, authToken]); // re-runs on login/token change

  // ── Session clock: update every 30s ──
  useEffect(() => {
    if (!loginTime) return;
    const fmt = () => {
      const diff = Math.floor((Date.now() - loginTime.getTime()) / 1000);
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      setSessionDisplay(h > 0 ? `${h}h ${m}m` : `${m}m`);
    };
    fmt();
    const t = setInterval(fmt, 30000);
    return () => clearInterval(t);
  }, [loginTime]);

  // ── Whisper server health check: on login + every 2 minutes ──
  const [whisperStatus, setWhisperStatus] = useState('unknown'); // 'ok' | 'down' | 'unknown'
  // The interval below is created once per login, so it can't read whisperStatus
  // from the closure — that value stays frozen at 'unknown' and the down → ok
  // transition never registers. Mirror the status in a ref so check() sees the
  // real previous value.
  const whisperStatusRef = useRef('unknown');
  const [showWhisperLogs, setShowWhisperLogs] = useState(false);
  useEffect(() => {
    if (!currentUser || !authToken) return;
    let cancelled = false;
    const check = async () => {
      try {
        const r = await axiosAuth.get('/transcribe/health');
        if (cancelled) return;
        const isOk = r.data?.status === 'ok';
        const wasDown = whisperStatusRef.current === 'down';
        whisperStatusRef.current = isOk ? 'ok' : 'down';
        setWhisperStatus(isOk ? 'ok' : 'down');
        if (!isOk) {
          pushToast('whisper-status', 'error', 'Whisper server is not running — transcription will fail.');
        } else if (wasDown) {
          pushToast('whisper-status', 'success', 'Whisper server is back online.');
        }
      } catch {
        if (cancelled) return;
        whisperStatusRef.current = 'down';
        setWhisperStatus('down');
        pushToast('whisper-status', 'error', 'Whisper server is unreachable.');
      }
    };
    check();
    const t = setInterval(check, 120000); // every 2 minutes
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, authToken]);

  useEffect(() => {
    if (!authToken) return;
    refreshSessionSummary();
  }, [authToken, refreshSessionSummary]);

  useEffect(() => {
    if (!showUserMenu || !authToken) return;
    refreshSessionSummary();
  }, [showUserMenu, authToken, refreshSessionSummary]);

  // ── Keep doLogoutRef current so countdown can call it without stale closure ──
  useEffect(() => { doLogoutRef.current = doLogout; });

  // ── Start the idle countdown (called when idle timer fires) ──
  const startCountdown = useCallback(() => {
    warningActiveRef.current = true;
    setShowIdleWarning(true);
    setIdleCountdown(60);
    if (countdownRef.current) clearInterval(countdownRef.current);
    let remaining = 60;
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      setIdleCountdown(remaining);
      if (remaining <= 0) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
        warningActiveRef.current = false;
        setShowIdleWarning(false);
        doLogoutRef.current?.();
      }
    }, 1000);
  }, []);

  // ── Reset idle timer on activity — no showIdleWarning in deps ──
  const resetIdleTimer = useCallback(() => {
    if (!authToken) return;
    if (warningActiveRef.current) return; // warning already showing — don't interrupt
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(startCountdown, idleMinutes * 60 * 1000);
  }, [authToken, idleMinutes, startCountdown]);

  // ── Attach/detach activity listeners whenever auth or idle duration changes ──
  useEffect(() => {
    if (!authToken) return;
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    const handler = () => resetIdleTimer();
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    resetIdleTimer(); // arm the timer immediately
    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
    };
  }, [authToken, idleMinutes, resetIdleTimer]);

  useEffect(() => {
    if (authToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [authToken]);

  // User preferences from backend (working_directory, subject_access, assigned_subjects)
  const [userPrefs, setUserPrefs]           = useState(null);
  const [assignedSubjects, setAssignedSubjects] = useState([]);  // array of project_root strings (for compatibility)
  const [subjectAssignments, setSubjectAssignments] = useState([]);  // full objects: [{project_root, role_override, allowed_parts}]
  const [projectChooserList, setProjectChooserList] = useState(null); // array of {project_root, file_subdir} when user must choose

  // Fetch preferences whenever we have a logged-in user
  const loadUserPrefs = React.useCallback(() => {
    if (!axiosAuth) return;
    axiosAuth.get('/user/preferences').then(r => {
      setUserPrefs(r.data);
      const rawAssigned = r.data.assigned_subjects || [];
      // Support both old format (array of strings) and new format (array of objects)
      const assignments = rawAssigned.map(a => typeof a === 'string' ? { project_root: a } : a);
      setSubjectAssignments(assignments);
      setAssignedSubjects(assignments.map(a => a.project_root));
    }).catch(() => {});
  }, [axiosAuth]);

  // On mount: check auth/config, validate token, load prefs
  useEffect(() => {
    axios.get(`${BACKEND_URL}/auth/config`).then(r => {
      setAuthEnabled(r.data.auth_enabled !== false);
      setGoogleClientId(r.data.google_client_id || '');
      setCloudTtsAvailable(r.data.cloud_tts_available || false);
      if (!r.data.auth_enabled) { setAuthChecked(true); return; }
      const tok = sessionStorage.getItem('gwv_token');
      if (!tok) { setAuthChecked(true); setShowLogin(true); return; }
      axios.get(`${BACKEND_URL}/auth/me`, { headers: { Authorization: `Bearer ${tok}` } })
        .then(res => {
          // Boot ID check: if server restarted, force re-login
          const serverBootId = res.data.boot_id;
          const savedBootId = localStorage.getItem('gwv_boot_id');
          if (serverBootId && savedBootId && serverBootId !== savedBootId) {
            console.log('[auth] Server restarted (boot_id changed), forcing re-login');
            sessionStorage.removeItem('gwv_token');
            localStorage.setItem('gwv_boot_id', serverBootId);
            setAuthChecked(true);
            setShowLogin(true);
            return;
          }
          if (serverBootId) localStorage.setItem('gwv_boot_id', serverBootId);

          setCurrentUser(res.data.user || res.data);
          setAuthChecked(true);
          // Use JWT iat (issued-at) as login time so session clock survives refresh
          try {
            const payload = JSON.parse(atob(tok.split('.')[1].replace(/-/g,'+').replace(/_/g,'/')));
            if (payload.iat) setLoginTime(new Date(payload.iat * 1000));
            else setLoginTime(new Date());
          } catch { setLoginTime(new Date()); }
          // Start a backend session record for this browser session
          axios.post(`${BACKEND_URL}/auth/session/start`, {},
            { headers: { Authorization: `Bearer ${tok}` } }
          ).then(sr => { sessionIdRef.current = sr.data.session_id; }).catch(() => {});
          // Load prefs
          axios.get(`${BACKEND_URL}/user/preferences`, { headers: { Authorization: `Bearer ${tok}` } })
            .then(pr => {
              setUserPrefs(pr.data);
              const rawAssigned = pr.data.assigned_subjects || [];
              const assignments = rawAssigned.map(a => typeof a === 'string' ? { project_root: a } : a);
              setSubjectAssignments(assignments);
              setAssignedSubjects(assignments.map(a => a.project_root));
            }).catch(() => {});
        })
        .catch(() => {
          sessionStorage.removeItem('gwv_token');
          setAuthToken(''); setCurrentUser(null);
          setAuthChecked(true); setShowLogin(true);
        });
    }).catch(() => {
      setAuthEnabled(true);
      setGoogleClientId('');
      setAuthChecked(true);
      setShowLogin(true);
    });
  }, []);

  useEffect(() => {
    if (!authChecked || !authEnabled) return;
    if (!currentUser) setShowLogin(true);
  }, [authChecked, authEnabled, currentUser]);

  // ============================================================
  // [1] CORE STATE
  // ============================================================
  const [videoList, setVideoList]       = useState([]);
  const [selectedVideo, setSelectedVideo] = useState("");
  const [selectedPart, setSelectedPart] = useState(1);

  // Compute effective role for the currently selected subject (granular access control)
  const effectiveRole = React.useMemo(() => {
    const globalRole = currentUser?.role || 'viewer';
    if (globalRole === 'admin') return 'admin';
    if (!selectedVideo) return globalRole;
    try {
      const root = getVideoProjectRoot(selectedVideo);
      const assignment = subjectAssignments.find(a => a.project_root === root);
      if (assignment && assignment.role_override) return assignment.role_override;
    } catch {}
    return globalRole;
  }, [currentUser?.role, selectedVideo, subjectAssignments]);

  // Compute allowed parts and task assignments for the currently selected subject
  const currentAssignment = React.useMemo(() => {
    if (currentUser?.role === 'admin') return null;
    if (!selectedVideo) return null;
    try {
      const root = getVideoProjectRoot(selectedVideo);
      return subjectAssignments.find(a => a.project_root === root) || null;
    } catch { return null; }
  }, [currentUser?.role, selectedVideo, subjectAssignments]);

  const taskAssignments = currentAssignment?.task_assignments || [];

  const allowedParts = React.useMemo(() => {
    if (currentUser?.role === 'admin') return null; // admin: all parts
    if (!currentAssignment) return null;
    // If granular task assignments exist, derive allowed parts from them
    if (taskAssignments.length > 0) {
      const parts = taskAssignments
        .filter(a => a.part_number !== null)
        .map(a => a.part_number);
      // If any assignment has part_number=null, all parts are allowed
      if (taskAssignments.some(a => a.part_number === null)) return null;
      return parts.length > 0 ? [...new Set(parts)] : null;
    }
    if (currentAssignment.allowed_parts) return currentAssignment.allowed_parts;
    return null; // null = all parts allowed
  }, [currentUser?.role, currentAssignment, taskAssignments]);

  const playVideoClipRange = useCallback((clip) => {
    if (!clip || !selectedVideo) return;
    const rootBase = getProjectRoot(selectedVideo);
    const targetVideo = videoList.find(v =>
      getProjectRoot(v) === rootBase && getPartNumber(v) === clip.part_number
    ) || selectedVideo;
    if (targetVideo !== selectedVideo) {
      setSelectedVideo(targetVideo);
    }
    window.setTimeout(() => {
      if (!videoRef.current || clip.start_time == null) return;
      if (clipStopHandlerRef.current) {
        try { videoRef.current.removeEventListener('timeupdate', clipStopHandlerRef.current); } catch {}
        clipStopHandlerRef.current = null;
      }
      const clipEnd = Number.isFinite(clip.end_time) ? clip.end_time : null;
      const stopAt = () => {
        if (!videoRef.current || clipEnd == null) return;
        if (videoRef.current.currentTime >= clipEnd) {
          videoRef.current.pause();
          videoRef.current.removeEventListener('timeupdate', stopAt);
          clipStopHandlerRef.current = null;
        }
      };
      clipStopHandlerRef.current = stopAt;
      videoRef.current.currentTime = clip.start_time;
      videoRef.current.play();
      if (clipEnd != null && clipEnd > clip.start_time) {
        videoRef.current.addEventListener('timeupdate', stopAt);
      }
    }, targetVideo !== selectedVideo ? 250 : 0);
  }, [selectedVideo, videoList]);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [isVideoReady, setIsVideoReady]     = useState(false); // true after onLoadedData fires
  const currentTime = useRef(0);  // DOM-updated directly — not React state
  const [marks, setMarks]               = useState([]);
  const [marksLoading, setMarksLoading]   = useState(false);
  const [glossary, setGlossary]         = useState([]);
  const [subjects, setSubjects]         = useState({});   // { [project_root]: { display_name, born, ... } }
  const [subjectDetailsTab, setSubjectDetailsTab] = useState('bio');
  const [subjectInterviewLang, setSubjectInterviewLang] = useState('');
  // Has the interview language actually been fetched for the current root?
  // Empty-string alone cannot distinguish "not set" from "not loaded yet", and
  // treating the in-flight window as "not set" made the editor demand a
  // language that was already stored, on every cold load.
  const [subjectLangLoaded, setSubjectLangLoaded] = useState(false);
  const [subjectKnownLangs, setSubjectKnownLangs] = useState([]);
  const [subjectLangSaving, setSubjectLangSaving] = useState(false);
  const [noSubtitlesDismissed, setNoSubtitlesDismissed] = useState(() => !!sessionStorage.getItem('gwv_dismissedNoSubtitles'));
  const [subjectSeedState, setSubjectSeedState] = useState({ seeding: false, lastSeededAt: null });
  const [mp4List, setMp4List] = useState([]);
  const [transcribingParts, setTranscribingParts] = useState({});  // { part: jobId }
  const subjectFormRef = useRef(null);

  // ============================================================
  // [2] UI TOGGLES
  // ============================================================
  const [appMode, setAppMode]             = useState('home');
  const prevAppModeRef = useRef('home');
  const openQRDesigner = useCallback(() => {
    prevAppModeRef.current = appMode;
    setAppMode('qr-designer');
    localStorage.setItem('appMode', 'qr-designer');
  }, [appMode]);
  const [kanbanProjectRoot, setKanbanProjectRoot] = useState(null); // project root for full-screen project kanban
  const [showGlossary, setShowGlossary]   = useState(false);
  const [showSettings, setShowSettings]   = useState(false);
  const [showShortcutsPalette, setShowShortcutsPalette] = useState(false);
  const [showDirectoryPalette, setShowDirectoryPalette] = useState(false);
  const [showExport, setShowExport]       = useState(false);
  const [reportLangs, setReportLangs]      = useState(['en']);
  const [subjectVttLangs, setSubjectVttLangs] = useState([]);
  const [translatingLangs, setTranslatingLangs] = useState(new Set());  // languages currently being translated
  const [rebuildingTranslations, setRebuildingTranslations] = useState(false);
  const [vttVersions, setVttVersions] = useState([]);
  // Defaulted to 'en' regardless of what the interview actually has, so a
  // Swedish-only project requested an English track that was never generated
  // and the browser logged a 404 on every load. Reconciled against the real
  // list below.
  const [editorSubLang, setEditorSubLang] = useState('en');
  const [theaterMode, setTheaterMode] = useState(false);
  // Keep the subtitle choice within what this interview actually has.
  //
  // subjectVttLangs comes from the backend scanning the project folder. If the
  // current pick is not in it, fall back to the interview's own language, then
  // to whatever exists, then to off — rather than requesting a file that is not
  // there. Waits for a non-empty list so a slow fetch does not flip the user's
  // choice to 'off' and back.
  useEffect(() => {
    if (!subjectVttLangs || subjectVttLangs.length === 0) return;
    if (editorSubLang === 'off') return;
    if (subjectVttLangs.includes(editorSubLang)) return;
    // interview_language is stored as a NAME ("Swedish"), not a code, so it has
    // to go through the normaliser — slicing two characters gives "sw" and would
    // never match "sv".
    const interview = normalizeLanguageKey(subjectInterviewLang || '');
    const fallback = subjectVttLangs.find(l => normalizeLanguageKey(l) === interview)
                     || subjectVttLangs[0];
    setEditorSubLang(fallback || 'off');
  }, [subjectVttLangs, editorSubLang, subjectInterviewLang]);

  // Sync subtitle track mode when language selection changes
  useEffect(() => {
    if (!videoRef.current) return;
    const tracks = videoRef.current.textTracks;
    for (let i = 0; i < tracks.length; i++) {
      tracks[i].mode = (editorSubLang !== 'off' && tracks[i].language === editorSubLang) ? 'showing' : 'hidden';
    }
  }, [editorSubLang]);
  const [showNotebookLMExport, setShowNotebookLMExport] = useState(false);
  const [nlmInfo, setNlmInfo] = useState(null);
  const [nlmSections, setNlmSections] = useState(['biography', 'family', 'timeline', 'places', 'qa']);
  const [nlmTapes, setNlmTapes] = useState({});  // {lang: [tape_numbers]}
  const [nlmExporting, setNlmExporting] = useState(false);
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  const userMenuRef = React.useRef(null);
  const userMenuPanelRef = React.useRef(null);
  const [exportAnchor, setExportAnchor]   = useState(null);
  const exportBtnRef = React.useRef(null);
  const exportFlyoutRef = React.useRef(null);
  const [toolsAnchor, setToolsAnchor]     = useState(null);
  const toolsBtnRef = React.useRef(null);
  const toolsFlyoutRef = React.useRef(null);

  // QR Palette state moved to QRDesignerPanel component

  const [shortcutBindings, setShortcutBindings] = useState(() => normalizeShortcutBindings(null));
  const [shortcutPalettePos, setShortcutPalettePos] = useState(() => {
    try {
      const saved = localStorage.getItem('shortcutPalettePos');
      if (saved) return JSON.parse(saved);
    } catch {}
    if (typeof window !== 'undefined') {
      return { x: Math.max(16, window.innerWidth - 316), y: 128 };
    }
    return { x: 16, y: 128 };
  });
  const [isDraggingShortcuts, setIsDraggingShortcuts] = useState(false);
  const [shortcutDragRel, setShortcutDragRel] = useState(null);
  const [listeningShortcutId, setListeningShortcutId] = useState(null);
  const [settingsPalettePos, setSettingsPalettePos] = useState(() => {
    try {
      const saved = localStorage.getItem('settingsPalettePos');
      if (saved) return JSON.parse(saved);
    } catch {}
    if (typeof window !== 'undefined') {
      return { x: Math.max(16, Math.round((window.innerWidth - 720) / 2)), y: 128 };
    }
    return { x: 24, y: 128 };
  });
  const [isDraggingSettings, setIsDraggingSettings] = useState(false);
  const [settingsDragRel, setSettingsDragRel] = useState(null);
  const [directoryPalettePos, setDirectoryPalettePos] = useState(() => {
    try {
      const saved = localStorage.getItem('directoryPalettePos');
      if (saved) return JSON.parse(saved);
    } catch {}
    if (typeof window !== 'undefined') {
      return { x: Math.max(16, Math.round((window.innerWidth - 760) / 2)), y: 128 };
    }
    return { x: 24, y: 128 };
  });
  const [isDraggingDirectory, setIsDraggingDirectory] = useState(false);
  const [directoryDragRel, setDirectoryDragRel] = useState(null);
  const shortcutBindingsRef = useRef(shortcutBindings);
  const listeningShortcutIdRef = useRef(listeningShortcutId);
  // qrActiveTemplate moved to QRDesignerPanel

  useEffect(() => { shortcutBindingsRef.current = shortcutBindings; }, [shortcutBindings]);
  useEffect(() => { listeningShortcutIdRef.current = listeningShortcutId; }, [listeningShortcutId]);

  useEffect(() => {
    setShortcutBindings(normalizeShortcutBindings(userPrefs?.shortcut_bindings));
  }, [userPrefs?.shortcut_bindings]);

  const saveShortcutBindings = useCallback((nextBindings) => {
    const normalized = normalizeShortcutBindings(nextBindings);
    setShortcutBindings(normalized);
    setUserPrefs(prev => (prev ? { ...prev, shortcut_bindings: normalized } : prev));
    if (!authToken) return;
    axiosAuth.put('/user/preferences', { shortcut_bindings: normalized }).catch(() => {});
  }, [authToken, axiosAuth]);

  const assignShortcutBinding = useCallback((actionId, binding) => {
    const next = { ...shortcutBindingsRef.current };
    const incomingKey = shortcutBindingKey(binding);
    if (incomingKey) {
      Object.keys(next).forEach((id) => {
        if (id !== actionId && shortcutBindingKey(next[id]) === incomingKey) {
          next[id] = null;
        }
      });
    }
    next[actionId] = binding;
    saveShortcutBindings(next);
  }, [saveShortcutBindings]);

  const resetShortcutBindings = useCallback(() => {
    saveShortcutBindings(DEFAULT_SHORTCUT_BINDINGS);
  }, [saveShortcutBindings]);

  // updateQrStyle moved to QRDesignerPanel

  const [glossarySearch, setGlossarySearch] = useState("");

  // QA state
  const [qaFindings, setQaFindings]       = useState([]);
  const [qaLoading, setQaLoading]         = useState(false);
  // Distinct from "reviewed, nothing found" — see refreshQA.
  const [qaError, setQaError]             = useState(false);
  const [qaFilter, setQaFilter]           = useState('all');      // all | flagged | auto_fixed | reviewed | audio_review | dismissed
  const [qaRunning, setQaRunning]         = useState(false);

  // Clips generation state (background job — persists across modal close)
  const [clipsGenerating, setClipsGenerating] = useState(false);

  // Clips editor panel state
  const [editorClips, setEditorClips]               = useState([]);
  const [editorClipsLoading, setEditorClipsLoading] = useState(false);
  const [editorClipsError, setEditorClipsError]     = useState(false);
  const [editorClipsView, setEditorClipsView]       = useState('essentials'); // essentials | intro | voice | all
  const [editorClipsFilter, setEditorClipsFilter]   = useState('all'); // assembly|all|proposed|approved|flagged|rejected|exported
  const [editorClipsFlagTarget, setEditorClipsFlagTarget] = useState(null);
  const [trimTarget, setTrimTarget]   = useState(null);   // clip being trimmed
  const [trimStart,  setTrimStart]    = useState(0);
  const [trimEnd,    setTrimEnd]      = useState(0);
  const [trimSaving, setTrimSaving]       = useState(false);
  const [splitSaving, setSplitSaving]     = useState(false);
  const [showNonNegHelp, setShowNonNegHelp] = useState(false);
  const [showUiHelp, setShowUiHelp] = useState(false);
  const [showWorkflowGuide, setShowWorkflowGuide] = useState(false);
  const [uiHelpData, setUiHelpData] = useState(null);

  // renderHelpText is defined after theme (see below line ~1060)
  const trimPreviewRef    = useRef(null); // interval handle for preview stop
  const trimWaveRef       = useRef(null); // DOM div for trim waveform
  const trimWavesurferRef = useRef(null); // WaveSurfer instance for trim modal
  const trimOrigStartRef  = useRef(0);   // original clip start (for view window) — used by WaveSurfer effect
  const trimOrigEndRef    = useRef(0);   // original clip end — used by WaveSurfer effect
  const [trimViewBounds, setTrimViewBounds] = useState({ start: 0, end: 0 }); // triggers re-render for overlays
  const trimPlayheadRef    = useRef(null); // DOM element for playhead line
  const trimPlayheadRaf   = useRef(null); // requestAnimationFrame handle
  const [editorClipsFlagNotes, setEditorClipsFlagNotes]   = useState('');
  const [assemblyExporting, setAssemblyExporting]   = useState(false);
  const [isRegeneratingWaveform, setIsRegeneratingWaveform] = useState(false);
  const [assemblyReady, setAssemblyReady]           = useState(false);
  const [clipFiles, setClipFiles]                   = useState(null); // { files: [], total_size: 0 }
  const clipFilesRoot = useRef(null); // which project root clipFiles belongs to
  const [clipFilesOpen, setClipFilesOpen]           = useState(true);
  const [clipPlayerSrc, setClipPlayerSrc]           = useState(null); // { url, subs: [{lang, url}], name }
  const [clipPlayerSubLang, setClipPlayerSubLang]   = useState('en');
  const [clipsTargetPart, setClipsTargetPart]       = useState(1);
  const [essentialsGenerating, setEssentialsGenerating] = useState(false);
  const [showClipsHelp, setShowClipsHelp] = useState(false);
  const dragFromIdx   = useRef(null);   // assembly drag-to-reorder
  const dragOverIdx   = useRef(null);
  const dragFromIdxNN = useRef(null);   // non-negotiable section drag
  const dragOverIdxNN = useRef(null);
  const pillDragFrom  = useRef(null);   // filter pill drag-to-reorder
  const pillDragOver  = useRef(null);

  const DEFAULT_PILL_ORDER = ['assembly','all','proposed','approved','flagged','exported','rejected'];
  const [filterPillOrder, setFilterPillOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('filterPillOrder') || 'null');
      if (Array.isArray(saved) && saved.length === DEFAULT_PILL_ORDER.length) return saved;
    } catch {}
    return DEFAULT_PILL_ORDER;
  });

  const setTrimPointFromPlayhead = useCallback((which) => {
    if (!videoRef.current) return;
    const at = Number(parseFloat(videoRef.current.currentTime).toFixed(3));
    if (!Number.isFinite(at)) return;
    if (which === 'start') {
      setTrimStart(at);
      if (at >= trimEnd) setTrimEnd(Number((at + 0.1).toFixed(3)));
    } else {
      setTrimEnd(at);
      if (at <= trimStart) setTrimStart(Math.max(0, Number((at - 0.1).toFixed(3))));
    }
  }, [trimEnd, trimStart]);

  const loadEditorClips = useCallback(() => {
    const root = getProjectRoot(selectedVideo);
    if (!root) return;
    setEditorClipsLoading(true);
    axios.get(`${BACKEND_URL}/clips/${encodeURIComponent(root)}`)
      .then(r => { setEditorClips(r.data || []); setEditorClipsError(false); })
      .catch(() => setEditorClipsError(true))
      .finally(() => setEditorClipsLoading(false));
  }, [selectedVideo]);

  // Update a single clip in-place (no scroll reset, no loading flash)
  const updateClipInPlace = useCallback((updated) => {
    if (!updated?.id) return;
    setEditorClips(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
  }, []);

  // Reload clips when switching to clips mode or when video changes
  useEffect(() => {
    if (appMode === 'clips') loadEditorClips();
  }, [appMode, selectedVideo]);

  // ── Toast notifications ──────────────────────────────────────────────────
  const [toasts, setToasts] = useState([]);
  const [toastHistory, setToastHistory] = useState([]);
  const pushToast = useCallback((id, type, message) => {
    setToasts(prev => {
      const existing = prev.find(t => t.id === id);
      const startedAt = existing?.startedAt ?? Date.now();
      const filtered  = prev.filter(t => t.id !== id);
      const toast = { id, type, message, startedAt,
        ...(type !== 'running' ? { endedAt: Date.now() } : {}) };
      // Save completed toasts to history
      if (type !== 'running') {
        setToastHistory(h => [toast, ...h].slice(0, 50));
      }
      return [...filtered, toast];
    });
  }, []);
  const dismissToast = useCallback((id) => {
    setToasts(prev => {
      const toast = prev.find(t => t.id === id);
      // Save dismissed running toasts to history (they were never saved)
      if (toast && toast.type === 'running') {
        setToastHistory(h => [{ ...toast, type: 'info', message: toast.message + ' (dismissed)', endedAt: Date.now() }, ...h].slice(0, 50));
      }
      return prev.filter(t => t.id !== id);
    });
  }, []);
  // Published to the whole tree so components too deep to receive pushToast as
  // a prop can report failures through the toast bar instead of window.alert().
  // Memoised because both callbacks are stable, so consumers never re-render
  // just because App did.
  const toastApi = useMemo(() => ({ pushToast, dismissToast }), [pushToast, dismissToast]);

  // ── Confirm dialog ────────────────────────────────────────────────────────
  // Replaces window.confirm(). State lives here rather than in a provider
  // component App renders, because context reaches descendants only — if the
  // state sat in the provider, App's own confirm() would read the context
  // default (false) and every guard here would become a silent no-op.
  // App uses `confirm` directly; children get it via useConfirm().
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const confirm = useCallback((opts) => {
    const o = typeof opts === 'string' ? { message: opts } : (opts || {});
    return new Promise((resolve) => setPendingConfirm({ ...o, resolve }));
  }, []);
  const resolveConfirm = useCallback((result) => {
    setPendingConfirm((cur) => {
      if (cur) cur.resolve(result);
      return null;
    });
  }, []);

  // ── Prompt dialog ─────────────────────────────────────────────────────────
  // Replaces window.prompt(). Same ownership rule as the confirm dialog above:
  // the state lives here so App's own prompt() is the real one, not the
  // context default. Resolves null on cancel, matching window.prompt.
  const [pendingPrompt, setPendingPrompt] = useState(null);
  const prompt = useCallback((opts) => {
    const o = typeof opts === 'string' ? { message: opts } : (opts || {});
    return new Promise((resolve) => setPendingPrompt({ ...o, resolve }));
  }, []);
  const resolvePrompt = useCallback((result) => {
    setPendingPrompt((cur) => {
      if (cur) cur.resolve(result);
      return null;
    });
  }, []);

  // ── Activity log ──────────────────────────────────────────────────────────
  // Persistent record of all background jobs: seed, qa, convert
  const [activityLog, setActivityLog] = useState([]);
  const logActivity = useCallback((id, type, label, status, extras = {}) => {
    setActivityLog(prev => {
      const existing = prev.find(e => e.id === id);
      if (existing) {
        return prev.map(e => e.id === id
          ? { ...e, status, ...(status !== 'running' ? { endedAt: new Date().toISOString() } : {}), ...extras }
          : e
        );
      }
      return [...prev, { id, type, label, status, startedAt: new Date().toISOString(), endedAt: null, ...extras }];
    });
  }, []);
  const clearActivityLog = useCallback(() => setActivityLog([]), []);
  useEffect(() => {
    const describeAxiosFailure = (err) => {
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'string' && detail.trim()) return detail.trim();
      if (detail && typeof detail === 'object') {
        const pieces = [
          detail.message,
          detail.kind ? `Type: ${detail.kind}` : '',
          detail.upstream_status ? `Upstream status: ${detail.upstream_status}` : '',
          detail.error ? `Error: ${detail.error}` : '',
        ].filter(Boolean);
        if (pieces.length) return pieces.join(' ');
      }
      if (err?.response?.status) return `Request failed (HTTP ${err.response.status}).`;
      if (err?.request) return `Network Error${err?.config?.url ? ` — ${err.config.url}` : ''}`;
      return err?.message || 'Request failed.';
    };

    const handler = (event) => {
      const err = event?.reason;
      if (!err || !(err.isAxiosError || err.name === 'AxiosError')) return;
      event.preventDefault();
      const msg = describeAxiosFailure(err);
      const method = String(err?.config?.method || 'request').toUpperCase();
      const shortUrl = err?.config?.url || 'unknown endpoint';
      const id = `axios-${method}-${shortUrl}-${Date.now()}`;
      pushToast(id, 'error', msg);
      logActivity(id, 'activity', `${method} ${shortUrl}`, 'error', { message: msg });
      console.error('[UnhandledAxios]', err);
    };

    window.addEventListener('unhandledrejection', handler, true);
    return () => window.removeEventListener('unhandledrejection', handler, true);
  }, [logActivity, pushToast]);
  const [isDarkMode, setIsDarkMode]       = useState(() => localStorage.getItem('darkMode') === 'true');
  const [activeScheme, setActiveScheme]   = useState(() => {
    // resolveScheme migrates a saved key from the old five (monochrome/classic/
    // warm/slate/forest) or a deleted custom theme onto one of the three current
    // schemes, so an existing preference still lands somewhere valid.
    return resolveScheme(localStorage.getItem('colorScheme'));
  });
  const setScheme = (key) => {
    setActiveScheme(key);
    localStorage.setItem('colorScheme', key);
  };
  // One-time cleanup: user-authored themes are gone. Drop any stored blob so it
  // can never be re-read, and repoint a saved custom key onto a real scheme.
  useEffect(() => {
    if (localStorage.getItem('customThemes')) localStorage.removeItem('customThemes');
    const saved = localStorage.getItem('colorScheme');
    if (saved && !COLOR_SCHEMES[saved]) localStorage.setItem('colorScheme', resolveScheme(saved));
  }, []);

  const effectiveDarkMode = isDarkMode;

  // One stroke weight, one font — both were previously only variable through the
  // removed custom-theme editor.
  const iconStrokeWidth = 1.8;
  const activeFontFamily = FONT_OPTIONS[0].value;

  const [serverError, setServerError]     = useState(false);
  // ConversionQueue is now self-contained — ref gives us its addJob/clearJob API
  const conversionQueueRef = React.useRef(null);

  // Font size controls — persisted to localStorage
  const [fontSizes, setFontSizes] = useState(() => {
    const defaults = {
      timestamp: 14,
      originalText: 13,
      editorText: 14,
      cardText: 12,
      cardPadding: 15,
      buttonSize: 13,
      partCard: 11,
    };
    try {
      const saved = localStorage.getItem('editorFontSizes');
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...defaults,
          ...parsed,
          cardText: parsed.cardText ?? parsed.partCard ?? defaults.cardText,
        };
      }
    } catch {}
    return defaults;
  });
  const updateFontSize = (key, val) => {
    setFontSizes(prev => {
      const next = { ...prev, [key]: val };
      localStorage.setItem('editorFontSizes', JSON.stringify(next));
      return next;
    });
  };

  // ============================================================
  // [3] <Icon icon={BookOpen} size={13} style={{marginRight:'5px'}} />GLOSSARY & SORTING STATE
  // ============================================================
  const [newMis, setNewMis]         = useState("");
  const [newCor, setNewCor]         = useState("");
  const [newScope, setNewScope]     = useState("project"); // "project" | "global"
  const [addingRule, setAddingRule] = useState(false);     // in-flight guard for "Add Rule"
  const [sortConfig, setSortConfig] = useState({ key: 'misspelling', direction: 'asc' });

  // ============================================================
  // [4] DRAGGABLE GLOSSARY STATE
  // ============================================================
  const [pos, setPos] = useState(() => {
    const saved = localStorage.getItem('glossaryPos');
    return saved ? JSON.parse(saved) : { x: 40, y: 120 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [rel, setRel]               = useState(null);

  // ============================================================
  // [5] WAVEFORM / ZOOM STATE & REFS
  // ============================================================
  const [zoomLevel, setZoomLevel]       = useState(50);
  const [waveformHeight, setWaveformHeight] = useState(() => {
    const saved = localStorage.getItem('gwv_waveform_height');
    return saved ? Number(saved) : 128;
  });
  const [isWaveformReady, setIsWaveformReady] = useState(false);
  const [clipsSidebarWidth, setClipsSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('gwv_clips_sidebar_width');
    return saved ? Number(saved) : 0; // 0 = use default clamp
  });
  const videoRef    = useRef(null);
  const clipStopHandlerRef = useRef(null);
  const qaVideoRef  = useRef(null);  // separate ref for QA panel player
  const waveformRef = useRef(null);
  const wavesurfer  = useRef(null);
  const activeMarkRef = useRef(null);
  // Polling intervals for MKV conversion jobs — declared here so the
  // unmount cleanup effect (further down) is never forward-referencing it.
  const pollIntervalsRef = useRef({});

  // ============================================================
  // [6] SETTINGS — TABS & DIRECTORY BROWSER STATE
  // ============================================================
  const [settingsTab, setSettingsTab]     = useState('textsize');
  const [browseData, setBrowseData]       = useState(null);
  const [activeConfig, setActiveConfig]   = useState(null);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [configSaving, setConfigSaving]   = useState(false);
  const [configMessage, setConfigMessage] = useState(null);
  const [workingDirInfo, setWorkingDirInfo] = useState(null);
  const [importBusy, setImportBusy]       = useState(null);   // null = idle, else key of the import in flight

  const showSettingsRef = useRef(false);
  const showDirectoryPaletteRef = useRef(false);
  const settingsTabRef = useRef('textsize');

  useEffect(() => { showSettingsRef.current = showSettings; }, [showSettings]);
  useEffect(() => { showDirectoryPaletteRef.current = showDirectoryPalette; }, [showDirectoryPalette]);
  useEffect(() => { settingsTabRef.current = settingsTab; }, [settingsTab]);


  // ============================================================
  // [7] THEME
  // ============================================================
  const theme = useMemo(() => {
    const scheme = COLOR_SCHEMES[activeScheme] || COLOR_SCHEMES.ledger;
    const t = effectiveDarkMode ? scheme.dark : scheme.light;
    return {
      ...t,
      glossaryAccent: t.highlight,
      // success/warning/danger/info now come from the scheme as REAL, distinct,
      // AA-checked colours. They used to all alias t.highlight, so "it worked"
      // and "be careful" rendered identically and ~150 components hardcoded
      // their own status colours to compensate. The `|| t.x` guards cover the
      // brief window before every scheme carried the four tokens.
      success:        t.success || t.highlight,
      warning:        t.warning || t.highlight,
      danger:         t.danger  || t.accent,
      info:           t.info    || t.highlight,
      retroGreen:     t.timecode,
      activeBorder:   t.highlight,
      activeText:     t.highlightText,
      fontFamily:     activeFontFamily,
      // The testimony is the artifact, so the words being transcribed read as a
      // scholarly edition — a serif — set apart from the sans interface chrome.
      // Source Serif 4 is already loaded (GOOGLE_FONTS_URL). Script-specific
      // needs (Hebrew, Arabic) still win via getLanguageFontFamily; this is only
      // the Latin-script fallback for cue text.
      readingFont:    "'Source Serif 4', Georgia, 'Times New Roman', serif",
      // Font sizes
      fsTimestamp:    `${fontSizes.timestamp}px`,
      fsOriginalText: `${fontSizes.originalText}px`,
      fsEditorText:   `${fontSizes.editorText}px`,
      fsCardText:     `${fontSizes.cardText}px`,
      fsBadge:        `${Math.max(10, fontSizes.timestamp - 2)}px`,
      fsCard:         `${fontSizes.cardPadding}px`,
      fsButton:       `${fontSizes.buttonSize}px`,
      fsPartName:     `${fontSizes.partCard}px`,
      fsPartLabel:    `${fontSizes.partCard + 1}px`,
      fsPartStats:    `${fontSizes.partCard + 1}px`,
    };
  }, [effectiveDarkMode, activeScheme, fontSizes, activeFontFamily]);

  // Publish the focus-ring colour to CSS, so the global :focus-visible rule in
  // index.css tracks the active scheme's highlight. See constants/tokens.js FOCUS.
  useEffect(() => {
    document.documentElement.style.setProperty('--gwv-focus', theme.highlight || '#4a90d9');
  }, [theme.highlight]);

  // ============================================================
  // ── Load Cormorant Garamond font for project title ──────────────────────
  useEffect(() => {
    if (!document.getElementById('gvts-font')) {
      const l = document.createElement('link');
      l.id   = 'gvts-font';
      l.rel  = 'stylesheet';
      l.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&display=swap';
      document.head.appendChild(l);
    }
  }, []);

  useEffect(() => {
    if (!document.getElementById('gvts-font-hebrew')) {
      const l = document.createElement('link');
      l.id   = 'gvts-font-hebrew';
      l.rel  = 'stylesheet';
      l.href = 'https://fonts.googleapis.com/css2?family=Noto+Sans+Hebrew:wght@400;500;700&display=swap';
      document.head.appendChild(l);
    }
  }, []);

  // ── Permission check helper ──
  // Falls back to role-based logic if permissions haven't loaded yet
  const hasPermission = useCallback((permissionKey) => {
    if (userPermissions && permissionKey in userPermissions) {
      return userPermissions[permissionKey];
    }
    // Fallback: traditional role checks
    const role = currentUser?.role;
    if (!role) return false;
    if (role === 'admin') return true;
    if (role === 'editor') {
      // Editor can't do admin-only things
      return !permissionKey.startsWith('admin.') &&
             !['qa.run_analysis', 'qa.configure_prompts', 'subject.extract', 'subject.view_costs',
               'places.delete', 'family_tree.admin', 'prompts.use', 'dashboard.manage_pipeline'].includes(permissionKey);
    }
    // Viewer: only view permissions
    return ['dashboard.view', 'editor.view_marks', 'clips.view', 'subject.view',
            'places.view', 'family_tree.view', 'timeline.view', 'general.keyboard_shortcuts'].includes(permissionKey);
  }, [currentUser, userPermissions]);

  // Render help text with inline Lucide icons: <IconName> → actual icon
  const helpIconMap = useMemo(() => ({
    LayoutDashboard, PenLine, ListChecks, Scissors, Settings, DownloadCloud, Hammer,
    HelpCircle, Activity, Keyboard, Sun, Moon, User, Shield, Wand2, CheckSquare,
    AlertTriangle, Play, Headphones, Bot, Clock, Star, MapPin, GitBranch, Users,
    Link2, Pin, Film, Video, ThumbsUp, ThumbsDown, Flag, ListOrdered, Upload,
    Folder, Trash2, RotateCcw, Copy, Check, Loader, KeyRound, Globe, Mail,
  }), []);
  const renderHelpText = useCallback((text) => {
    if (!text) return null;
    const parts = text.split(/(<[A-Z][A-Za-z0-9]*>)/g);
    return parts.map((part, i) => {
      const m = part.match(/^<([A-Z][A-Za-z0-9]*)>$/);
      if (m && helpIconMap[m[1]]) {
        return <span key={i} style={{ display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle', margin: '0 2px' }}><Icon icon={helpIconMap[m[1]]} size={13} color={theme.glossaryAccent} /></span>;
      }
      return <span key={i}>{part}</span>;
    });
  }, [helpIconMap, theme.glossaryAccent]);

  // [8] SHARED STYLE HELPERS
  // ============================================================
  const inputStyle = useMemo(() => ({
    width: '100%',
    padding: '10px',
    background: theme.inputBg,
    color: theme.text,
    border: `1px solid ${theme.border}`,
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
  }), [theme.inputBg, theme.text, theme.border]);

  const formatLoggedTime = useCallback((seconds) => {
    const total = Math.max(0, Math.floor(seconds || 0));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    if (h <= 0) return `${m}m`;
    return `${h}h ${m}m`;
  }, []);

  // Human-readable duration: '45s' / '7m 05s' / '1h 23m'
  const formatDurHuman = useCallback((seconds) => {
    const s = Math.max(0, Math.round(seconds || 0));
    if (s >= 3600) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
    if (s >= 60) return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
    return `${s}s`;
  }, []);

  const endTrackedActivity = useCallback((durationSeconds) => {
    const current = trackedActivityRef.current;
    if (!current?.id || !authToken) return Promise.resolve();
    const elapsed = durationSeconds ?? Math.max(0, Math.floor((Date.now() - current.startedAt) / 1000));
    const activityId = current.id;
    trackedActivityRef.current = { id: null, key: '', startedAt: 0 };
    return axios.post(`${BACKEND_URL}/activity/end`,
      { activity_id: activityId, duration_seconds: elapsed },
      { headers: { Authorization: `Bearer ${authToken}` } }
    ).catch(() => {});
  }, [authToken]);

  const trackedActivityPayload = useMemo(() => {
    if (!authToken || !currentUser) return null;
    let area = 'dashboard';
    let action = null;
    if (showGlossary) {
      area = 'tool';
      action = 'glossary';
    } else if (appMode === 'qr-designer') {
      area = 'tool';
      action = 'qr-designer';
    } else if (showSettings) {
      area = 'settings';
      action = settingsTab || 'general';
    } else if (appMode === 'admin') {
      area = 'admin';
      action = 'dashboard';
    } else if (appMode === 'access-requests') {
      area = 'admin';
      action = 'access-requests';
    } else if (appMode === 'qa') {
      area = 'qa';
      action = qaFilter || 'all';
    } else if (appMode === 'subject') {
      area = 'subject';
      action = subjectDetailsTab || 'bio';
    } else if (appMode === 'clips') {
      area = 'clips';
      action = editorClipsView || 'intro';
    } else if (appMode === 'project-kanban') {
      area = 'project-kanban';
      action = kanbanProjectRoot || 'idle';
    } else if (appMode === 'editor') {
      area = 'editor';
      action = selectedVideo ? `tape-${selectedPart}` : 'idle';
    } else {
      area = 'dashboard';
      action = appMode;
    }
    return {
      area,
      action,
      project_root: selectedVideo ? getVideoProjectRoot(selectedVideo) : null,
      video_id: selectedVideo || null,
      metadata: {
        app_mode: appMode,
        selected_part: selectedPart || null,
      },
    };
  }, [
    appMode,
    authToken,
    currentUser,
    editorClipsView,
    qaFilter,
    selectedPart,
    selectedVideo,
    settingsTab,
    showGlossary,
    showSettings,
    subjectDetailsTab,
  ]);

  const trackedActivityKey = useMemo(() => {
    if (!trackedActivityPayload) return '';
    return JSON.stringify(trackedActivityPayload);
  }, [trackedActivityPayload]);

  useEffect(() => {
    if (appMode !== 'admin' && appMode !== 'access-requests') return;
    if (currentUser?.role === 'admin') return;
    setAppMode('home');
    localStorage.setItem('appMode', 'home');
  }, [appMode, currentUser]);

  // Redirect to home if project-kanban mode has no project root (e.g. page refresh)
  useEffect(() => {
    if (appMode === 'project-kanban' && !kanbanProjectRoot) {
      setAppMode('home');
      localStorage.setItem('appMode', 'home');
    }
  }, [appMode, kanbanProjectRoot]);

  useEffect(() => {
    if (!authToken || !trackedActivityPayload || !trackedActivityKey) return;
    if (trackedActivityRef.current.key === trackedActivityKey) return;
    let cancelled = false;

    const startNext = () => {
      axios.post(`${BACKEND_URL}/activity/start`,
        {
          session_id: sessionIdRef.current,
          area: trackedActivityPayload.area,
          action: trackedActivityPayload.action,
          project_root: trackedActivityPayload.project_root,
          video_id: trackedActivityPayload.video_id,
          metadata: trackedActivityPayload.metadata,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      ).then(res => {
        if (cancelled) return;
        trackedActivityRef.current = {
          id: res.data?.activity_id || null,
          key: trackedActivityKey,
          startedAt: Date.now(),
        };
      }).catch(() => {
        if (cancelled) return;
        trackedActivityRef.current = {
          id: null,
          key: trackedActivityKey,
          startedAt: Date.now(),
        };
      });
    };

    endTrackedActivity().finally(() => {
      if (!cancelled) startNext();
    });

    return () => { cancelled = true; };
  }, [authToken, endTrackedActivity, trackedActivityKey, trackedActivityPayload]);

  // ============================================================

  // Close Export flyout when clicking outside
  useEffect(() => {
    if (!showExport) return;
    const handler = (e) => {
      if (
        exportFlyoutRef.current && !exportFlyoutRef.current.contains(e.target) &&
        exportBtnRef.current   && !exportBtnRef.current.contains(e.target)
      ) setShowExport(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showExport]);

  useEffect(() => {
    if (!showToolsMenu) return;
    const handler = (e) => {
      if (
        toolsFlyoutRef.current && !toolsFlyoutRef.current.contains(e.target) &&
        toolsBtnRef.current    && !toolsBtnRef.current.contains(e.target)
      ) setShowToolsMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showToolsMenu]);

  // [9] DATA HELPERS
  // ============================================================

  // selectedVideo is read inside but via closure — use a ref so useCallback
  // doesn't need to re-create on every selectedVideo change
  const selectedVideoRef = useRef(selectedVideo);
  useEffect(() => { selectedVideoRef.current = selectedVideo; }, [selectedVideo]);

  // Mutable ref for selectedPart so markErrorRef never closes over a stale value
  const selectedPartRef  = useRef(selectedPart);
  useEffect(() => { selectedPartRef.current = selectedPart; }, [selectedPart]);

  // ── Targeted refresh helpers ─────────────────────────────────────────────
  // Each fetches exactly one resource so unrelated state never re-renders.
  // refreshData() is the legacy full-refresh kept for the few places that
  // genuinely need all three (startup, working-dir change).

  const refreshVideos = useCallback((forceVideoReset = false) => {
    axios.get(`${BACKEND_URL}/videos`).then(res => {
      setVideoList(res.data);
      if (res.data.length > 0 && (!selectedVideoRef.current || forceVideoReset)) {
        setSelectedVideo(res.data[0]);
      }
    }).catch(err => {
      console.error('[refreshVideos] FAILED:', err?.response?.status, err?.response?.data?.detail || err.message);
      setServerError(true);
    });
  }, []);  // stable

  const refreshMarks = useCallback(() => {
    if (!selectedVideoRef.current) return;
    setMarksLoading(true);
    // Fetch all marks for the whole project (all parts) so cross-part search works.
    // Frontend scoping (colMarks) handles the current-part filter.
    // Inline the root extraction so we don't depend on getProjectRoot being defined first.
    const root = (selectedVideoRef.current || '').split(/\.pt\d+/i)[0].split(/ - pt\d+/i)[0];
    axios.get(`${BACKEND_URL}/marks`, {
      params: { project_root: root }
    })
      .then(res => setMarks(res.data))
      .catch(() => {})   // swallow — backend may be briefly unreachable on restart
      .finally(() => setMarksLoading(false));
  }, []);  // stable — reads via ref

  const completeAllCurrentPart = useCallback(async () => {
    if (!await confirm({
      message: `Bulk complete all pending marks for Tape ${selectedPartRef.current}?`,
      title: 'Complete all pending marks?',
      confirmLabel: 'Complete',
      destructive: false,
    })) return;
    axios.put(`${BACKEND_URL}/marks/bulk-complete/${encodeURIComponent(selectedVideoRef.current)}?part=${selectedPartRef.current}`)
      .then(res => {
        refreshMarks();
        if (res.data.completed_count === 0) pushToast('marks-bulk-complete', 'info', `No pending marks for Tape ${selectedPartRef.current}.`);
      })
      .catch(err => pushToast('marks-bulk-complete', 'error', `Error: ${err.response?.data?.detail || err.message}`));
  }, [refreshMarks, pushToast, confirm]);

  const refreshGlossary = useCallback(() => {
    const vid = selectedVideoRef.current;
    if (!vid) return;
    const root = getProjectRoot(vid);
    axios.get(`${BACKEND_URL}/glossary`, { params: { project_root: root } })
      .then(res => setGlossary(res.data))
      .catch(() => {});  // swallow — backend may be briefly unreachable on restart
  }, []);  // stable — reads via ref (was [selectedVideo] which cascaded into refreshData → config effect → video reset)

  const refreshSubjects = useCallback(() => {
    axios.get(`${BACKEND_URL}/subjects`)
      .then(res => {
        const map = {};
        res.data.forEach(s => { map[s.project_root] = s; });
        setSubjects(map);
      })
      .catch(err => {
        console.error('[refreshSubjects] FAILED:', err?.response?.status, err?.response?.data?.detail || err.message);
      });
  }, []);

  const refreshWorkingDirInfo = useCallback(() => {
    return axios.get(`${BACKEND_URL}/working-dir`)
      .then(res => {
        setWorkingDirInfo(res.data || null);
        return res.data || null;
      })
      .catch(() => {
        setWorkingDirInfo(null);
        return null;
      });
  }, []);

  const refreshData = useCallback((forceVideoReset = false) => {
    refreshVideos(forceVideoReset);
    refreshMarks();
    refreshGlossary();
    refreshSubjects();
    refreshWorkingDirInfo();
  }, [refreshVideos, refreshMarks, refreshGlossary, refreshSubjects, refreshWorkingDirInfo]);

  // Called whenever the working directory changes — clears all testimony-specific
  // state so nothing from the previous project bleeds into the new one.
  const resetForNewDirectory = useCallback(() => {
    setEditorClips([]);
    setEditorClipsFilter('all');
    setEditorClipsFlagTarget(null);
    setQaFindings([]);
    setTrimTarget(null);
    setSelectedPart(1);
    // setSearchTerm / setHistoryFilter live in the HistoryPanel child component — not in scope here
  }, []);

  const handleAdminWorkingDirectoryChanged = useCallback((info) => {
    if (!info) return;
    setWorkingDirInfo(info);
    setActiveConfig(prev => ({
      ...(prev || {}),
      active_display_path: info.display_path,
      active_subdir: info.working_subdir,
    }));
    setSelectedVideo('');
    setVideoList([]);
    setMarks([]);
    setGlossary([]);
    resetForNewDirectory();
    refreshVideos(true);
    refreshSubjects();
    // Pre-generate waveform peaks for all videos in the new directory (background)
    peaksCacheRef.current = {};  // clear stale session cache
    axios.post(`${BACKEND_URL}/waveform-peaks/generate-all`).catch(() => {});
  }, [refreshSubjects, refreshVideos, resetForNewDirectory]);

  const refreshQA = useCallback(() => {
    if (!selectedVideo) { return; }
    // QA always targets the Swedish (.sv) VTT — derive it if currently on .en
    const svId = selectedVideo.replace(/\.en$/i, '.sv');
    setQaLoading(true);
    // Fetch findings for ALL parts (part=0 means all)
    const url = `${BACKEND_URL}/qa/findings/${encodeURIComponent(svId)}?part=0`;
    axios.get(url)
      .then(res => { setQaFindings(res.data || []); setQaError(false); })
      // A failed QA fetch used to empty the findings, which renders exactly like
      // a transcript the AI reviewed and found nothing wrong with. Those are
      // opposite conclusions and must not look the same.
      .catch(err => { console.error('[QA] fetch failed:', err.message); setQaError(true); })
      .finally(() => setQaLoading(false));
  }, [selectedVideo, selectedPart]);

  const runQAAnalysis = useCallback(() => {
    if (!selectedVideo || qaRunning) return;
    const svId = selectedVideo.replace(/\.en$/i, '.sv');
    const toastId = `qa-${svId}-${selectedPart}`;
    const root = getProjectRoot(selectedVideo);
    const subjectName = subjects[root]?.display_name || cleanLabel(root);
    setQaRunning(true);
    const qaLabel = `QA Analysis — ${svId} Tape ${selectedPart}`;
    pushToast(toastId, 'running', `QA Analysis for ${subjectName} running…`);
    logActivity(toastId, 'qa', qaLabel, 'running');
    const url = `${BACKEND_URL}/qa/run/${encodeURIComponent(svId)}?part=${selectedPart}&clear_existing=true`;

    // POST kicks off a background job — returns immediately with { job_id }
    axios.post(url)
      .then(res => {
        const jobId = res.data?.job_id;
        if (!jobId) {
          // Fallback: old-style synchronous response (shouldn't happen)
          refreshQA();
          pushToast(toastId, 'success', `QA Analysis for ${subjectName} complete`);
          logActivity(toastId, 'qa', qaLabel, 'success');
          setQaRunning(false);
          return;
        }

        // Poll the job status until complete or failed
        const poll = () => {
          axios.get(`${BACKEND_URL}/jobs/${jobId}`)
            .then(jr => {
              if (jr.data.status === 'running') {
                setTimeout(poll, 3000);  // check every 3 seconds
                return;
              }
              if (jr.data.status === 'complete') {
                refreshQA();
                const r = jr.data.result || {};
                pushToast(toastId, 'success', `QA Analysis for ${subjectName} complete — ${r.findings_stored || 0} findings, ${r.findings_flagged || 0} flagged`);
                logActivity(toastId, 'qa', qaLabel, 'success');
              } else {
                const errMsg = jr.data.error || 'Unknown error';
                pushToast(toastId, 'error', `QA Analysis failed: ${errMsg}`);
                logActivity(toastId, 'qa', qaLabel, 'error', { message: errMsg });
              }
              setQaRunning(false);
            })
            .catch(pollErr => {
              // Network error polling — retry a few times
              console.warn('[QA] poll error, retrying:', pollErr.message);
              setTimeout(poll, 5000);
            });
        };
        setTimeout(poll, 2000);  // first poll after 2s
      })
      .catch(err => {
        const detail = err?.response?.data?.detail;
        let msg;
        if (!err?.response) {
          msg = err?.code === 'ECONNABORTED' ? 'Request timed out'
              : err?.code === 'ERR_NETWORK' ? 'Network error — server may be down or unreachable'
              : `Network error: ${err.message}`;
        } else {
          msg = typeof detail === 'string'
            ? detail
            : detail?.message || `HTTP ${err.response.status}: ${err.message}`;
        }
        console.error('[QA] run failed:', err);
        pushToast(toastId, 'error', `QA Analysis failed: ${msg}`);
        logActivity(toastId, 'qa', qaLabel, 'error', { message: msg });
        setQaRunning(false);
      });
  }, [selectedVideo, selectedPart, qaRunning, refreshQA, pushToast, logActivity, subjects]);

  // markErrorRef: stable ref to always-current markError implementation.
  // Lets the keyboard effect run once with [] deps and never go stale.
  const markErrorRef = useRef(null);
  const markError = useCallback(() => {
    if (!videoRef.current) return;
    axios.post(`${BACKEND_URL}/mark`, {
      timestamp: videoRef.current.currentTime,
      video_id:  selectedVideoRef.current,
      part:      selectedPartRef.current,  // read from ref — no stale closure
    }).then(refreshMarks);
  }, [refreshMarks]);  // refreshMarks is stable; refs never change identity
  useEffect(() => { markErrorRef.current = markError; }, [markError]);

  // Mark Quote — creates a memorable_quote clip at current playhead position
  const markQuote = useCallback(() => {
    if (!videoRef.current) return;
    pushToast('mark-quote', 'running', 'Marking memorable quote…');
    axios.post(`${BACKEND_URL}/clips/mark-quote`, {
      video_id:  selectedVideoRef.current,
      part:      selectedPartRef.current,
      timestamp: videoRef.current.currentTime,
    }).then(r => {
      pushToast('mark-quote', 'success', `Memorable quote marked — clip #${r.data.id}`);
      loadEditorClips();
    }).catch(e => {
      pushToast('mark-quote', 'error', e?.response?.data?.detail || 'Failed to mark quote');
    });
  }, [pushToast, loadEditorClips]);
  const markQuoteRef = useRef(null);
  useEffect(() => { markQuoteRef.current = markQuote; }, [markQuote]);

  // Derive the project root from any filename pattern, e.g.:
  //   "Chaim David Szpigler.pt1.sv"  → "Chaim David Szpigler"
  //   "Chaim_David_pt1_sv"           → "Chaim_David"
  //   "Interview - pt2"              → "Interview"
  // Strip the trailing part/tape suffix and sv marker to get the project root.
  // Handles: _pt1_sv, .pt1.sv, Tape1_sv, Tape_1_sv, Tape 1.sv, Part2_sv, _sv, .sv
  // The project root for the folder we are working in.
  //
  // This used to strip the part and language suffix off the video FILENAME,
  // which meant the folder was ignored entirely. Copying an interview's files
  // into a new folder produced the same root, so the copy silently inherited
  // the original's subject record, family tree, places, timeline, pipeline and
  // task assignments — and marks bled between them in both directions.
  //
  // The backend now resolves the root from the folder via the project registry
  // and publishes it on /config. Ask, don't guess.
  //
  // Until /config answers we do not know which project we are in, and we must
  // NOT guess. Returning a filename-derived root here reintroduced the exact
  // bug this replaced: in a copied folder it names the original project, so
  // every request fired in that window reads and writes the wrong interview's
  // subject record, places, genogram and timeline.
  //
  // '' means "not known yet". Callers send no root and get nothing back, which
  // is the safe answer. getProjectRoot changes identity when activeConfig
  // arrives, so effects depending on it re-run with the real root.
  const getProjectRoot = useCallback(
    () => activeConfig?.project_root || '',
    [activeConfig],
  );

  useEffect(() => {
    if ((appMode !== 'subject' && appMode !== 'editor') || !authToken) return;
    // Languages list is global — always load it
    axios.get(`${BACKEND_URL}/pipeline/languages`)
      .then(r => setSubjectKnownLangs([...new Set([...(r.data.used || []), ...(r.data.all || [])])]))
      .catch(() => {});
    // Subject-specific data needs a root — fall back to working directory name
    const root = selectedVideo
      ? getProjectRoot(selectedVideo)
      : (activeConfig?.active_subdir || '').split('/').filter(Boolean).pop() || '';
    if (!root) return;
    axios.get(`${BACKEND_URL}/subject?root=${encodeURIComponent(root)}`)
      .then(r => setSubjects(prev => ({ ...prev, [root]: r.data })))
      .catch(() => {});
    axios.get(`${BACKEND_URL}/translate/languages/${encodeURIComponent(root)}`)
      .then(r => setSubjectVttLangs(r.data.languages || []))
      .catch(() => {});
    setSubjectLangLoaded(false);
    axios.get(`${BACKEND_URL}/pipeline/language?root=${encodeURIComponent(root)}`)
      .then(r => setSubjectInterviewLang(r.data.interview_language || ''))
      .catch(() => {})
      // Loaded either way: on failure we still stop claiming it is unset, so a
      // transient network error does not nag for a language that exists.
      .finally(() => setSubjectLangLoaded(true));
  }, [appMode, selectedVideo, getProjectRoot, authToken, activeConfig?.active_subdir]);

  // Convert any raw filename-derived string into a clean human-readable label:
  // underscores → spaces, collapse multiple spaces, trim.
  const cleanLabel = useCallback((str) => {
    if (!str) return '';
    return str.replace(/_/g, ' ').replace(/\s{2,}/g, ' ').trim();
  }, []);

  // Extract the sequential part number from a video_id.
  // Looks for the last number before _sv/.sv in the stem.
  // e.g. Tape5_sv→5, pt3.sv→3, Part_2_sv→2, SingleTape_sv→1
  const getPartNumber = useCallback((videoId) => {
    if (!videoId) return 1;
    const stem = videoId
      .replace(/[._\s-]*(sv|en|he|pl|de|fr|ru|yi|yid|es|it|nl)$/i, '')
      .replace(/[._\s-]+$/, '');
    const m = stem.match(/(?:tape|part|pt|episode|ep)[._\s-]*(\d+)$/i);
    return m ? parseInt(m[1], 10) : 1;
  }, []);

  // Extract tape number from video_id and always display as "Tape N".
  const getTapeLabel = useCallback((videoId) => {
    if (!videoId) return null;
    const stem = videoId
      .replace(/[._\s-]*(sv|en|he|pl|de|fr|ru|yi|yid)$/i, '')
      .replace(/[._\s-]+$/, '');
    // Match any tape/part/pt/episode/ep prefix + number, OR bare trailing number
    const m = stem.match(/(?:tape|part|pt|episode|ep)[._\s-]*(\d+)$/i)
           || stem.match(/(\d+)$/);
    if (!m) return null;
    const num = m[1] || m[0];
    return `Tape ${parseInt(num, 10)}`;
  }, []);

  const runQAAllParts = useCallback(() => {
    if (!selectedVideo || qaRunning) return;
    const root = getProjectRoot(selectedVideo);
    const subjectName = subjects[root]?.display_name || cleanLabel(root);
    const projectVideos = videoList
      .filter(v => getProjectRoot(v) === root)
      .sort((a, b) => getPartNumber(a) - getPartNumber(b));
    if (projectVideos.length === 0) return;

    setQaRunning(true);
    const toastId = `qa-all-${root}`;
    pushToast(toastId, 'running', `QA Analysis for ${subjectName} — all ${projectVideos.length} parts…`);
    logActivity(toastId, 'qa', `QA All Parts — ${root}`, 'running');

    let totalFindings = 0;

    const runNextPart = (idx) => {
      if (idx >= projectVideos.length) {
        pushToast(toastId, 'success', `QA for ${subjectName} complete — ${projectVideos.length} parts, ${totalFindings} findings`);
        logActivity(toastId, 'qa', `QA All Parts — ${root}`, 'success');
        setQaRunning(false);
        refreshQA();
        return;
      }

      const vid = projectVideos[idx];
      const svId = vid.replace(/\.en$/i, '.sv');
      const part = getPartNumber(vid);
      pushToast(toastId, 'running', `QA for ${subjectName} — Tape ${part} (${idx + 1}/${projectVideos.length})…`);

      axios.post(`${BACKEND_URL}/qa/run/${encodeURIComponent(svId)}?part=${part}&clear_existing=true`)
        .then(res => {
          const jobId = res.data?.job_id;
          if (!jobId) { runNextPart(idx + 1); return; }

          const poll = () => {
            axios.get(`${BACKEND_URL}/jobs/${jobId}`)
              .then(jr => {
                if (jr.data.status === 'running') { setTimeout(poll, 3000); return; }
                if (jr.data.status === 'complete') {
                  totalFindings += jr.data.result?.findings_stored || 0;
                }
                runNextPart(idx + 1);
              })
              .catch(() => setTimeout(poll, 5000));
          };
          setTimeout(poll, 2000);
        })
        .catch(() => runNextPart(idx + 1));
    };

    runNextPart(0);
  }, [selectedVideo, qaRunning, videoList, refreshQA, pushToast, logActivity, subjects, getProjectRoot, getPartNumber]);

  // Load MP4s for the current working directory
  const refreshMp4s = useCallback(() => {
    axios.get(`${BACKEND_URL}/transcribe/mp4s`).then(r => setMp4List(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (appMode === 'editor') refreshMp4s();
  }, [appMode, refreshMp4s]);

  const transcribePart = useCallback((mp4, part) => {
    const lang = subjectInterviewLang?.trim();
    if (!lang) { pushToast('transcribe-no-lang', 'error', 'Please set the interview language first.'); return; }
    const label = mp4.stem || `Tape ${part}`;

    // Check Whisper service health before proceeding
    pushToast(`transcribe-check-${part}`, 'running', 'Checking Whisper service…');
    axios.get(`${BACKEND_URL}/transcribe/health`).then(async h => {
      pushToast(`transcribe-check-${part}`, 'dismiss');
      if (h.data?.status === 'unreachable' || h.data?.error) {
        pushToast(`transcribe-whisper-down-${part}`, 'error', 'Whisper service is not running.\n\nPlease start the Whisper server on the Mac first:\n\n  cd MacTranscriber\n  python3 whisper/server.py');
        return;
      }
      if (!await confirm({
        message: `Transcribe ${label}?\n\nLanguage: ${lang}\nWhisper: connected\n\nThis will:\n  1. Transcribe audio → ${lang} subtitles (Whisper)\n  2. Translate ${lang} → English (Gemini)`,
        title: 'Start transcription?',
        confirmLabel: 'Transcribe',
        destructive: false,
      })) return;

    const svId = mp4.stem || '_scan_';
    setTranscribingParts(prev => ({ ...prev, [part]: true }));
    pushToast(`transcribe-${part}`, 'running', `Transcribing ${label} [${lang}]…`);

    axios.post(`${BACKEND_URL}/transcribe/${encodeURIComponent(svId)}?part=${part}&language=${encodeURIComponent(lang)}`)
      .then(res => {
        const jobId = res.data?.job_id;
        if (!jobId) { setTranscribingParts(prev => ({ ...prev, [part]: false })); return; }

        const pollTranslate = (translateJobId) => {
          axios.get(`${BACKEND_URL}/jobs/${translateJobId}`)
            .then(jr => {
              if (jr.data.status === 'running') {
                pushToast(`translate-en-${part}`, 'running', `${label}: Translating to English…`);
                setTimeout(() => pollTranslate(translateJobId), 4000); return;
              }
              if (jr.data.status === 'complete') {
                const r = jr.data.result || {};
                pushToast(`translate-en-${part}`, 'success', `${label} English translation complete — ${(r.files_written || []).length} files`);
              } else {
                pushToast(`translate-en-${part}`, 'error', `${label} English translation failed: ${jr.data.error || 'Unknown error'}`);
              }
              refreshVideos();
              setTranscribingParts(prev => ({ ...prev, [part]: false }));
            })
            .catch(() => setTimeout(() => pollTranslate(translateJobId), 6000));
        };

        const poll = () => {
          axios.get(`${BACKEND_URL}/transcribe/jobs/${jobId}`)
            .then(jr => {
              if (jr.data.status === 'running') {
                if (jr.data.progress) pushToast(`transcribe-${part}`, 'running', `${label}: ${jr.data.progress}`);
                setTimeout(poll, 5000); return;
              }
              if (jr.data.status === 'complete') {
                const r = jr.data.result || {};
                const fileCount = (r.pass1_files || []).length;
                pushToast(`transcribe-${part}`, 'success', `${label} transcription complete — ${fileCount} files in ${r.total_duration_s || '?'}s`);
                refreshVideos();
                refreshMp4s();
                // If backend auto-triggered English translation, poll that too
                if (jr.data.translate_job_id) {
                  pushToast(`translate-en-${part}`, 'running', `${label}: Translating to English…`);
                  setTimeout(() => pollTranslate(jr.data.translate_job_id), 3000);
                } else {
                  setTranscribingParts(prev => ({ ...prev, [part]: false }));
                }
              } else {
                pushToast(`transcribe-${part}`, 'error', `${label} failed: ${jr.data.error || 'Unknown error'}`);
                setTranscribingParts(prev => ({ ...prev, [part]: false }));
              }
            })
            .catch(() => setTimeout(poll, 8000));
        };
        setTimeout(poll, 5000);
      })
      .catch(err => {
        pushToast(`transcribe-${part}`, 'error', `Transcription failed: ${err?.response?.data?.detail || err.message}`);
        setTranscribingParts(prev => ({ ...prev, [part]: false }));
      });
    }).catch(() => {
      pushToast(`transcribe-check-${part}`, 'dismiss');
      pushToast(`transcribe-whisper-down-${part}`, 'error', 'Whisper service is not running.\n\nPlease start the Whisper server on the Mac first:\n\n  cd MacTranscriber\n  python3 whisper/server.py');
    });
  }, [subjectInterviewLang, pushToast, refreshVideos, refreshMp4s, confirm]);

  const clipLanguageMeta = useMemo(() => {
    const root = getProjectRoot(selectedVideo);
    const language = subjectInterviewLang?.trim() || subjects[root]?.interview_language || '';
    return language ? getLanguageMeta(language) : null;
  }, [getProjectRoot, selectedVideo, subjectInterviewLang, subjects]);

  // ── Reusable subtitle language selector ──
  const renderSubtitleSelector = useCallback((langs, value, onChange, { compact = false } = {}) => {
    if (!langs || langs.length === 0) return null;
    return (
      <LanguageSelector
        languages={langs}
        value={value}
        onChange={onChange}
        theme={theme}
        compact={compact}
        showOff
        offLabel="Subtitles Off"
        label="Subtitles"
      />
    );
  }, [theme]);

  const renderClipLanguagePill = useCallback((fontSize = '0.62rem') => {
    if (!clipLanguageMeta) return null;
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '2px 9px',
          borderRadius: '999px',
          background: clipLanguageMeta.bg,
          border: `1px solid ${clipLanguageMeta.border}`,
          color: clipLanguageMeta.text,
          fontSize,
          fontWeight: 700,
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {clipLanguageMeta.label}
      </span>
    );
  }, [clipLanguageMeta]);

  const handlePartClick = (partNum) => {
    const projectRoot = getProjectRoot(selectedVideo);
    const targetVideo = videoList.find(v =>
      getProjectRoot(v) === projectRoot && getPartNumber(v) === partNum
    );
    if (targetVideo) setSelectedVideo(targetVideo);
    else setSelectedPart(partNum);
  };

  // ============================================================
  // [10] FORMAT HELPERS
  // ============================================================

  const formatVTTTime = useCallback((seconds) => {
    const date = new Date(seconds * 1000);
    const hh = String(date.getUTCHours()).padStart(2, '0');
    const mm = String(date.getUTCMinutes()).padStart(2, '0');
    const ss = String(date.getUTCSeconds()).padStart(2, '0');
    const ms = String(date.getUTCMilliseconds()).padStart(3, '0');
    return `${hh}:${mm}:${ss}.${ms}`;
  }, []);

  // ============================================================
  // [11] MEMOS
  // ============================================================

  const projectStats = useMemo(() => {
    if (!selectedVideo) return { breakdown: [], grandDone: 0, grandTotal: 0 };
    const projectRoot  = getProjectRoot(selectedVideo);
    // Drive part cards from actual files in videoList — always in sync with disk
    const projectVideos = videoList
      .filter(v => getProjectRoot(v) === projectRoot)
      .sort((a, b) => getPartNumber(a) - getPartNumber(b));
    // Fall back to showing at least 1 card if videoList not yet loaded
    const partsToShow = projectVideos.length > 0
      ? projectVideos.map(v => getPartNumber(v))
      : [1];
    // Still read marks for the PENDING/DONE counts
    const projectMarks = marks.filter(m => getProjectRoot(m.video_id) === projectRoot);
    const hasEdit = m => m.edited_text && m.edited_text.trim() && m.edited_text.trim() !== (m.original_text || '').trim();
    const breakdown = partsToShow.map(p => {
      const vid    = projectVideos.find(v => getPartNumber(v) === p);
      const pMarks = projectMarks.filter(m => m.part_number === p);
      const edited = pMarks.filter(hasEdit);
      return {
        part:      p,
        videoId:   vid || null,
        tapeLabel: vid ? getTapeLabel(vid) : null,
        total:     pMarks.length,
        done:      edited.filter(m => m.status === 'completed').length,
        pending:   edited.filter(m => m.status !== 'completed').length,
      };
    });
    return {
      breakdown,
      grandDone:  projectMarks.filter(m => hasEdit(m) && m.status === 'completed').length,
      grandTotal: projectMarks.filter(hasEdit).length,
    };
  }, [marks, selectedVideo, videoList, getProjectRoot, getPartNumber, getTapeLabel]);

  const processedGlossary = useMemo(() => {
    let items = glossary.filter(g =>
      (g.misspelling || "").toLowerCase().includes(glossarySearch.toLowerCase()) ||
      (g.correction  || "").toLowerCase().includes(glossarySearch.toLowerCase())
    );
    if (sortConfig.key) {
      items.sort((a, b) => {
        const valA = (a[sortConfig.key] || "").toLowerCase();
        const valB = (b[sortConfig.key] || "").toLowerCase();
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ?  1 : -1;
        return 0;
      });
    }
    return items;
  }, [glossary, glossarySearch, sortConfig]);

  // ============================================================
  // [12] EFFECTS
  // ============================================================

  // Load QA findings when switching into QA mode.
  // We read selectedVideo via its ref so the effect never goes stale.
  const refreshQARef = useRef(refreshQA);
  useEffect(() => { refreshQARef.current = refreshQA; }, [refreshQA]);

  useEffect(() => {
    if (appMode === 'qa' && selectedVideoRef.current) {
      refreshQARef.current();
    }
  }, [appMode]);

  // Keyboard shortcuts — registered once, always reads current state via refs
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (listeningShortcutIdRef.current) return;
      if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') return;
      const bindings = shortcutBindingsRef.current;
      if (bindingMatchesEvent(bindings.playPause, e)) {
        e.preventDefault();
        if (videoRef.current) videoRef.current.paused ? videoRef.current.play() : videoRef.current.pause();
      }
      if (bindingMatchesEvent(bindings.markError, e)) {
        e.preventDefault();
        markErrorRef.current?.();
      }
      if (bindingMatchesEvent(bindings.markQuote, e)) {
        e.preventDefault();
        markQuoteRef.current?.();
      }
      if (bindingMatchesEvent(bindings.seekForward, e)) {
        e.preventDefault();
        if (videoRef.current) videoRef.current.currentTime += 5;
      }
      if (bindingMatchesEvent(bindings.seekBack, e))  {
        e.preventDefault();
        if (videoRef.current) videoRef.current.currentTime -= 5;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);  // stable — all state read via refs inside handleKeyDown

  // Close user menu on outside click
  useEffect(() => {
    const handler = (e) => {
      const inBadge = userMenuRef.current && userMenuRef.current.contains(e.target);
      const inPanel = userMenuPanelRef.current && userMenuPanelRef.current.contains(e.target);
      if (!inBadge && !inPanel) setShowUserMenu(false);
    };
    if (showUserMenu) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showUserMenu]);

  // Clean up all polling intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(pollIntervalsRef.current).forEach(clearInterval);
    };
  }, []);

  // ── Peaks cache (shared between main + trim WaveSurfer) ─────────────────
  // Keyed by "videoId|part" so each video/part combo is fetched once per session.
  const peaksCacheRef = useRef({});

  const fetchPeaks = useCallback(async (videoId, part) => {
    const key = `${videoId}|${part}`;
    if (peaksCacheRef.current[key]) return peaksCacheRef.current[key];
    try {
      const res = await axios.get(`${BACKEND_URL}/waveform-peaks/${encodeURIComponent(videoId)}`, {
        params: { part },
      });
      if (res.data?.peaks?.length) {
        const result = {
          peaks: [new Float32Array(res.data.peaks)],
          duration: res.data.duration || 0,
        };
        peaksCacheRef.current[key] = result;
        return result;
      }
    } catch (_) {}
    return null;
  }, []);

  // Regenerate waveform — force re-extract peaks from FFmpeg and rebuild WaveSurfer
  const regenerateWaveform = useCallback(async (silent = false) => {
    if (!selectedVideo || isRegeneratingWaveform) return;
    setIsRegeneratingWaveform(true);
    if (!silent) pushToast('waveform-regen', 'running', 'Regenerating waveform…');
    const key = `${selectedVideo}|${selectedPart}`;
    delete peaksCacheRef.current[key];
    setIsWaveformReady(false);
    try {
      const res = await axios.get(`${BACKEND_URL}/waveform-peaks/${encodeURIComponent(selectedVideo)}`, { params: { part: selectedPart, force: true } });
      if (res.data?.peaks?.length) {
        peaksCacheRef.current[key] = { peaks: [new Float32Array(res.data.peaks)], duration: res.data.duration || 0 };
      }
      if (!silent) pushToast('waveform-regen', 'success', 'Waveform regenerated');
    } catch (_) {
      if (!silent) pushToast('waveform-regen', 'error', 'Waveform regeneration failed');
    }
    wavesurfer.current?.destroy();
    wavesurfer.current = null;
    setIsVideoReady(false);
    setIsRegeneratingWaveform(false);
    setTimeout(() => setIsVideoReady(true), 100);
  }, [selectedVideo, selectedPart, isRegeneratingWaveform, pushToast]);

  // WaveSurfer initialisation — creates synchronously, then upgrades with peaks if available
  useEffect(() => {
    if (!waveformRef.current || !videoRef.current || !isVideoReady || !selectedVideo) return;

    const videoEl = videoRef.current;  // snapshot ref for cleanup

    console.log('[WaveSurfer] create', {
      selectedVideo,
      selectedPart,
      isVideoLoading,
      activeSubdir: activeConfig?.active_subdir ?? null,
    });

    setIsWaveformReady(false);
    wavesurfer.current?.destroy();
    waveformRef.current.innerHTML = '';

    // Check if peaks are already cached (instant path — no async gap)
    const cachedKey = `${selectedVideo}|${selectedPart}`;
    const cached = peaksCacheRef.current[cachedKey];

    const opts = {
      container: waveformRef.current,
      media: videoRef.current,
      height: 'auto',
      normalize: true,
      minPxPerSec: zoomLevel,
      autoScroll: true,
      autoCenter: true,
      waveColor: isDarkMode ? '#444' : '#ccc',
      progressColor: theme.waveProgress || theme.highlight,
      backend: 'MediaElement',
    };

    if (cached) {
      opts.peaks = cached.peaks;
      if (cached.duration) opts.duration = cached.duration;
      console.log('[WaveSurfer] using cached peaks');
    }

    wavesurfer.current = WaveSurfer.create(opts);

    const markReady = () => setIsWaveformReady(true);
    wavesurfer.current.on('ready', markReady);
    videoEl.addEventListener('canplay', markReady);
    if (cached) markReady();

    // If peaks weren't cached, fetch them in the background for next time
    if (!cached) {
      fetchPeaks(selectedVideo, selectedPart).then(result => {
        if (result) console.log('[WaveSurfer] peaks cached for next load');
      });
    }

    return () => {
      console.log('[WaveSurfer] destroy', {
        selectedVideo,
        selectedPart,
        isVideoLoading,
        activeSubdir: activeConfig?.active_subdir ?? null,
      });

      wavesurfer.current?.destroy();
      wavesurfer.current = null;
      try { videoEl.removeEventListener('canplay', markReady); } catch {}
      setIsWaveformReady(false);
    };
  // isVideoReady gates creation — WaveSurfer only (re)creates once the video
  // element has loaded data, preventing the destroy→create→destroy flicker
  // that occurred when selectedVideo changed before the <video> was ready.
  }, [selectedVideo, selectedPart, activeConfig?.active_subdir, isDarkMode, isVideoReady, theme.waveProgress, theme.highlight, fetchPeaks]);

  // Zoom control (guarded against race conditions)
  useEffect(() => {
    if (wavesurfer.current && isWaveformReady) {
      try {
        const duration = wavesurfer.current.getDuration();
        if (duration > 0) wavesurfer.current.zoom(zoomLevel);
      } catch (err) {}
    }
  }, [zoomLevel, isWaveformReady]);

  // ── Trim modal WaveSurfer ────────────────────────────────────────────────
  // Uses a URL-based instance (independent of the main MediaElement waveform)
  // so both can coexist without Web Audio "already connected" errors.
  useEffect(() => {
    if (!trimTarget || !trimWaveRef.current) return;

    // Compute the MP4 URL for the clip's part
    const projectBase = getProjectRoot(trimTarget.project_root || selectedVideo);
    const partVid = videoList.find(v => {
      const vr = getProjectRoot(v);
      return vr === projectBase && getPartNumber(v) === trimTarget.part_number;
    }) || selectedVideo;
    const s = partVid;
    const derivedUrl = encodeVideoPath(activeConfig?.active_subdir ?? '', buildMp4Candidates(s)[0] + '.mp4');
    const videoUrl = (
      trimTarget.part_number === selectedPart &&
      videoRef.current &&
      videoRef.current.currentSrc
    ) ? videoRef.current.currentSrc : derivedUrl;

    // Store original bounds for view window calculation
    trimOrigStartRef.current = trimTarget.start_time;
    trimOrigEndRef.current   = trimTarget.end_time;
    setTrimViewBounds({ start: trimTarget.start_time, end: trimTarget.end_time });

    // Destroy any previous instance
    if (trimWavesurferRef.current) {
      try { trimWavesurferRef.current.destroy(); } catch (_) {}
      trimWavesurferRef.current = null;
    }

    const BUFFER = 2; // seconds of context either side
    const viewStart    = Math.max(0, trimTarget.start_time - BUFFER);
    const viewEnd      = trimTarget.end_time + BUFFER;
    const viewDuration = viewEnd - viewStart;
    const containerW   = trimWaveRef.current.offsetWidth || 460;
    const autoZoom     = Math.max(80, Math.round(containerW / viewDuration));

    // Check peaks cache (same cache as main waveform)
    const trimVid = partVid || selectedVideo;
    const cachedKey = `${trimVid}|${trimTarget.part_number}`;
    const cached = peaksCacheRef.current[cachedKey];

    const wsOpts = {
      container:     trimWaveRef.current,
      height:        64,
      minPxPerSec:   autoZoom,
      autoScroll:    false,
      autoCenter:    false,
      waveColor:     isDarkMode ? '#3b82f6' : '#60a5fa',
      progressColor: isDarkMode ? '#93c5fd' : '#1d4ed8',
    };

    if (cached) {
      wsOpts.peaks = cached.peaks;
      if (cached.duration) wsOpts.duration = cached.duration;
      wsOpts.url = videoUrl;
      wsOpts.backend = 'MediaElement';
    } else {
      // No cached peaks — fall back to WebAudio decode
      wsOpts.url = videoUrl;
      wsOpts.backend = 'WebAudio';
      // Fetch peaks in background for next time
      fetchPeaks(trimVid, trimTarget.part_number);
    }

    const ws = WaveSurfer.create(wsOpts);

    ws.on('ready', () => {
      try {
        const dur = ws.getDuration();
        if (dur > 0) {
          ws.seekTo(viewStart / dur);
        }
      } catch (_) {}
    });
    ws.on('error', (err) => {
      console.error('[TrimWaveSurfer] error', { videoUrl, err, trimTarget });
    });

    trimWavesurferRef.current = ws;

    return () => {
      try { trimWavesurferRef.current?.destroy(); } catch (_) {}
      trimWavesurferRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimTarget?.id]);  // only re-create when the clip itself changes

  // Playhead sync + auto-stop at OUT — while trim is active (direct DOM updates for smooth motion)
  useEffect(() => {
    if (!trimTarget) {
      cancelAnimationFrame(trimPlayheadRaf.current);
      return;
    }
    const BUFFER = 2;
    const tick = () => {
      if (videoRef.current) {
        const t = videoRef.current.currentTime;
        // Auto-stop at OUT marker
        if (!videoRef.current.paused && t >= trimEnd) {
          videoRef.current.pause();
          videoRef.current.currentTime = trimEnd;
        }
        // Direct DOM update for smooth playhead
        if (trimPlayheadRef.current) {
          const viewStart = Math.max(0, trimOrigStartRef.current - BUFFER);
          const viewEnd = trimOrigEndRef.current + BUFFER;
          const viewDur = viewEnd - viewStart;
          const pct = viewDur > 0 ? Math.max(0, Math.min(100, (t - viewStart) / viewDur * 100)) : 0;
          trimPlayheadRef.current.style.left = `${pct}%`;
          trimPlayheadRef.current.style.display = 'block';
        }
      }
      trimPlayheadRaf.current = requestAnimationFrame(tick);
    };
    trimPlayheadRaf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(trimPlayheadRaf.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimTarget?.id, trimEnd]);

  // Sync selectedPart from video filename; reset video readiness on video change
  useEffect(() => {
    if (selectedVideo) {
      setIsVideoReady(false); // video element will remount; wait for onLoadedData
      // Use getPartNumber which handles all naming conventions (Tape1, pt1, Part_3, etc.)
      setSelectedPart(getPartNumber(selectedVideo));
    }
  }, [selectedVideo]);

  // Refresh data whenever video/part changes
  useEffect(() => {
    if (!selectedVideo) return;
    refreshMarks();
  }, [selectedVideo, selectedPart, refreshMarks]);

  useEffect(() => {
    if (!selectedVideo) return;
    refreshGlossary();
  }, [selectedVideo, refreshGlossary]);

  // Sync VTT to DB whenever video/part changes
  useEffect(() => {
    if (!selectedVideo) return;
    axios.post(`${BACKEND_URL}/sync/${encodeURIComponent(selectedVideo)}?part=${selectedPart}`).catch(() => {});
  }, [selectedVideo, selectedPart]);

  // Load config, THEN reload the video list so it uses the correct active dir.
  //
  // This must still fire exactly ONCE — depending on [refreshData,
  // refreshWorkingDirInfo] re-fired whenever a nested callback identity
  // changed, and refreshData(true) resets the selected video. The once-only
  // guarantee is now a ref, so the dependency array can safely wait for auth.
  //
  // Why it has to wait: /config requires authentication. Landing without a
  // valid token (never logged in, or an expired one) made it 401, and the
  // catch below swallowed it — so refreshData() never ran and the editor sat
  // empty with no transcript. Logging in did not help, because with [] deps
  // this effect had already run and never retried; only a hard reload fixed
  // it, by which point the token was valid at mount. That was the bug.
  const configLoadedRef = useRef(false);
  useEffect(() => {
    if (!authChecked) return;                 // auth flow has not resolved yet
    if (authEnabled && !authToken) return;    // not signed in yet — wait
    if (configLoadedRef.current) return;      // already loaded; do not re-fire
    configLoadedRef.current = true;
    axios.get(`${BACKEND_URL}/config`).then(res => {
      setActiveConfig(res.data);
      refreshWorkingDirInfo();
      refreshData(true);  // re-run after config is known, in case subdir changed
      // Pre-generate waveform peaks for all videos in the current directory
      axios.post(`${BACKEND_URL}/waveform-peaks/generate-all`).catch(() => {});
    }).catch(() => {
      // Release the guard so a later sign-in can retry, instead of leaving the
      // app permanently empty after one failed call.
      configLoadedRef.current = false;
      setActiveConfig(null);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChecked, authEnabled, authToken]);

  // ============================================================
  // [13] DRAG LOGIC (Glossary palette)
  // ============================================================

  const onMouseDown = (e) => {
    const box = e.currentTarget.parentNode.getBoundingClientRect();
    setRel({ x: e.pageX - box.left, y: e.pageY - box.top });
    setIsDragging(true);
  };

  useEffect(() => {
    const onMove = (e) => { if (isDragging) setPos({ x: e.pageX - rel.x, y: e.pageY - rel.y }); };
    const onUp   = () => {
      if (isDragging) {
        setIsDragging(false);
        localStorage.setItem('glossaryPos', JSON.stringify(pos));
      }
    };
    if (isDragging) {
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    }
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, rel, pos]);

  const clampShortcutPalettePos = useCallback((nextPos) => {
    const minTop = appShellOffset + 10;
    if (typeof window === 'undefined') {
      return { x: nextPos.x, y: Math.max(minTop, nextPos.y) };
    }
    const width = 300;
    const maxX = Math.max(12, window.innerWidth - width - 12);
    const maxY = Math.max(minTop, window.innerHeight - 120);
    return {
      x: Math.min(Math.max(12, nextPos.x), maxX),
      y: Math.min(Math.max(minTop, nextPos.y), maxY),
    };
  }, [appShellOffset]);

  const clampSettingsPalettePos = useCallback((nextPos) => {
    const minTop = appShellOffset + 10;
    if (typeof window === 'undefined') {
      return { x: nextPos.x, y: Math.max(minTop, nextPos.y) };
    }
    const width = Math.min(720, window.innerWidth - 24);
    const maxX = Math.max(12, window.innerWidth - width - 12);
    const maxY = Math.max(minTop, window.innerHeight - 160);
    return {
      x: Math.min(Math.max(12, nextPos.x), maxX),
      y: Math.min(Math.max(minTop, nextPos.y), maxY),
    };
  }, [appShellOffset]);

  const clampDirectoryPalettePos = useCallback((nextPos) => {
    const minTop = appShellOffset + 10;
    if (typeof window === 'undefined') {
      return { x: nextPos.x, y: Math.max(minTop, nextPos.y) };
    }
    const width = Math.min(760, window.innerWidth - 24);
    const maxX = Math.max(12, window.innerWidth - width - 12);
    const maxY = Math.max(minTop, window.innerHeight - 160);
    return {
      x: Math.min(Math.max(12, nextPos.x), maxX),
      y: Math.min(Math.max(minTop, nextPos.y), maxY),
    };
  }, [appShellOffset]);

  const onShortcutPaletteMouseDown = useCallback((e) => {
    const box = e.currentTarget.parentNode.getBoundingClientRect();
    setShortcutDragRel({ x: e.clientX - box.left, y: e.clientY - box.top });
    setIsDraggingShortcuts(true);
  }, []);

  const onSettingsPaletteMouseDown = useCallback((e) => {
    const box = e.currentTarget.parentNode.getBoundingClientRect();
    setSettingsDragRel({ x: e.clientX - box.left, y: e.clientY - box.top });
    setIsDraggingSettings(true);
  }, []);

  const onDirectoryPaletteMouseDown = useCallback((e) => {
    const box = e.currentTarget.parentNode.getBoundingClientRect();
    setDirectoryDragRel({ x: e.clientX - box.left, y: e.clientY - box.top });
    setIsDraggingDirectory(true);
  }, []);

  useEffect(() => {
    if (!showShortcutsPalette) return;
    setShortcutPalettePos(prev => {
      const clamped = clampShortcutPalettePos(prev);
      return prev.x === clamped.x && prev.y === clamped.y ? prev : clamped;
    });
  }, [appShellOffset, showShortcutsPalette, clampShortcutPalettePos]);

  useEffect(() => {
    if (!showSettings) return;
    setSettingsPalettePos(prev => {
      const clamped = clampSettingsPalettePos(prev);
      return prev.x === clamped.x && prev.y === clamped.y ? prev : clamped;
    });
  }, [appShellOffset, showSettings, clampSettingsPalettePos]);

  useEffect(() => {
    if (!showDirectoryPalette) return;
    setDirectoryPalettePos(prev => {
      const clamped = clampDirectoryPalettePos(prev);
      return prev.x === clamped.x && prev.y === clamped.y ? prev : clamped;
    });
  }, [appShellOffset, showDirectoryPalette, clampDirectoryPalettePos]);

  useEffect(() => {
    const onMove = (e) => {
      if (!isDraggingShortcuts || !shortcutDragRel) return;
      setShortcutPalettePos(clampShortcutPalettePos({
        x: e.clientX - shortcutDragRel.x,
        y: e.clientY - shortcutDragRel.y,
      }));
    };
    const onUp = () => {
      if (!isDraggingShortcuts) return;
      setIsDraggingShortcuts(false);
      localStorage.setItem('shortcutPalettePos', JSON.stringify(clampShortcutPalettePos(shortcutPalettePos)));
    };
    if (isDraggingShortcuts) {
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    }
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isDraggingShortcuts, shortcutDragRel, shortcutPalettePos, clampShortcutPalettePos]);

  useEffect(() => {
    const onMove = (e) => {
      if (!isDraggingSettings || !settingsDragRel) return;
      setSettingsPalettePos(clampSettingsPalettePos({
        x: e.clientX - settingsDragRel.x,
        y: e.clientY - settingsDragRel.y,
      }));
    };
    const onUp = () => {
      if (!isDraggingSettings) return;
      setIsDraggingSettings(false);
      localStorage.setItem('settingsPalettePos', JSON.stringify(clampSettingsPalettePos(settingsPalettePos)));
    };
    if (isDraggingSettings) {
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    }
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isDraggingSettings, settingsDragRel, settingsPalettePos, clampSettingsPalettePos]);

  useEffect(() => {
    const onMove = (e) => {
      if (!isDraggingDirectory || !directoryDragRel) return;
      setDirectoryPalettePos(clampDirectoryPalettePos({
        x: e.clientX - directoryDragRel.x,
        y: e.clientY - directoryDragRel.y,
      }));
    };
    const onUp = () => {
      if (!isDraggingDirectory) return;
      setIsDraggingDirectory(false);
      localStorage.setItem('directoryPalettePos', JSON.stringify(clampDirectoryPalettePos(directoryPalettePos)));
    };
    if (isDraggingDirectory) {
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    }
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isDraggingDirectory, directoryDragRel, directoryPalettePos, clampDirectoryPalettePos]);

  // QR drag logic removed — now full-screen via QRDesignerPanel

  useEffect(() => {
    if (!listeningShortcutId) return;
    const onCapture = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.code === 'Escape' && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) {
        setListeningShortcutId(null);
        return;
      }
      const binding = captureShortcutBinding(e);
      if (!binding) return;
      assignShortcutBinding(listeningShortcutId, binding);
      setListeningShortcutId(null);
    };
    window.addEventListener('keydown', onCapture, true);
    return () => window.removeEventListener('keydown', onCapture, true);
  }, [listeningShortcutId, assignShortcutBinding]);

  // QR helpers, logo handling moved to QRDesignerPanel

  // ============================================================
  // [14] DIRECTORY BROWSER LOGIC
  // ============================================================

  const browseTo = useCallback((subdir) => {
    setBrowseLoading(true);
    setConfigMessage(null);
    const target = subdir ?? '';
    browseCurrentSubdir.current = target;
    axios.get(`${BACKEND_URL}/browse?subdir=${encodeURIComponent(target)}`)
      .then(res => { setBrowseData(res.data); browseCurrentSubdir.current = res.data.current_subdir ?? target; })
      .catch(err => {
        setConfigMessage({ type: 'err', text: err.response?.data?.detail || 'Browse failed.' });
      })
      .finally(() => setBrowseLoading(false));
  }, [BACKEND_URL]);

const onConversionDone = useCallback((rel_path) => {
  // Log completion in activity log
  const filename = rel_path ? rel_path.split('/').pop().replace(/\.mkv$/i, '') : 'file';
  const id = `convert-${rel_path}`;
  logActivity(id, 'convert', `Convert — ${filename}`, 'success');
  // Only refresh the directory browser when the directory UI is actually open.
  if (!showDirectoryPaletteRef.current && (!showSettingsRef.current || settingsTabRef.current !== 'directory')) return;
    browseTo(browseCurrentSubdir.current);
  }, [browseTo, logActivity]);

  const openDirectoryTab = () => {
    setShowSettings(false);
    setShowShortcutsPalette(false);
    setShowDirectoryPalette(true);
    setConfigMessage(null);
    const startSubdir = activeConfig?.active_subdir ?? '';
    browseTo(startSubdir);
    refreshWorkingDirInfo();
  };

  // pollIntervalsRef declared above in refs section



  // Track current browse subdir in a ref so pollConversion closure can read it without stale state
  const browseCurrentSubdir = useRef('');

  const startConversion = (rel_path) => {
    axios.post(`${BACKEND_URL}/convert/mkv?rel_path=${encodeURIComponent(rel_path)}`)
      .then(() => {
        ConversionQueue.addJob(rel_path);
        const filename = rel_path.split('/').pop().replace(/\.mkv$/i, '');
        logActivity(`convert-${rel_path}`, 'convert', `Convert — ${filename}`, 'running');
      })
      .catch(err => {
        ConversionQueue.addJob && ConversionQueue.addJob(rel_path);
        const filename = rel_path.split('/').pop().replace(/\.mkv$/i, '');
        logActivity(`convert-${rel_path}`, 'convert', `Convert — ${filename}`, 'running');
      });
  };

  const saveDirectoryConfig = () => {
    if (!browseData) return;
    setConfigSaving(true);
    setConfigMessage(null);
    const subdir = browseData.current_subdir;
    const isAdmin = currentUser?.role === 'admin';

    // Admin: set global working directory for everyone
    // Non-admin: save to user preferences only
    const savePromise = isAdmin
      ? axios.post(`${BACKEND_URL}/working-dir`, { subdir })
      : axios.put(`${BACKEND_URL}/user/preferences`, { working_directory: subdir })
          .then(() => axios.get(`${BACKEND_URL}/working-dir`));  // reload dir info

    savePromise
      .then(res => {
        const data = res.data || {};
        setActiveConfig(prev => ({ ...prev, active_display_path: data.display_path || subdir, active_subdir: data.working_subdir || subdir }));
        setWorkingDirInfo(prev => ({ ...(prev || {}), ...data }));
        setConfigMessage({ type: 'ok', text: `Working directory set: ${(data.display_path || subdir || '').replace(/^\/+/, '')}` });
        setSelectedVideo('');
        resetForNewDirectory();
        refreshData(true);
        setShowDirectoryPalette(false);
      })
      .catch(err => {
        const detail = err.response?.data?.detail;
        const status = err.response?.status;
        const msg = Array.isArray(detail)
          ? detail.map(d => d.msg || JSON.stringify(d)).join('; ')
          : (detail || err.message || 'Failed to save config.');
        setConfigMessage({ type: 'err', text: status ? `[${status}] ${msg}` : msg });
      })
      .finally(() => setConfigSaving(false));
  };

  const resetToMountRoot = () => {
    setConfigSaving(true);
    setConfigMessage(null);
    const isAdmin = currentUser?.role === 'admin';

    const resetPromise = isAdmin
      ? axios.post(`${BACKEND_URL}/working-dir`, { subdir: '' })
      : axios.put(`${BACKEND_URL}/user/preferences`, { working_directory: '' })
          .then(() => axios.get(`${BACKEND_URL}/working-dir`));

    resetPromise
      .then(res => {
        const data = res.data || {};
        setActiveConfig(prev => ({ ...prev, active_display_path: data.display_path || '', active_subdir: '' }));
        setBrowseData(null);
        browseTo('');
        setWorkingDirInfo(prev => ({ ...(prev || {}), ...data }));
        setConfigMessage({ type: 'ok', text: 'Reset to mount root' });
        setSelectedVideo('');
        resetForNewDirectory();
        refreshData(true);
      })
      .catch(err => {
        const detail = err.response?.data?.detail;
        const status = err.response?.status;
        const msg = Array.isArray(detail)
          ? detail.map(d => d.msg || JSON.stringify(d)).join('; ')
          : (detail || err.message || 'Failed to reset.');
        setConfigMessage({ type: 'err', text: status ? `[${status}] ${msg}` : msg });
      })
      .finally(() => setConfigSaving(false));
  };

  // Helper: switch working directory to a project (admin=global, non-admin=personal prefs)
  const switchToProjectDir = useCallback((subdir, root) => {
    const isAdmin = currentUser?.role === 'admin';
    const setDirPromise = isAdmin
      ? axiosAuth.post('/working-dir', { subdir })
      : axiosAuth.put('/user/preferences', { working_directory: subdir })
          .then(() => axiosAuth.get('/working-dir'));
    return setDirPromise.then(res => {
      const data = res.data || {};
      setWorkingDirInfo(data);
      setActiveConfig(prev => ({
        ...prev,
        active_display_path: data.display_path || subdir,
        active_subdir: data.working_subdir || subdir,
      }));
      setSelectedVideo('');
      setVideoList([]);
      resetForNewDirectory();
      setSelectedPart(1);
      return axiosAuth.get('/videos');
    }).then(res => {
      if (!res?.data) return;
      setVideoList(res.data);
      if (root) {
        const normalise = s => s.toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
        const normRoot = normalise(root);
        const match =
          res.data.find(v => normalise(v).startsWith(normRoot)) ||
          res.data.find(v => normalise(getProjectRoot(v)) === normRoot) ||
          res.data[0];
        setSelectedVideo(match || '');
      }
      refreshSubjects();
    });
  }, [currentUser?.role, axiosAuth, resetForNewDirectory, refreshSubjects]);

  // ============================================================
  // [15] SERVER ERROR SCREEN
  // ============================================================

  // ============================================================
  // [16] RENDER
  // ============================================================

  const isBioDetailsTab = appMode === 'subject' && subjectDetailsTab === 'bio';
  const subjectHeaderPadding = '8px 20px';
  const subjectHeaderJustify = 'center';
  const subjectHeaderMaxWidth = '1480px';
  const showLoginOnly = authChecked && authEnabled && !currentUser;
  const workingDirectoryButtonLabel = activeConfig?.active_subdir
    ? `${activeConfig.active_subdir}`.replace(/^\/+/, '')
    : '/';
  const directoryLanguageCounts = useMemo(() => {
    const counts = new Map();
    (workingDirInfo?.pairs || []).forEach(pair => {
      const label = getWorkingDirLanguageLabel(pair);
      if (!label) return;
      counts.set(label, (counts.get(label) || 0) + 1);
    });

    const orderedLabels = [];
    (workingDirInfo?.available_languages || []).forEach(label => {
      if (!orderedLabels.includes(label)) orderedLabels.push(label);
    });
    counts.forEach((_, label) => {
      if (!orderedLabels.includes(label)) orderedLabels.push(label);
    });

    return orderedLabels.map(label => ({
      label,
      count: counts.get(label) || 0,
      meta: getLanguageMeta(label),
    }));
  }, [workingDirInfo]);
  const directorySelectorContent = (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
      {!!directoryLanguageCounts.length && (
        <div style={{ background: theme.card, padding: '10px 14px', borderRadius: '10px', marginBottom: '12px', flexShrink: 0, border: `1px solid ${theme.border}` }}>
          <div style={{ fontSize: '0.65rem', color: theme.textSub, fontWeight: 'bold', marginBottom: '8px', letterSpacing: '0.05em' }}>
            LANGUAGES IN FOLDER
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {directoryLanguageCounts.map(({ label, count, meta }) => (
              <span
                key={label}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 9px',
                  borderRadius: '999px',
                  background: meta.bg,
                  border: `1px solid ${meta.border}`,
                  color: meta.text,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                }}
                title={`${count} subtitle file${count === 1 ? '' : 's'}`}
              >
                <span>{meta.label}</span>
                <span style={{ opacity: 0.82, fontFamily: 'monospace', fontSize: '0.64rem' }}>{count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {configMessage && (
        <div style={{ padding: '8px 12px', borderRadius: '8px', marginBottom: '10px', fontSize: '0.8rem', fontWeight: 'bold', background: configMessage.type === 'ok' ? theme.card : `${theme.accent}22`, color: configMessage.type === 'ok' ? theme.textSub : theme.accent, border: `1px solid ${configMessage.type === 'ok' ? theme.border : theme.accent}`, flexShrink: 0 }}>
          {configMessage.text}
        </div>
      )}

      {browseLoading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textSub }}>
          Loading...
        </div>
      ) : browseData ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '2px', marginBottom: '10px', padding: '8px 10px', background: theme.card, borderRadius: '8px', flexShrink: 0, border: `1px solid ${theme.border}` }}>
            {(browseData.breadcrumbs || []).map((crumb, i) => (
              <span key={crumb.subdir + i} style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                {i > 0 && <span style={{ color: theme.textSub, fontSize: '0.75rem', padding: '0 2px' }}><Icon icon={ChevronRight} size={14} /></span>}
                <span
                  onClick={() => browseTo(crumb.subdir)}
                  // The last crumb is the directory you are already in — it is
                  // not an action, so it stays out of the tab order.
                  role={i === (browseData.breadcrumbs.length - 1) ? undefined : 'button'}
                  tabIndex={i === (browseData.breadcrumbs.length - 1) ? undefined : 0}
                  onKeyDown={ev => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); browseTo(crumb.subdir); } }}
                  onFocus={ev => { ev.currentTarget.style.outline = `2px solid ${theme.accent}`; ev.currentTarget.style.outlineOffset = '1px'; }}
                  onBlur={ev => { ev.currentTarget.style.outline = 'none'; }}
                  style={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    color: theme.text,
                    fontWeight: i === (browseData.breadcrumbs.length - 1) ? 'bold' : 'normal',
                    cursor: i === (browseData.breadcrumbs.length - 1) ? 'default' : 'pointer',
                    textDecoration: i === (browseData.breadcrumbs.length - 1) ? 'none' : 'underline',
                    padding: '1px 3px',
                    borderRadius: '3px',
                  }}
                >
                  {i === 0 ? '/' : crumb.label}
                </span>
              </span>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '8px', flexShrink: 0, marginBottom: '12px' }}>
            <Button
              theme={theme}
              variant="secondary"
              onClick={saveDirectoryConfig}
              loading={configSaving}
              icon={Check}
              style={{ flex: 3 }}
            >
              {configSaving ? 'Saving...' : 'Set as Working Directory'}
            </Button>
            <Button
              theme={theme}
              variant="secondary"
              onClick={resetToMountRoot}
              disabled={configSaving}
              style={{ flex: 1 }}
            >
              Reset
            </Button>
          </div>

          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', border: `1px solid ${theme.border}`, borderRadius: '8px', marginBottom: '12px' }}>
            {browseData.entries.filter(e => {
              const n = (e.name || '').toLowerCase();
              return !(n.startsWith('#') || n.startsWith('@') || n === '.recycle' || n === '@eadir' || n === '.ds_store' || n === 'lost+found');
            }).length === 0 && !browseData.current_subdir ? (
              <div style={{ padding: '16px', color: theme.textSub, fontSize: '0.8rem', textAlign: 'center' }}>
                No files or folders here.
              </div>
            ) : (
              <>
                {browseData.current_subdir && (
                  <div
                    onClick={() => {
                      const parts = browseData.current_subdir.split('/').filter(Boolean);
                      parts.pop();
                      browseTo(parts.join('/'));
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label="Go to parent folder"
                    onKeyDown={ev => {
                      if (ev.key !== 'Enter' && ev.key !== ' ') return;
                      ev.preventDefault();
                      const parts = browseData.current_subdir.split('/').filter(Boolean);
                      parts.pop();
                      browseTo(parts.join('/'));
                    }}
                    onFocus={ev => { ev.currentTarget.style.outline = `2px solid ${theme.accent}`; ev.currentTarget.style.outlineOffset = '-2px'; }}
                    onBlur={ev => { ev.currentTarget.style.outline = 'none'; }}
                    style={{ padding: '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: `1px solid ${theme.border}`, fontSize: '0.82rem', background: theme.card }}
                  >
                    <Icon icon={ArrowLeft} size={14} />
                    <span>..</span>
                  </div>
                )}
                {browseData.entries.filter(e => {
                  const n = (e.name || '').toLowerCase();
                  return !(n.startsWith('#') || n.startsWith('@') || n === '.recycle' || n === '@eadir' || n === '.ds_store' || n === 'lost+found');
                }).map(e => {
                  const isUnsupported = !e.is_dir && e.file_type === 'mkv';
                  const fileIcon = {
                    vtt_sv: <Icon icon={FileText} size={14} />,
                    vtt: <Icon icon={FileText} size={14} />,
                    srt: <Icon icon={FileText} size={14} />,
                    mp4: <Icon icon={Film} size={14} />,
                    webm: <Icon icon={Film} size={14} />,
                    mkv: <Icon icon={AlertTriangle} size={14} />,
                  }[e.file_type] || <Icon icon={File} size={14} />;

                  return (
                    <div
                      key={e.rel_path}
                      onClick={() => e.is_dir ? browseTo(e.rel_path) : null}
                      // Only folders do anything on click, so only folders join
                      // the tab order — a focusable file row would be a dead stop.
                      role={e.is_dir ? 'button' : undefined}
                      tabIndex={e.is_dir ? 0 : undefined}
                      onKeyDown={ev => {
                        if (!e.is_dir) return;
                        if (ev.target !== ev.currentTarget) return; // the → MP4 button keys itself
                        if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); browseTo(e.rel_path); }
                      }}
                      onFocus={ev => { if (e.is_dir) { ev.currentTarget.style.outline = `2px solid ${theme.accent}`; ev.currentTarget.style.outlineOffset = '-2px'; } }}
                      onBlur={ev => { ev.currentTarget.style.outline = 'none'; }}
                      style={{
                        padding: '8px 14px',
                        cursor: e.is_dir ? 'pointer' : 'default',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${theme.border}`,
                        fontSize: '0.82rem',
                        opacity: 1,
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={ev => { if (e.is_dir) ev.currentTarget.style.background = theme.card; }}
                      onMouseLeave={ev => { ev.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, overflow: 'hidden' }}>
                        <span style={{ flexShrink: 0 }}>{e.is_dir ? <Icon icon={Folder} size={14} /> : fileIcon}</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isUnsupported ? theme.textSub : theme.text }}>
                          {e.name}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, marginLeft: '8px' }}>
                        {e.is_dir && e.vtt_count > 0 && (
                          <span style={{ fontSize: '0.65rem', background: theme.card, color: theme.textSub, padding: '2px 7px', borderRadius: '10px', fontWeight: 'bold', border: `1px solid ${theme.border}` }}>
                            {e.vtt_count} VTT
                          </span>
                        )}
                        {e.is_dir && e.mp4_count > 0 && (
                          <span style={{ fontSize: '0.65rem', background: theme.card, color: theme.textSub, padding: '2px 7px', borderRadius: '10px', fontWeight: 'bold', border: `1px solid ${theme.border}` }}>
                            {e.mp4_count} MP4
                          </span>
                        )}
                        {e.is_dir && e.mkv_count > 0 && (
                          <span style={{ fontSize: '0.65rem', background: `${theme.accent}22`, color: theme.accent, padding: '2px 7px', borderRadius: '10px', fontWeight: 'bold', border: `1px solid ${theme.accent}55` }}>
                            {e.mkv_count} MKV
                          </span>
                        )}
                        {!e.is_dir && isUnsupported && (
                          <Button
                            theme={theme}
                            variant="secondary"
                            size="sm"
                            onClick={ev => { ev.stopPropagation(); startConversion(e.rel_path); }}
                          >
                            → MP4
                          </Button>
                        )}
                        {!e.is_dir && e.size_mb > 0 && (
                          <span style={{ fontSize: '0.65rem', color: theme.textSub }}>
                            {e.size_mb} MB
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {workingDirInfo && workingDirInfo.pairs && workingDirInfo.pairs.length > 0 && (
            <div style={{ flexShrink: 0, minHeight: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontSize: '0.7rem', color: theme.textSub, fontWeight: 'bold', letterSpacing: '0.05em' }}>
                  SUBTITLE FILES IN WORKING DIRECTORY
                </div>
                {workingDirInfo.pairs.some(p => !p.already_imported) && (
                  <Button
                    theme={theme}
                    variant="secondary"
                    size="sm"
                    icon={Upload}
                    disabled={!!importBusy}
                    onClick={() => {
                      if (importBusy) return;
                      setImportBusy('__all__');
                      axios.post(`${BACKEND_URL}/working-dir/import-all`)
                        .then(() => {
                          axios.get(`${BACKEND_URL}/working-dir`).then(r => setWorkingDirInfo(r.data));
                          refreshData(true);
                          setConfigMessage({ type: 'ok', text: '✓ All subtitles imported.' });
                        })
                        .catch(err => setConfigMessage({ type: 'err', text: `Import failed: ${err?.response?.data?.detail || err.message}` }))
                        .finally(() => setImportBusy(null));
                    }}
                  >
                    {importBusy === '__all__' ? 'Importing…' : 'Import All'}
                  </Button>
                )}
              </div>
              <div style={{ maxHeight: '28vh', overflowY: 'auto', border: `1px solid ${theme.border}`, borderRadius: '8px', background: theme.card, padding: '6px' }}>
                {workingDirInfo.pairs.map((pair, index) => (
                  <div key={pair.subtitle_file || `${pair.video_id}_${pair.part}_${index}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', background: theme.card, borderRadius: '6px', marginBottom: '4px', fontSize: '0.75rem', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', flex: 1 }}>
                      <span style={{ flexShrink: 0 }}>{pair.mp4_file ? <Icon icon={Film} size={14} /> : <Icon icon={FileText} size={14} />}</span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: theme.text, fontFamily: 'monospace', fontSize: '0.72rem' }}>
                        {pair.subtitle_file}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                      {pair.already_imported
                        ? <span style={{ fontSize: '0.65rem', background: `${theme.success}22`, color: theme.textSub, border: `1px solid ${theme.success}44`, padding: '2px 8px', borderRadius: '10px', fontWeight: 'bold' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              <Icon icon={Check} size={10} />
                              {pair.line_count} lines
                            </span>
                          </span>
                        : <Button
                            theme={theme}
                            variant="secondary"
                            size="sm"
                            icon={Upload}
                            disabled={!!importBusy}
                            onClick={() => {
                              if (importBusy) return;
                              const key = `${pair.video_id}_${pair.part}`;
                              setImportBusy(key);
                              axios.post(`${BACKEND_URL}/working-dir/import?video_id=${encodeURIComponent(pair.video_id)}&part=${pair.part}`)
                                .then(() => {
                                  axios.get(`${BACKEND_URL}/working-dir`).then(r => setWorkingDirInfo(r.data));
                                  refreshVideos(); refreshMarks();
                                })
                                .catch(err => setConfigMessage({ type: 'err', text: `Import failed: ${err?.response?.data?.detail || err.message}` }))
                                .finally(() => setImportBusy(null));
                            }}
                          >
                            {importBusy === `${pair.video_id}_${pair.part}` ? 'Importing…' : 'Import'}
                          </Button>
                      }
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textSub }}>
          <Button theme={theme} variant="secondary" onClick={() => browseTo(activeConfig?.active_subdir ?? '')}>
            Open Browser
          </Button>
        </div>
      )}
    </div>
  );

  useEffect(() => {
    const node = headerShellRef.current;
    if (!node) return;
    const update = () => {
      const next = Math.ceil(node.getBoundingClientRect().height || 0);
      if (next > 0) setAppShellOffset(next);
    };
    update();
    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(update);
      observer.observe(node);
    }
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      if (observer) observer.disconnect();
    };
  }, [appMode, subjectDetailsTab]);

  if (serverError) return (
    <div style={{ background: '#111', color: 'white', height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
      <h2><Icon icon={Server} size={20} style={{marginRight:'8px'}} />Backend Offline</h2>
      <p style={{ color: '#888' }}>{BACKEND_URL}</p>
      <button onClick={() => window.location.reload()} style={{ padding: '10px 24px', background: '#555', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>Retry</button>
    </div>
  );

  if (!authChecked) {
    return (
      <IconStrokeContext.Provider value={iconStrokeWidth}>
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: theme.bg, color: theme.textSub, fontFamily: theme.fontFamily }}>
          <div style={{ fontSize: '0.9rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Loading…</div>
        </div>
      </IconStrokeContext.Provider>
    );
  }

  if (showLoginOnly) {
    return (
      <IconStrokeContext.Provider value={iconStrokeWidth}>
        <LoginModal
          BACKEND_URL={BACKEND_URL}
          googleClientId={googleClientId}
          theme={theme}
          onLogin={doLogin}
          isDarkMode={isDarkMode}
          isDevEnvironment={isDevEnvironment}
          onToggleDark={() => { const next = !isDarkMode; setIsDarkMode(next); localStorage.setItem('darkMode', next); }}
        />
      </IconStrokeContext.Provider>
    );
  }

  return (
    <IconStrokeContext.Provider value={iconStrokeWidth}>
    <ToastProvider value={toastApi}>
    <ConfirmProvider value={confirm}>
    <PromptProvider value={prompt}>
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: theme.bg, color: theme.text, overflow: 'hidden', fontFamily: theme.fontFamily, paddingLeft: '52px', boxSizing: 'border-box' }}>

      {/* ── LEFT SPINE — the shallow shell's persistent navigation. Every screen
           is first-class here (the four that used to hide in dropdowns now sit
           on the rail), so the top folder-tab strip is retired. Fixed on the
           left; the header and content are shifted right by its width via the
           root's paddingLeft. Active tint uses contrastText, so the label icon
           stays legible on every scheme. ── */}
      <nav aria-label="Primary navigation" style={{ position: 'fixed', left: 0, top: 0, bottom: 0, width: '52px', background: theme.sidebar, borderRight: `1px solid ${theme.border}`, zIndex: 150, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 0', gap: '2px' }}>
        {(() => {
          const hasWorkDir = !!(activeConfig?.active_subdir);
          const needsWorkDir = new Set(['subject', 'editor', 'qa', 'clips']);
          const go = (mode) => {
            if (mode === 'home' && currentUser?.role !== 'admin' && assignedSubjects.length > 1) {
              axiosAuth.get('/pipeline/projects').then(r => {
                const projects = r.data || [];
                if (projects.length > 1) setProjectChooserList(projects);
                else { setAppMode('home'); localStorage.setItem('appMode', 'home'); }
              }).catch(() => { setAppMode('home'); localStorage.setItem('appMode', 'home'); });
              return;
            }
            setAppMode(mode); localStorage.setItem('appMode', mode);
            if (mode === 'qa') refreshQARef.current();
            if (mode === 'clips') loadEditorClips();
          };
          const subjLabel = (() => { const r = selectedVideo ? getProjectRoot(selectedVideo) : (activeConfig?.active_subdir || '').split('/').filter(Boolean).pop() || ''; return subjects[r]?.display_name || cleanLabel(r) || 'Details'; })();
          const sStyle = (active, disabled) => ({ width: '40px', height: '40px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '10px', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', background: active ? theme.glossaryAccent : 'transparent', color: active ? contrastText(theme.glossaryAccent) : theme.textSub, opacity: disabled ? 0.3 : 1, transition: '0.15s', position: 'relative' });
          const divider = (extra = {}) => <div style={{ width: '24px', height: '1px', background: theme.border, margin: '6px 0', flexShrink: 0, ...extra }} />;
          // One Dashboard only. The Project Board is a per-project drill-down
          // reached by clicking a project in the dashboard's Board view, not a
          // top-level destination, so it is not on the rail.
          const screenGroups = [
            [
              { id: 'home', label: (currentUser?.role !== 'admin' && assignedSubjects.length > 1) ? 'Projects' : t('nav.dashboard'), icon: LayoutDashboard },
              { id: 'subject', label: subjLabel, icon: User },
              { id: 'editor', label: t('nav.editor'), icon: PenLine },
              { id: 'qa', label: t('nav.qa'), icon: ListChecks },
              { id: 'clips', label: t('nav.clips'), icon: Scissors },
            ],
          ];
          const screenBtn = ({ id, label, icon }) => {
            const disabled = !hasWorkDir && needsWorkDir.has(id);
            const active = appMode === id;
            const fg = active ? contrastText(theme.glossaryAccent) : theme.textSub;
            return (
              <button key={id} onClick={() => { if (!disabled) go(id); }}
                title={disabled ? `${label} — set a working directory first` : label}
                aria-label={label} aria-current={active ? 'page' : undefined} disabled={disabled}
                style={sStyle(active, disabled)}>
                <Icon icon={icon} size={18} color={fg} />
                {id === 'access-requests' && accessRequests.length > 0 && (
                  <span style={{ position: 'absolute', transform: 'translate(11px, -11px)', minWidth: '15px', height: '15px', borderRadius: '999px', background: theme.danger || '#ef4444', color: '#fff', fontSize: '0.55rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>{accessRequests.length}</span>
                )}
              </button>
            );
          };
          return (
            <>
              {screenGroups.map((group, gi) => (
                <React.Fragment key={gi}>
                  {gi > 0 && divider()}
                  {group.map(screenBtn)}
                </React.Fragment>
              ))}

              {/* Reports + Tools — the menus that used to sit in the top bar, now
                  on the rail. They open as flyouts to the RIGHT of the spine
                  (left = the button's right edge), computed from each ref. */}
              {divider()}
              <button ref={exportBtnRef} title="Reports" aria-label="Reports"
                onClick={() => {
                  if (exportBtnRef.current) { const r = exportBtnRef.current.getBoundingClientRect(); setExportAnchor({ top: r.top, left: r.right + 8 }); }
                  setShowToolsMenu(false);
                  setShowExport(v => {
                    if (!v) {
                      const rt = selectedVideo ? getProjectRoot(selectedVideo) : (activeConfig?.active_subdir || '').split('/').filter(Boolean).pop() || '';
                      if (rt) {
                        axios.get(`${BACKEND_URL}/translate/languages/${encodeURIComponent(rt)}`).then(r => setSubjectVttLangs(r.data.languages || [])).catch(() => {});
                        axios.get(`${BACKEND_URL}/translate/versions/${encodeURIComponent(rt)}`).then(r => setVttVersions(r.data || [])).catch(() => setVttVersions([]));
                      }
                    }
                    return !v;
                  });
                }}
                style={sStyle(showExport, false)}>
                <Icon icon={FileText} size={18} color={showExport ? contrastText(theme.glossaryAccent) : theme.textSub} />
              </button>
              <button ref={toolsBtnRef} title={t('nav.tools')} aria-label={t('nav.tools')}
                onClick={() => {
                  if (toolsBtnRef.current) { const r = toolsBtnRef.current.getBoundingClientRect(); setToolsAnchor({ top: r.top, left: r.right + 8 }); }
                  setShowExport(false);
                  setShowToolsMenu(v => !v);
                }}
                style={sStyle(showToolsMenu, false)}>
                <Icon icon={Hammer} size={18} color={showToolsMenu ? contrastText(theme.glossaryAccent) : theme.textSub} />
              </button>

              {/* Admin — pinned to the bottom of the rail */}
              {currentUser?.role === 'admin' && (
                <>
                  {divider({ marginTop: 'auto' })}
                  {[
                    { id: 'admin', label: t('nav.admin'), icon: Settings },
                    { id: 'access-requests', label: `Access Requests${accessRequests.length > 0 ? ` (${accessRequests.length})` : ''}`, icon: Mail },
                  ].map(screenBtn)}
                </>
              )}
            </>
          );
        })()}
      </nav>

      {/* ── HEADER ── */}
      <div ref={headerShellRef} style={{ display: 'flex', flexDirection: 'column', background: theme.bg, zIndex: 100, flexShrink: 0, position: 'fixed', top: 0, left: '52px', right: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 20px', height: '56px', position: 'relative', background: theme.sidebar }}>

        {/* LEFT: Brandmark + title + subject name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>

          {/* App logo mark — reads config from localStorage via AppLogoMark */}
          <AppLogoMark fallbackSize={36} />

          {/* Title block + subject name */}
          <div style={{ minWidth: 0, width: '320px', maxWidth: '320px' }}>
            <div style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '1.15rem', fontWeight: 400, letterSpacing: '0.02em', color: theme.text, lineHeight: 1.1, whiteSpace: 'nowrap', width: '100%' }}>
              Giving Words to Voices™
            </div>
            {(() => {
              // Show the CURRENT working directory's subject. Only fall back to
              // the selected video when the working directory is a parent that
              // holds several subjects (so it has no subject of its own). This
              // fixes the header sticking to a stale name after the working
              // directory changes — it always tracked selectedVideo before.
              const dirRoot = (activeConfig?.active_subdir || '').split('/').filter(Boolean).pop() || '';
              const vidRoot = selectedVideo ? getProjectRoot(selectedVideo) : '';
              const root = subjects[dirRoot] ? dirRoot : (vidRoot || dirRoot);
              const sub  = subjects[root];
              const name = sub?.display_name || (root ? cleanLabel(root) : '');
              if (!name) return null;
              return (
                <div style={{ marginTop: '1px', width: '100%' }}>
                  <span style={{ display: 'block', fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '0.85rem', fontWeight: 400, fontStyle: 'italic', color: theme.textSub, letterSpacing: '0.03em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                    {name}
                  </span>
                </div>
              );
            })()}
          </div>
        </div>

        {isDevEnvironment && (
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              minHeight: '26px',
              padding: '4px 12px',
              borderRadius: '999px',
              background: 'rgba(220, 38, 38, 0.12)',
              border: '1px solid rgba(220, 38, 38, 0.34)',
              color: '#dc2626',
              fontSize: '0.66rem',
              fontWeight: 800,
              letterSpacing: '0.09em',
              textTransform: 'uppercase',
              boxShadow: '0 4px 12px rgba(220,38,38,0.12)',
              zIndex: 1,
            }}
          >
            <Icon icon={AlertTriangle} size={11} color="#dc2626" />
            {t('misc.devEnvironment')}
          </div>
        )}

        {/* RIGHT: controls */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexShrink: 0 }}>

          {/* Toast notifications — inline in top bar */}
          <div id="toast-anchor" style={{ display: 'flex', alignItems: 'center' }} />

          {/* Help */}
          <button type="button" onClick={() => setShowWorkflowGuide(true)} style={headerIconButtonStyle}
            title="Workflow Guide" aria-label="Workflow Guide"
          >
            <Icon icon={HelpCircle} size={18} />
          </button>

          {/* Shortcuts */}
          <button type="button" onClick={() => { setShowSettings(false); setShowDirectoryPalette(false); setBrowseData(null); setConfigMessage(null); setListeningShortcutId(null); setShowShortcutsPalette(v => !v); }} style={headerIconButtonStyle}
            title="Keyboard Shortcuts" aria-label="Keyboard Shortcuts"
          >
            <Icon icon={Keyboard} size={18} />
          </button>

          {/* Gear */}
          <button type="button" onClick={() => {
            setShowShortcutsPalette(false);
            setShowDirectoryPalette(false);
            setListeningShortcutId(null);
            setShowSettings(v => {
              const next = !v;
              if (next) setSettingsTab('textsize');
              return next;
            });
            setBrowseData(null);
            setConfigMessage(null);
          }} style={headerIconButtonStyle} title="Workspace Settings" aria-label="Workspace Settings">
            <Icon icon={Settings} size={18} />
          </button>

          {/* Whisper status indicator — admin only, always visible */}
          {currentUser?.role === 'admin' && (
            <button
              onClick={() => setShowWhisperLogs(true)}
              title={
                whisperStatus === 'ok' ? 'Whisper server is online — click to view logs' :
                whisperStatus === 'down' ? 'Whisper server is DOWN — click to view logs' :
                'Whisper status checking — click to view logs'
              }
              style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                height: '28px', padding: '0 8px',
                background: whisperStatus === 'down' ? 'rgba(239,68,68,0.12)' : 'transparent',
                border: `1px solid ${whisperStatus === 'down' ? 'rgba(239,68,68,0.5)' : theme.border}`,
                borderRadius: '6px', cursor: 'pointer',
                animation: whisperStatus === 'down' ? 'pulse-red 1.6s ease-in-out infinite' : 'none',
              }}
            >
              <span style={{
                width: '8px', height: '8px', borderRadius: '50%',
                background: whisperStatus === 'ok' ? '#4ade80' : whisperStatus === 'down' ? '#ef4444' : '#a0a0b0',
                boxShadow: whisperStatus === 'ok' ? '0 0 6px #4ade8088' : whisperStatus === 'down' ? '0 0 8px #ef4444aa' : 'none',
              }} />
              <Icon icon={Terminal} size={11} color={whisperStatus === 'down' ? '#ef4444' : theme.textSub} />
              <span style={{ fontSize: '0.62rem', fontWeight: 700, color: whisperStatus === 'down' ? '#ef4444' : theme.textSub, letterSpacing: '0.04em' }}>
                {whisperStatus === 'ok' ? 'WHISPER' : whisperStatus === 'down' ? 'WHISPER DOWN' : 'WHISPER ?'}
              </span>
            </button>
          )}

          {/* User badge + dropdown */}
          {currentUser && (
            <div style={{ position: 'relative' }} ref={userMenuRef}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '0 10px', height: '28px', background: theme.card, borderRadius: '20px', border: `1px solid ${showUserMenu ? theme.activeBorder : (accessRequests.length > 0 && currentUser.role === 'admin' ? 'rgba(239,68,68,0.5)' : theme.border)}`, cursor: 'pointer', userSelect: 'none', boxSizing: 'border-box' }}
                onClick={() => setShowUserMenu(v => !v)}
              >
                <Icon icon={currentUser.role === 'admin' ? Shield : User} size={13} color={currentUser.role === 'admin' ? '#f59e0b' : theme.glossaryAccent} />
                <span style={{ fontSize: '0.75rem', fontWeight: 'bold', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {currentUser.display_name || currentUser.name || currentUser.email?.split('@')[0]}
                </span>
                <Badge theme={theme} role={currentUser.role} size="sm">
                  {currentUser.role}
                </Badge>
                {/* Pending access request alert */}
                {currentUser.role === 'admin' && accessRequests.length > 0 && (
                  <span
                    onClick={e => { e.stopPropagation(); setAppMode('access-requests'); setShowUserMenu(false); }}
                    title={`${accessRequests.length} pending access request${accessRequests.length > 1 ? 's' : ''} — click to review`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '2px 7px', borderRadius: '10px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)', color: '#ef4444', fontSize: '0.6rem', fontWeight: 800, lineHeight: 1, cursor: 'pointer' }}>
                    <Icon icon={Mail} size={9} color="#ef4444" />
                    {accessRequests.length}
                  </span>
                )}
                {sessionDisplay && (
                  <span style={{ fontSize: '0.58rem', color: theme.textSub, borderLeft: `1px solid ${theme.border}`, paddingLeft: '6px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <Icon icon={Clock} size={9} color={theme.textSub} />{sessionDisplay}
                  </span>
                )}
                <Icon icon={ChevronDown} size={11} color={theme.textSub} />
              </div>
              {showUserMenu && ReactDOM.createPortal(
                <div
                  ref={userMenuPanelRef}
                  style={{ position: 'fixed', top: `${appShellOffset + 8}px`, right: '16px', width: '220px', background: theme.sidebar, border: `1.5px solid ${theme.border}`, borderRadius: '12px', zIndex: Z.dropdown, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', padding: '6px 0', overflow: 'hidden' }}
                >
                  {/* User info header */}
                  <div style={{ padding: '10px 14px 8px', borderBottom: `1px solid ${theme.border}`, marginBottom: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {currentUser.display_name || currentUser.name || currentUser.email?.split('@')[0]}
                      </div>
                      <Badge theme={theme} role={currentUser.role} size="sm" style={{ flexShrink: 0 }}>
                        {currentUser.role}
                      </Badge>
                    </div>
                    <div style={{ fontSize: '0.7rem', color: theme.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentUser.email}</div>
                    {sessionDisplay && (
                      <div style={{ fontSize: '0.65rem', color: theme.textSub, marginTop: '5px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Icon icon={Clock} size={10} color={theme.textSub} />
                        Session: <strong style={{ color: theme.text }}>{sessionDisplay}</strong>
                      </div>
                    )}
                    <div style={{ fontSize: '0.65rem', color: theme.textSub, marginTop: '5px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Icon icon={History} size={10} color={theme.textSub} />
                      Total logged in: <strong style={{ color: theme.text }}>{formatLoggedTime(sessionSummary.total_seconds)}</strong>
                    </div>
                    {sessionSummary.latest_session && (
                      <div style={{ fontSize: '0.62rem', color: theme.textSub, marginTop: '5px', lineHeight: 1.4 }}>
                        {sessionSummary.latest_session.device_name && (
                          <div><strong style={{ color: theme.text }}>Device:</strong> {sessionSummary.latest_session.device_name}</div>
                        )}
                        {sessionSummary.latest_session.ip_address && (
                          <div><strong style={{ color: theme.text }}>IP:</strong> {sessionSummary.latest_session.ip_address}</div>
                        )}
                        {sessionSummary.latest_session.location_label && (
                          <div><strong style={{ color: theme.text }}>Location:</strong> {sessionSummary.latest_session.location_label}</div>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Idle timeout preference */}
                  <div style={{ padding: '8px 14px 6px', borderBottom: `1px solid ${theme.border}`, marginBottom: '4px' }}>
                    <div style={{ fontSize: '0.62rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Icon icon={Timer} size={10} color={theme.textSub} /> Auto-logout after idle
                    </div>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {IDLE_OPTIONS.map(m => (
                        <button key={m} onClick={() => setIdleMinutesPersist(m)}
                          style={{ padding: '2px 7px', borderRadius: '10px', fontSize: '0.65rem', fontWeight: idleMinutes === m ? 700 : 400, cursor: 'pointer', border: `1px solid ${idleMinutes === m ? theme.glossaryAccent : theme.border}`, background: idleMinutes === m ? `${theme.glossaryAccent}20` : 'transparent', color: idleMinutes === m ? theme.glossaryAccent : theme.textSub, transition: '0.12s' }}>
                          {m}m
                        </button>
                      ))}
                      <button onClick={async () => {
                        const v = await prompt({
                          title: 'Custom idle timeout',
                          message: 'Custom idle timeout (minutes):',
                          defaultValue: String(idleMinutes),
                          confirmLabel: 'Set',
                        });
                        if (v === null) return;   // cancelled — no feedback needed
                        const n = parseInt(v, 10);
                        if (n > 0 && n <= 480) setIdleMinutesPersist(n);
                        else pushToast('idle-timeout', 'error', 'Idle timeout must be a whole number of minutes between 1 and 480.');
                      }} style={{ padding: '2px 7px', borderRadius: '10px', fontSize: '0.65rem', cursor: 'pointer', border: `1px solid ${theme.border}`, background: 'transparent', color: theme.textSub }}>
                        custom…
                      </button>
                    </div>
                  </div>
                  {/* Menu items */}
                  {[
                    { icon: UserCog, label: 'Edit Profile',    action: () => { setShowProfileModal(true); setShowUserMenu(false); } },
                    ...(currentUser.role === 'admin' ? [
                      { icon: Users, label: 'Manage Users', action: () => { setAppMode('admin'); localStorage.setItem('appMode', 'admin'); setShowUserMenu(false); } },
                      { icon: Mail,  label: `Access Requests${accessRequests.length > 0 ? ` (${accessRequests.length})` : ''}`, action: () => { setAppMode('access-requests'); setShowUserMenu(false); }, highlight: accessRequests.length > 0 },
                      { icon: Terminal, label: `Whisper Status: ${whisperStatus === 'ok' ? 'Online' : whisperStatus === 'down' ? 'Down' : 'Checking…'}`, action: () => { setShowWhisperLogs(true); setShowUserMenu(false); } },
                    ] : []),
                    { icon: LogOut, label: 'Sign Out', action: () => { doLogout(); setShowUserMenu(false); }, danger: true },
                  ].map(({ icon, label, action, danger, highlight }) => (
                    <button key={label} onClick={action}
                      style={{ width: '100%', padding: '9px 14px', background: highlight ? 'rgba(239,68,68,0.06)' : 'none', border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '9px', fontSize: '0.83rem', color: danger ? '#ef4444' : highlight ? '#ef4444' : theme.text, fontWeight: highlight ? 700 : 500 }}
                      onMouseEnter={e => e.currentTarget.style.background = highlight ? 'rgba(239,68,68,0.12)' : theme.card}
                      onMouseLeave={e => e.currentTarget.style.background = highlight ? 'rgba(239,68,68,0.06)' : 'none'}
                    ><Icon icon={icon} size={14} color={danger ? '#ef4444' : highlight ? '#ef4444' : theme.textSub} />{label}</button>
                  ))}
                </div>,
                document.body
              )}
            </div>
          )}
          {!currentUser && authChecked && (
            <button onClick={() => setShowLogin(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '4px 10px', background: theme.glossaryAccent, color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 'bold' }}>
              <Icon icon={LogIn} size={13} />Sign In
            </button>
          )}

          {/* Language switcher — single flag, click to cycle */}
          {(() => {
            const currentIdx = i18nLanguages.findIndex(l => l.code === i18nLocale);
            const current = i18nLanguages[currentIdx] || i18nLanguages[0];
            return (
              <button
                onClick={() => {
                  const nextIdx = (currentIdx + 1) % i18nLanguages.length;
                  setI18nLocale(i18nLanguages[nextIdx].code);
                }}
                title={`Interface: ${current?.label || i18nLocale} — click to change`}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', fontSize: '1.1rem', lineHeight: 1, display: 'flex', alignItems: 'center' }}
              >
                {current?.flag || '🌐'}
              </button>
            );
          })()}

          {/* Dark/light */}
          <button
            type="button"
            onClick={() => { const next = !isDarkMode; setIsDarkMode(next); localStorage.setItem('darkMode', next); }}
            title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px', background: 'none', border: 'none', color: 'inherit' }}
          >
            {isDarkMode ? <Icon icon={Sun} size={18} /> : <Icon icon={Moon} size={18} />}
          </button>
        </div>
      </div>

      {/* ── Full-width rule ── */}
      <div style={{ width: '100%', height: '1px', background: theme.border }} />

      {/* The folder-tab strip (screen nav + Reports/Tools) is retired; screen
          navigation and the Reports/Tools flyouts now live on the left spine,
          so the header is a single row. */}

      {/* ── UI Help Panel ── */}
      {showWorkflowGuide && <WorkflowGuide onClose={() => setShowWorkflowGuide(false)} theme={theme} />}

      {showWhisperLogs && currentUser?.role === 'admin' && ReactDOM.createPortal(
        <WhisperLogsModal
          axiosAuth={axiosAuth}
          onClose={() => setShowWhisperLogs(false)}
          theme={theme}
          whisperStatus={whisperStatus}
        />,
        document.body
      )}

      {showExport && ReactDOM.createPortal(
        <div ref={exportFlyoutRef} style={{ position: 'fixed', top: exportAnchor?.top ?? (appShellOffset + 8), left: exportAnchor?.left, right: exportAnchor?.left != null ? undefined : (exportAnchor?.right ?? 20), width: '235px', background: theme.sidebar, border: `1px solid ${theme.border}`, borderRadius: '10px', boxShadow: '0 8px 32px rgba(0,0,0,0.22)', zIndex: Z.dropdown, overflow: 'hidden' }}>

          <div style={{ padding: '6px 12px 3px', fontSize: '0.59rem', color: theme.textSub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Include in Report</div>
          <div style={{ padding: '4px 14px 6px', display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
            {(() => {
              return [...subjectVttLangs].sort((a,b) => getLanguageName(a).localeCompare(getLanguageName(b))).map(l => {
                const checked = reportLangs.includes(l);
                const label = getLanguageName(l);
                const canDelete = currentUser?.role === 'admin' || currentUser?.role === 'editor';
                return (
                  <span key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: '0', borderRadius: '10px',
                    border: `1px solid ${checked ? theme.glossaryAccent : theme.border}`,
                    background: checked ? `${theme.glossaryAccent}18` : 'transparent' }}>
                    <button onClick={() => setReportLangs(prev => checked ? prev.filter(x => x !== l) : [...prev, l])}
                      style={{ padding: '3px 8px', borderRadius: canDelete ? '10px 0 0 10px' : '10px', fontSize: '0.62rem', fontWeight: 700, cursor: 'pointer',
                        border: 'none', background: 'transparent',
                        color: checked ? theme.glossaryAccent : theme.textSub }}>
                      {label}
                    </button>
                    {canDelete && (
                      <button onClick={async e => {
                        e.stopPropagation();
                        const r = selectedVideo ? getProjectRoot(selectedVideo) : (activeConfig?.active_subdir || '').split('/').filter(Boolean).pop() || '';
                        if (!r) return;
                        if (!await confirm({
                          message: `Delete ${label} (${l.toUpperCase()}) VTT files?\n\nFiles will be versioned to VTT_VERSIONS/ first (recoverable).`,
                          title: `Delete ${label} VTT files?`,
                          confirmLabel: 'Delete',
                          destructive: true,
                        })) return;
                        axios.delete(`${BACKEND_URL}/translate/language/${encodeURIComponent(r)}?lang=${l}`)
                          .then(() => {
                            pushToast(`lang-del-${Date.now()}`, 'success', `Deleted ${label} — files versioned to VTT_VERSIONS/`);
                            setSubjectVttLangs(prev => prev.filter(x => x !== l));
                            setReportLangs(prev => prev.filter(x => x !== l));
                            // Re-fetch versions
                            axios.get(`${BACKEND_URL}/translate/versions/${encodeURIComponent(r)}`).then(vr => setVttVersions(vr.data || [])).catch(() => {});
                          })
                          .catch(err => pushToast(`lang-del-err-${Date.now()}`, 'error', `Delete failed: ${err?.response?.data?.detail || err.message}`));
                      }}
                        title={`Delete ${label} VTT files (versioned first)`}
                        style={{ padding: '3px 5px', borderRadius: '0 10px 10px 0', fontSize: '0.55rem', cursor: 'pointer',
                          border: 'none', borderLeft: `1px solid ${checked ? theme.glossaryAccent + '44' : theme.border}`,
                          background: 'transparent', color: '#ef4444', fontWeight: 700, display: 'flex', alignItems: 'center' }}>
                        <Icon icon={Trash2} size={11} color="#ef4444" />
                      </button>
                    )}
                  </span>
                );
              });
            })()}
          </div>
          <div style={{ height: '1px', background: theme.border, margin: '2px 0' }} />
          <button onClick={() => { const r = selectedVideo ? getProjectRoot(selectedVideo) : (activeConfig?.active_subdir || '').split('/').filter(Boolean).pop() || ''; const langParam = reportLangs.join(',') || 'en'; if (r) { const url = `${BACKEND_URL}/export/subject-report/${encodeURIComponent(r)}?lang=${langParam}`; navigator.clipboard.writeText(url.replace(BACKEND_URL, window.location.origin + '/api')).then(() => {}).catch(() => {}); window.open(authToken ? `${url}&token=${encodeURIComponent(authToken)}` : url); } setShowExport(false); }}
            style={{ width: '100%', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px', fontSize: theme.fsButton, color: theme.text, fontWeight: 600 }}
            onMouseEnter={e => e.currentTarget.style.background = theme.card}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          ><Icon icon={FileText} size={14} color={theme.glossaryAccent} />View Report (+ copy URL)</button>
          <button onClick={() => { const r = selectedVideo ? getProjectRoot(selectedVideo) : (activeConfig?.active_subdir || '').split('/').filter(Boolean).pop() || ''; const langParam = reportLangs.join(',') || 'en'; if (r) { const u = `${BACKEND_URL}/export/subject-report/${encodeURIComponent(r)}?download=1&lang=${langParam}`; window.open(authToken ? `${u}&token=${encodeURIComponent(authToken)}` : u); } setShowExport(false); }}
            style={{ width: '100%', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px', fontSize: theme.fsButton, color: theme.text, fontWeight: 600 }}
            onMouseEnter={e => e.currentTarget.style.background = theme.card}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          ><Icon icon={Download} size={14} color={theme.glossaryAccent} />Download Report (.md)</button>
          <button onClick={() => { setShowExport(false); setShowNotebookLMExport(true); const r = selectedVideo ? getProjectRoot(selectedVideo) : (activeConfig?.active_subdir || '').split('/').filter(Boolean).pop() || ''; if (r) { axios.get(`${BACKEND_URL}/export/notebooklm-info/${encodeURIComponent(r)}`).then(res => setNlmInfo(res.data)).catch(() => setNlmInfo(null)); } }}
            style={{ width: '100%', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px', fontSize: theme.fsButton, color: theme.text, fontWeight: 600 }}
            onMouseEnter={e => e.currentTarget.style.background = theme.card}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          ><Icon icon={Globe} size={14} color={'#8b5cf6'} />Export for NotebookLM</button>

          <div style={{ height: '6px' }} />
        </div>,
        document.body
      )}
      {/* ── Theater Mode Overlay ── */}
      {theaterMode && selectedVideo && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: Z.modal,
            background: '#000', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
          }}
          onKeyDown={e => { if (e.key === 'Escape') setTheaterMode(false); }}
          tabIndex={-1}
          ref={el => el && el.focus()}
        >
          {/* Top bar */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 20px',
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 100%)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontSize: '1rem', fontWeight: 400, color: 'rgba(255,255,255,0.8)',
                letterSpacing: '0.03em',
              }}>
                {subjects[getProjectRoot(selectedVideo)]?.display_name || cleanLabel(getProjectRoot(selectedVideo))}
                {selectedPart > 0 && <span style={{ color: 'rgba(255,255,255,0.4)', marginLeft: '8px' }}>Tape {selectedPart}</span>}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* Subtitle selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {subjectVttLangs.length > 0 && (
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <button
                      onClick={() => setEditorSubLang('off')}
                      style={{
                        padding: '4px 8px', borderRadius: '6px', fontSize: '0.62rem', fontWeight: 700,
                        border: 'none', cursor: 'pointer',
                        background: editorSubLang === 'off' ? 'rgba(255,255,255,0.2)' : 'transparent',
                        color: editorSubLang === 'off' ? '#fff' : 'rgba(255,255,255,0.4)',
                      }}
                    >Off</button>
                    {[...subjectVttLangs].sort().map(lang => {
                      const isActive = editorSubLang === lang;
                      const meta = getLanguageMeta(lang);
                      return (
                        <button
                          key={lang}
                          onClick={() => setEditorSubLang(lang)}
                          title={getLanguageName(lang)}
                          style={{
                            padding: '4px 10px', borderRadius: '6px', fontSize: '0.62rem', fontWeight: 700,
                            border: 'none', cursor: 'pointer',
                            background: isActive ? (meta?.bg || 'rgba(255,255,255,0.2)') : 'transparent',
                            color: isActive ? (meta?.text || '#fff') : 'rgba(255,255,255,0.5)',
                          }}
                        >
                          {getLanguageFlag(lang)} {lang.toUpperCase()}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* Close */}
              <button
                onClick={() => setTheaterMode(false)}
                style={{
                  background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '8px',
                  padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                  color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem', fontWeight: 600,
                }}
              >
                <Icon icon={Minimize2} size={14} />
              </button>
            </div>
          </div>

          {/* Video — fills the screen */}
          <video
            ref={el => {
              if (el && videoRef.current && el !== videoRef.current) {
                // Sync time from main player
                el.currentTime = videoRef.current.currentTime;
                if (!videoRef.current.paused) el.play();
              }
            }}
            controls
            autoPlay
            style={{
              width: '100%', height: '100%', objectFit: 'contain',
              background: '#000',
            }}
            crossOrigin="anonymous"
            onTimeUpdate={e => {
              currentTime.current = e.target.currentTime;
              if (videoRef.current && Math.abs(videoRef.current.currentTime - e.target.currentTime) > 1) {
                videoRef.current.currentTime = e.target.currentTime;
              }
            }}
            onPlay={() => { if (videoRef.current?.paused) videoRef.current.play(); }}
            onPause={() => { if (videoRef.current && !videoRef.current.paused) videoRef.current.pause(); }}
          >
            {(() => {
              const candidates = buildMp4Candidates(selectedVideo);
              return candidates.map((c, i) => (
                <source key={i} src={encodeVideoPath(activeConfig?.active_subdir ?? '', c + '.mp4')} type="video/mp4" />
              ));
            })()}
            {editorSubLang !== 'off' && (() => {
              const parts = selectedVideo.split('.');
              const langSuffix = parts[parts.length - 1];
              const tok = sessionStorage.getItem('gwv_token') || '';
              const trackUrl = editorSubLang === langSuffix
                ? `${BACKEND_URL}/vtt/${encodeURIComponent(selectedVideo)}?part=${selectedPart}&token=${encodeURIComponent(tok)}`
                : `${BACKEND_URL}/vtt/${encodeURIComponent(selectedVideo)}?part=${selectedPart}&lang=${editorSubLang}&token=${encodeURIComponent(tok)}`;
              return <track key={`theater-${editorSubLang}`} label="Subtitles" kind="subtitles" src={trackUrl} srcLang={editorSubLang} default />;
            })()}
          </video>
        </div>,
        document.body
      )}

      {/* ── NotebookLM Export Modal ── */}
      {showNotebookLMExport && (
        <Modal
          open
          onClose={() => setShowNotebookLMExport(false)}
          theme={theme}
          width={580}
          zIndex={Z.modal}
          maxHeight="calc(100vh - 80px)"
          header={(
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon icon={Globe} size={16} color={'#8b5cf6'} />
              <span style={{ fontWeight: 700, fontSize: '0.85rem', color: theme.text }}>Export for NotebookLM</span>
            </div>
          )}
          footer={(
            <>
              <Button theme={theme} variant="outline" onClick={() => setShowNotebookLMExport(false)}>
                Cancel
              </Button>
              <button onClick={() => {
                const r = selectedVideo ? getProjectRoot(selectedVideo) : (activeConfig?.active_subdir || '').split('/').filter(Boolean).pop() || '';
                if (!r) return;
                if (nlmSections.length === 0 && Object.keys(nlmTapes).length === 0) return;
                setNlmExporting(true);
                axios.post(`${BACKEND_URL}/export/notebooklm/${encodeURIComponent(r)}`, { sections: nlmSections, tapes: nlmTapes }, { responseType: 'blob' })
                  .then(res => {
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
                    const a = document.createElement('a'); a.href = url; a.download = `${r}_notebooklm_${_ts()}.zip`; a.click();
                    window.URL.revokeObjectURL(url);
                    setShowNotebookLMExport(false);
                    pushToast('nlm-ok', 'success', `NotebookLM export downloaded (${nlmSections.length + Object.values(nlmTapes).reduce((s, v) => s + (v === 'all' ? (nlmInfo?.tapes?.[Object.keys(nlmTapes).find(k => nlmTapes[k] === v)]?.length || 0) : v.length), 0)} files)`);
                  })
                  .catch(err => pushToast('nlm-err', 'error', `Export failed: ${err?.response?.data?.detail || err.message}`))
                  .finally(() => setNlmExporting(false));
              }}
                disabled={nlmExporting || (nlmSections.length === 0 && Object.keys(nlmTapes).length === 0)}
                style={{ padding: '8px 20px', background: (nlmSections.length === 0 && Object.keys(nlmTapes).length === 0) ? theme.border : '#8b5cf6', border: 'none', borderRadius: '8px', cursor: (nlmSections.length === 0 && Object.keys(nlmTapes).length === 0) ? 'default' : 'pointer', fontSize: '0.72rem', color: '#fff', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                {nlmExporting ? <><Icon icon={Loader} size={13} style={{ animation: 'spin 1s linear infinite' }} /> Exporting...</> : <><Icon icon={Download} size={13} /> Export ZIP</>}
              </button>
            </>
          )}
        >
              <div style={{ fontSize: '0.70rem', color: theme.textSub, marginBottom: '14px' }}>
                Select items to export as separate source files for Google NotebookLM.
                {nlmInfo?.name ? ` Subject: ${nlmInfo.name}` : ''}
              </div>
              {/* Select All */}
              {(() => {
                const allSections = ['biography', 'family', 'timeline', 'places', 'qa'];
                const allTapeKeys = nlmInfo?.tapes ? Object.keys(nlmInfo.tapes) : [];
                const allSelected = allSections.every(s => nlmSections.includes(s)) && allTapeKeys.every(k => !!nlmTapes[k]);
                return (
                  <button onClick={() => {
                    if (allSelected) {
                      setNlmSections([]);
                      setNlmTapes({});
                    } else {
                      setNlmSections([...allSections]);
                      const t = {};
                      allTapeKeys.forEach(k => { t[k] = 'all'; });
                      setNlmTapes(t);
                    }
                  }} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: theme.glossaryAccent, fontWeight: 700, fontSize: '0.70rem', marginBottom: '10px', padding: 0 }}>
                    <Icon icon={allSelected ? CheckSquare : ListChecks} size={13} color={theme.glossaryAccent} />
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </button>
                );
              })()}
              {/* Section checkboxes */}
              <div style={{ fontSize: '0.60rem', color: theme.textSub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px', marginTop: '4px' }}>Sections</div>
              {[
                { key: 'biography', label: 'Biography & Metadata', count: nlmInfo?.counts?.biography },
                { key: 'family', label: 'Family Tree', count: nlmInfo?.counts?.family, extra: nlmInfo?.counts?.relationships ? ` + ${nlmInfo.counts.relationships} relationships` : '' },
                { key: 'timeline', label: 'Timeline', count: nlmInfo?.counts?.timeline, suffix: 'events' },
                { key: 'places', label: 'Places', count: nlmInfo?.counts?.places, suffix: 'places' },
                { key: 'qa', label: 'QA Analysis', count: nlmInfo?.counts?.qa, suffix: 'findings' },
              ].map(item => {
                const checked = nlmSections.includes(item.key);
                const disabled = item.key !== 'biography' && (item.count === 0 || item.count === undefined);
                return (
                  <button key={item.key} onClick={() => { if (disabled) return; setNlmSections(prev => checked ? prev.filter(s => s !== item.key) : [...prev, item.key]); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '7px 10px', background: checked ? `${theme.glossaryAccent}18` : 'none', border: `1px solid ${checked ? theme.glossaryAccent + '40' : 'transparent'}`, borderRadius: '8px', cursor: disabled ? 'default' : 'pointer', marginBottom: '4px', opacity: disabled ? 0.4 : 1 }}>
                    <Icon icon={checked ? CheckSquare : File} size={13} color={checked ? theme.glossaryAccent : theme.textSub} />
                    <span style={{ flex: 1, textAlign: 'left', fontSize: '0.72rem', color: theme.text, fontWeight: checked ? 600 : 400 }}>{item.label}</span>
                    {item.count > 0 && (
                      <span style={{ fontSize: '0.60rem', color: theme.textSub, background: theme.card, padding: '1px 6px', borderRadius: '4px' }}>
                        {item.count}{item.suffix ? ` ${item.suffix}` : ''}{item.extra || ''}
                      </span>
                    )}
                  </button>
                );
              })}
              {/* Transcripts */}
              {nlmInfo?.tapes && Object.keys(nlmInfo.tapes).length > 0 && (
                <>
                  <div style={{ fontSize: '0.60rem', color: theme.textSub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px', marginTop: '14px' }}>Transcripts</div>
                  {Object.entries(nlmInfo.tapes).sort((a, b) => a[0].localeCompare(b[0])).map(([lang, tapeNums]) => {
                    const checked = !!nlmTapes[lang];
                    const langLabel = (getLanguageMeta(lang)?.label || lang).replace(/^\w/, c => c.toUpperCase());
                    return (
                      <button key={lang} onClick={() => { setNlmTapes(prev => { const next = { ...prev }; if (checked) { delete next[lang]; } else { next[lang] = 'all'; } return next; }); }}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '7px 10px', background: checked ? `${theme.glossaryAccent}18` : 'none', border: `1px solid ${checked ? theme.glossaryAccent + '40' : 'transparent'}`, borderRadius: '8px', cursor: 'pointer', marginBottom: '4px' }}>
                        <Icon icon={checked ? CheckSquare : FileText} size={13} color={checked ? theme.glossaryAccent : theme.textSub} />
                        <span style={{ flex: 1, textAlign: 'left', fontSize: '0.72rem', color: theme.text, fontWeight: checked ? 600 : 400 }}>{langLabel} Transcript</span>
                        <span style={{ fontSize: '0.60rem', color: theme.textSub, background: theme.card, padding: '1px 6px', borderRadius: '4px' }}>
                          {tapeNums.length} {tapeNums.length === 1 ? 'tape' : 'tapes'}
                        </span>
                      </button>
                    );
                  })}
                </>
              )}
        </Modal>
      )}
      {/* ── Exported Clip Player Modal ── */}
      {clipPlayerSrc && (
        <Modal
          open
          onClose={() => setClipPlayerSrc(null)}
          theme={theme}
          width={900}
          zIndex={Z.modal}
          maxHeight="calc(100vh - 60px)"
          backdropStyle={{ background: 'rgba(0,0,0,0.75)' }}
          bodyStyle={{ background: '#000', padding: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          header={(
            <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon icon={Film} size={14} color={theme.glossaryAccent} style={{ flexShrink: 0 }} />
              <span style={{ fontWeight: 700, fontSize: '0.80rem', color: theme.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{clipPlayerSrc.name}</span>
              {clipPlayerSrc.subs.length > 0 && renderSubtitleSelector(
                clipPlayerSrc.subs.map(s => s.lang),
                clipPlayerSubLang,
                setClipPlayerSubLang,
                { compact: true }
              )}
              <a href={clipPlayerSrc.url} download={clipPlayerSrc.name} title="Download"
                style={{ color: theme.textSub, display: 'flex', padding: '4px', flexShrink: 0 }}>
                <Download size={14} />
              </a>
            </div>
          )}
        >
              <video key={clipPlayerSrc.url} ref={el => {
                if (!el) return;
                // Live-swap subtitle tracks without reloading video
                const tracks = el.textTracks;
                for (let i = 0; i < tracks.length; i++) {
                  tracks[i].mode = (clipPlayerSubLang !== 'off' && tracks[i].language === clipPlayerSubLang) ? 'showing' : 'hidden';
                }
              }} controls autoPlay style={{ width: '100%', maxHeight: 'calc(100vh - 140px)', objectFit: 'contain' }} crossOrigin="anonymous">
                <source src={clipPlayerSrc.url} type="video/mp4" />
                {clipPlayerSrc.subs.map(s => (
                  <track key={s.lang} label={getLanguageName(s.lang)} kind="subtitles" src={s.url} srcLang={s.lang} />
                ))}
              </video>
        </Modal>
      )}
      {showToolsMenu && ReactDOM.createPortal(
        <div ref={toolsFlyoutRef} style={{ position: 'fixed', top: toolsAnchor?.top ?? (appShellOffset + 8), left: toolsAnchor?.left, right: toolsAnchor?.left != null ? undefined : (toolsAnchor?.right ?? 20), width: '235px', background: theme.sidebar, border: `1px solid ${theme.border}`, borderRadius: '10px', boxShadow: '0 8px 32px rgba(0,0,0,0.22)', zIndex: Z.dropdown, overflow: 'hidden' }}>
          {[
            { icon: QrCode, label: 'QR Code Designer', action: () => { openQRDesigner(); setShowToolsMenu(false); } },
            { icon: BookOpen, label: 'NotebookLM Prompt Library', action: () => { prevAppModeRef.current = appMode; setAppMode('prompt-library'); localStorage.setItem('appMode', 'prompt-library'); setShowToolsMenu(false); } },
            { icon: GitMerge, label: 'Review AI Merges', action: () => { prevAppModeRef.current = appMode; setAppMode('merge-review'); localStorage.setItem('appMode', 'merge-review'); setShowToolsMenu(false); } },
            { icon: PackageOpen, label: 'MKV → MP4 Converter', action: () => {
              const w = window.open('/converter-standalone.html', 'mkv_converter', 'width=860,height=640,resizable=yes,scrollbars=no');
              if (w) {
                w.BACKEND_URL = BACKEND_URL;
                const onMsg = (e) => { if (e.source === w && e.data === 'THEME_READY') { w.postMessage({ type: 'THEME', theme }, '*'); window.removeEventListener('message', onMsg); } };
                window.addEventListener('message', onMsg);
              }
              setShowToolsMenu(false);
            }},
          ].map(({ icon, label, action }) => (
            <button key={label} onClick={action}
              style={{ width: '100%', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px', fontSize: theme.fsButton, color: theme.text, fontWeight: 600 }}
              onMouseEnter={e => e.currentTarget.style.background = theme.card}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            ><Icon icon={icon} size={14} color={theme.textSub} />{label}</button>
          ))}
          <div style={{ height: '6px' }} />
        </div>,
        document.body
      )}

      {appMode === 'subject' && (
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', padding: '0', background: theme.bg, flexShrink: 0 }}>
          {/* Subject detail tabs — drops down from nav like a connected tab */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 8px 6px',
            background: theme.sidebar,
            border: `1px solid ${theme.border}`,
            borderTop: 'none',
            borderRadius: '0 0 12px 12px',
            boxShadow: isDarkMode ? '0 6px 14px rgba(0,0,0,0.15)' : '0 6px 14px rgba(15,23,42,0.06)',
            marginTop: '-1px',
          }}>
              {[
                { id: 'bio', label: 'Biography', icon: User },
                { id: 'places', label: 'Places & Map', icon: MapPin },
                { id: 'family', label: 'Genogram', icon: GitBranch },
                { id: 'timeline', label: 'Timeline', icon: Clock },
              ].map(({ id, label, icon }) => (
                <button
                  key={id}
                  onClick={() => setSubjectDetailsTab(id)}
                  style={{
                    padding: '5px 12px',
                    border: `1px solid ${subjectDetailsTab === id ? theme.glossaryAccent : theme.border}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.72rem',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    background: subjectDetailsTab === id ? theme.glossaryAccent : theme.bg,
                    color: subjectDetailsTab === id ? '#fff' : theme.text,
                    transition: '0.15s',
                  }}
                >
                  <Icon icon={icon} size={13} />
                  {label}
                </button>
              ))}
              {/* Contextual help for active sub-tab */}
              {subjectDetailsTab === 'bio' && <PageHelp title="Biography" theme={theme} steps={[
                'Fill in the subject\'s vital statistics from the interview.',
                'Click "Extract from Transcript" to let AI populate fields automatically.',
                'If data exists, you\'ll see a preview to review before applying.',
                'The pronunciation field shows how the name should be spoken phonetically.',
                'Click "Save Subject Record" at the bottom to persist changes.',
              ]} />}
              {subjectDetailsTab === 'places' && <PageHelp title="Places & Map" theme={theme} steps={[
                'View all locations mentioned in the testimony on the interactive map.',
                'Click a place marker to see details and linked references.',
                'Add places manually or let AI extract them from the transcript.',
                'Autocomplete suggests Holocaust-relevant places as you type.',
              ]} />}
              {subjectDetailsTab === 'family' && <PageHelp title="Genogram" theme={theme} steps={[
                'Add family members with name, relationship, birth/death dates.',
                'Create relationships between members using the relationship form.',
                'Drag ungrouped members into family groups on the visual tree.',
                'Export as GEDCOM for genealogy software or backup as JSON.',
                'Family birth/death dates appear as pink events on the Timeline.',
              ]} />}
              {subjectDetailsTab === 'timeline' && <PageHelp title="Timeline" theme={theme} steps={[
                'Click "+ Add Event" to create events with date, title, and category.',
                'Use category pills to filter: Pre-war, Occupation, Camp, Liberation, Post-war, Family.',
                'Click an event on the left to highlight it on the visual timeline, and vice versa.',
                'Pink "Family" events are auto-generated from Genogram birth/death dates.',
                'Events with audio/video references link to specific tape timecodes.',
              ]} />}
          </div>
          {/* Re-extract button — only on family tab, pinned right */}
          {subjectDetailsTab === 'family' && subjectSeedState.lastSeededAt && !subjectSeedState.seeding && (
            <div style={{ position: 'absolute', top: '50%', right: '16px', transform: 'translateY(-50%)' }}>
              <button
                onClick={async () => {
                  if (!await confirm({
                    message: 'Clear AI-generated family members and re-extract from transcript?\n\nManually edited members will be preserved.\nRelationships will be rebuilt automatically.',
                    title: 'Clear AI-generated family?',
                    confirmLabel: 'Clear and re-extract',
                    destructive: true,
                  })) return;
                  const r = getProjectRoot(selectedVideo);
                  axios.delete(`${BACKEND_URL}/subject/family/all?root=${encodeURIComponent(r)}`)
                    .then(() => {
                      if (subjectFormRef.current?.triggerSeed) subjectFormRef.current.triggerSeed();
                    })
                    .catch(err => pushToast('family-clear-error', 'error', `Clear failed: ${err?.response?.data?.detail || err.message}`));
                }}
                style={{
                  padding: '5px 10px', borderRadius: '999px',
                  border: '1px solid #ef444444', background: '#ef44440a',
                  color: '#ef4444', cursor: 'pointer', fontSize: '0.64rem', fontWeight: 700,
                  display: 'flex', alignItems: 'center', gap: '4px',
                }}
              >
                <Icon icon={RotateCcw} size={11} />Re-extract
              </button>
            </div>
          )}
        </div>
      )}

      </div>

      {/* ── BODY ── (shifted right by the spine width, like the fixed header) */}
      <div style={{ position: 'fixed', top: `${appShellOffset}px`, left: '52px', right: 0, bottom: 0, display: 'flex', overflow: 'hidden', minHeight: 0, boxSizing: 'border-box' }}>

        {/* ═══════════════════════════════════════════════════════
            ACCESS REQUESTS PAGE (admin only)
        ════════════════════════════════════════════════════════ */}
        {appMode === 'access-requests' && (
          <AccessRequestsPage
            axiosAuth={axiosAuth}
            theme={theme}
            isDarkMode={isDarkMode}
            accessRequests={accessRequests}
            setAccessRequests={setAccessRequests}
          />
        )}

        {appMode === 'admin' && (
          <AdminDashboardPage
            axiosAuth={axiosAuth}
            currentUser={currentUser}
            theme={theme}
            isDarkMode={isDarkMode}
            BACKEND_URL={BACKEND_URL}
            videoList={videoList}
            accessRequests={accessRequests}
            onGoToAccessRequests={() => {
              setAppMode('access-requests');
              localStorage.setItem('appMode', 'access-requests');
            }}
            onOpenGlossary={() => setShowGlossary(true)}
            onOpenQR={() => { openQRDesigner(); }}
            onOpenConverter={() => {
              const w = window.open('/converter-standalone.html', 'mkv_converter', 'width=860,height=640,resizable=yes,scrollbars=no');
              if (w) {
                w.BACKEND_URL = BACKEND_URL;
                const onMsg = (e) => { if (e.source === w && e.data === 'THEME_READY') { w.postMessage({ type: 'THEME', theme }, '*'); window.removeEventListener('message', onMsg); } };
                window.addEventListener('message', onMsg);
              }
            }}
            onWorkingDirectoryChanged={handleAdminWorkingDirectoryChanged}
          />
        )}

        {/* ═══════════════════════════════════════════════════════
            HOME / DASHBOARD MODE
        ════════════════════════════════════════════════════════ */}
        {/* Project chooser modal — shown when non-admin user has multiple assignments */}
        {projectChooserList && projectChooserList.length > 0 && (
          <Modal
            open
            onClose={() => setProjectChooserList(null)}
            title="Select Project"
            theme={theme}
            width={420}
            zIndex={Z.modal}
            footer={(
              <Button theme={theme} variant="outline" fullWidth onClick={() => setProjectChooserList(null)}>
                Cancel
              </Button>
            )}
          >
              <p style={{ margin: '0 0 18px', fontSize: '0.78rem', color: theme.textSub }}>
                {currentUser?.role === 'admin'
                  ? 'Pick a project to open the editor. Star one to make it your default on next login.'
                  : 'You are assigned to multiple projects. Choose which one to work on:'}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {projectChooserList.map(proj => {
                  const isDefault = userPrefs?.default_project === proj.project_root;
                  return (
                  <div key={proj.project_root} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <button
                      onClick={() => {
                        const subdir = proj.file_subdir || '';
                        setProjectChooserList(null);
                        switchToProjectDir(subdir, proj.project_root)
                          .then(() => { setAppMode('editor'); localStorage.setItem('appMode', 'editor'); })
                          .catch(() => {});
                      }}
                      style={{ flex: 1, padding: '12px 16px', background: isDefault ? (isDarkMode ? 'rgba(250,204,21,0.08)' : 'rgba(250,204,21,0.12)') : (isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'), border: `1px solid ${isDefault ? '#facc15' : theme.border}`, borderRadius: '10px', cursor: 'pointer', textAlign: 'left', color: theme.text, fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '10px', transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}
                      onMouseLeave={e => e.currentTarget.style.background = isDefault ? (isDarkMode ? 'rgba(250,204,21,0.08)' : 'rgba(250,204,21,0.12)') : (isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)')}
                    >
                      <FolderOpen size={16} style={{ color: theme.textSub, flexShrink: 0 }} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{proj.project_root.replace(/_/g, ' ')}</div>
                        {proj.progress_pct !== undefined && (
                          <div style={{ fontSize: '0.65rem', color: theme.textSub, fontWeight: 400, marginTop: '2px' }}>{Math.round(proj.progress_pct)}% complete</div>
                        )}
                      </div>
                    </button>
                    <button
                      title={isDefault ? 'Default project' : 'Set as default'}
                      onClick={e => {
                        e.stopPropagation();
                        const newDefault = isDefault ? null : proj.project_root;
                        axiosAuth.put('/user/preferences', { default_project: newDefault }).then(() => {
                          setUserPrefs(prev => ({ ...prev, default_project: newDefault }));
                        }).catch(() => {});
                      }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', flexShrink: 0 }}
                    >
                      <Icon icon={Star} size={16} style={{ color: isDefault ? '#facc15' : theme.textSub, fill: isDefault ? '#facc15' : 'none', transition: '0.15s' }} />
                    </button>
                  </div>
                  );
                })}
              </div>
          </Modal>
        )}

        {appMode === 'home' && (
          <DashboardPanel
            axiosAuth={axiosAuth}
            currentUser={currentUser}
            theme={theme}
            isDarkMode={isDarkMode}
            workingDirLabel={(activeConfig?.active_display_path || workingDirInfo?.display_path || '').replace(/^\/+/, '')}
            onOpenDirectory={openDirectoryTab}
            defaultProject={userPrefs?.default_project}
            onSetDefaultProject={(root) => {
              axiosAuth.put('/user/preferences', { default_project: root }).then(() => {
                setUserPrefs(prev => ({ ...prev, default_project: root }));
              }).catch(() => {});
            }}
            onOpenInterview={(root, fileSubdir) => {
              const subdir = fileSubdir || '';
              switchToProjectDir(subdir, root)
                .catch(() => {})
                .finally(() => { setAppMode('editor'); localStorage.setItem('appMode', 'editor'); });
            }}
            onOpenProjectKanban={(root, fileSubdir) => {
              const subdir = fileSubdir || '';
              switchToProjectDir(subdir, root)
                .catch(() => {})
                .finally(() => {
                  setKanbanProjectRoot(root);
                  setAppMode('project-kanban');
                  localStorage.setItem('appMode', 'project-kanban');
                });
            }}
          />
        )}

        {/* ═══════════════════════════════════════════════════════
            PROJECT KANBAN MODE
        ════════════════════════════════════════════════════════ */}
        {appMode === 'project-kanban' && kanbanProjectRoot && (
          <ProjectKanbanPanel
            axiosAuth={axiosAuth}
            currentUser={currentUser}
            theme={theme}
            isDarkMode={isDarkMode}
            projectRoot={kanbanProjectRoot}
            onBack={() => { setAppMode('home'); localStorage.setItem('appMode', 'home'); }}
            onOpenEditor={(partNumber) => {
              setSelectedPart(partNumber);
              setAppMode('editor');
              localStorage.setItem('appMode', 'editor');
            }}
            onOpenQA={() => {
              setAppMode('qa');
              localStorage.setItem('appMode', 'qa');
            }}
          />
        )}

        {/* ═══════════════════════════════════════════════════════
            QR DESIGNER — FULL SCREEN
        ════════════════════════════════════════════════════════ */}
        {appMode === 'qr-designer' && (
          <QRDesignerPanel
            theme={theme}
            isDarkMode={isDarkMode}
            selectedVideo={selectedVideo}
            subjectName={(() => { const r = selectedVideo ? getProjectRoot(selectedVideo) : ''; return subjects[r]?.display_name || cleanLabel(r) || ''; })()}
            cleanLabel={cleanLabel}
            getProjectRoot={getProjectRoot}
            onBack={() => {
              const prev = prevAppModeRef.current || 'home';
              setAppMode(prev);
              localStorage.setItem('appMode', prev);
            }}
            BACKEND_URL={BACKEND_URL}
          />
        )}

        {appMode === 'merge-review' && (
          <MergeReviewPanel
            theme={theme}
            isDarkMode={isDarkMode}
            root={(() => {
              const dirRoot = (activeConfig?.active_subdir || '').split('/').filter(Boolean).pop() || '';
              const vidRoot = selectedVideo ? getProjectRoot(selectedVideo) : '';
              return subjects[dirRoot] ? dirRoot : (vidRoot || dirRoot);
            })()}
            subjectName={(() => { const r = selectedVideo ? getProjectRoot(selectedVideo) : ''; return subjects[r]?.display_name || cleanLabel(r) || ''; })()}
            onBack={() => {
              const prev = prevAppModeRef.current || 'subject';
              setAppMode(prev);
              localStorage.setItem('appMode', prev);
            }}
            BACKEND_URL={BACKEND_URL}
            pushToast={pushToast}
            axiosAuth={axiosAuth}
          />
        )}

        {appMode === 'prompt-library' && (
          <PromptLibraryPanel
            theme={theme}
            isDarkMode={isDarkMode}
            selectedVideo={selectedVideo}
            subjectName={(() => { const r = selectedVideo ? getProjectRoot(selectedVideo) : ''; return subjects[r]?.display_name || cleanLabel(r) || ''; })()}
            getProjectRoot={getProjectRoot}
            onBack={() => {
              const prev = prevAppModeRef.current || 'home';
              setAppMode(prev);
              localStorage.setItem('appMode', prev);
            }}
            BACKEND_URL={BACKEND_URL}
            pushToast={pushToast}
            axiosAuth={axiosAuth}
            logActivity={logActivity}
          />
        )}

        {/* ═══════════════════════════════════════════════════════
            QA MODE PANEL
        ════════════════════════════════════════════════════════ */}
        {appMode === 'qa' && (
          <QAPanel
            selectedVideo={selectedVideo}
            selectedPart={selectedPart}
            videoRef={qaVideoRef}
            qaFindings={qaFindings}
            qaLoading={qaLoading}
            qaError={qaError}
            qaRunning={qaRunning}
            qaFilter={qaFilter}
            setQaFilter={setQaFilter}
            runQAAnalysis={runQAAnalysis}
            runQAAllParts={runQAAllParts}
            refreshQA={refreshQA}
            theme={theme}
            BACKEND_URL={BACKEND_URL}
            activeSubdir={activeConfig?.active_subdir ?? ''}
            userRole={effectiveRole}
            availableLanguages={subjectVttLangs}
            marks={marks}
            marksLoading={marksLoading}
            formatVTTTime={formatVTTTime}
            inputStyle={inputStyle}
            sourceLanguage={subjectInterviewLang || ''}
            vttSourceLanguage={(() => { const m = (selectedVideo || '').match(/[._\- ]([a-z]{2})$/i); return m ? m[1].toLowerCase() : ''; })()}
            getProjectRoot={getProjectRoot}
            refreshMarks={refreshMarks}
            onSwitchTape={handlePartClick}
            currentUserId={currentUser?.id || currentUser?.sub ? parseInt(currentUser.id || currentUser.sub) : null}
          />
        )}

        {appMode === 'subject' && (() => {
          const root = getProjectRoot(selectedVideo);
          const sub = subjects[root] || {};
          return (
            <div style={{ flex: 1, padding: '20px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', overflow: 'hidden' }}>
              <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <SubjectEditForm
                  ref={subjectFormRef}
                  key={root}
                  root={root}
                  initial={sub}
                  pushToast={pushToast}
                  logActivity={logActivity}
                  theme={theme}
                  backendUrl={BACKEND_URL}
                  isDarkMode={isDarkMode}
                  currentUser={currentUser}
                  onSeedStateChange={setSubjectSeedState}
                  videoId={selectedVideo}
                  activeSubdir={activeConfig?.active_subdir ?? ''}
                  part={selectedPart}
                  cloudTtsAvailable={cloudTtsAvailable}
                  externalTab={subjectDetailsTab}
                  onTabChange={setSubjectDetailsTab}
                  hideInternalTabs
                  fullscreen
                  onOpenMergeReview={() => {
                    prevAppModeRef.current = appMode;
                    setAppMode('merge-review');
                    localStorage.setItem('appMode', 'merge-review');
                  }}
                  onSeekVideo={(t) => {
                    if (videoRef.current) {
                      videoRef.current.currentTime = t;
                      videoRef.current.play();
                    }
                  }}
                  onPlayClip={playVideoClipRange}
                  onOpenClips={() => {
                    setAppMode('clips');
                    localStorage.setItem('appMode', 'clips');
                    loadEditorClips();
                  }}
                  onSaved={(updated) => {
                    setSubjects(prev => ({ ...prev, [root]: updated }));
                  }}
                  onSeedStart={(r) => {
                    const id = `seed-${r}`;
                    const label = `Extract from Transcript — ${r}`;
                    pushToast(id, 'running', `Extracting from transcript — ${r}…`);
                    logActivity(id, 'seed', label, 'running');
                  }}
                  onSeedDone={(r, type, msg) => {
                    const id = `seed-${r}`;
                    const label = `Extract from Transcript — ${r}`;
                    pushToast(id, type, msg);
                    logActivity(id, 'seed', label, type,
                      type === 'error' ? { message: msg } : {});
                  }}
                />
              </div>
            </div>
          );
        })()}

        {/* ═══════════════════════════════════════════════════════
            EDITOR MODE — LEFT COLUMN
        ════════════════════════════════════════════════════════ */}
        {(appMode === 'editor' || appMode === 'clips') && (
        <div style={{ flex: appMode === 'editor' ? '1 1 0%' : 3, padding: 'clamp(12px, 1.8vw, 20px)', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', overflowY: appMode === 'clips' ? 'auto' : 'hidden', minHeight: 0 }}>

          {/* No subtitles overlay — centered modal with dimmed background */}
          {appMode === 'editor' && videoList.length === 0 && !selectedVideo && !noSubtitlesDismissed && (
            <Modal
              open
              theme={theme}
              width={480}
              zIndex={Z.modal}
              showClose={false}
              closeOnBackdrop={false}
              backdropStyle={{ background: 'rgba(0,0,0,0.55)' }}
              bodyStyle={{ padding: '32px', borderRadius: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', textAlign: 'center' }}
            >
                <div style={{
                  width: '56px', height: '56px', borderRadius: '18px',
                  background: '#7c3aed14', border: '1px solid #7c3aed33',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon icon={Mic} size={26} color="#7c3aed" />
                </div>
                <div style={{ fontSize: '1.1rem', fontWeight: 800, color: theme.text }}>
                  No subtitles found
                </div>
                <div style={{ fontSize: '0.85rem', color: theme.textSub, lineHeight: 1.6 }}>
                  Set the interview language and click <strong>Transcribe</strong> to generate subtitles from your video files.
                </div>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '5px', marginTop: '4px',
                  padding: '3px 5px 3px 8px',
                  background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '999px',
                }}>
                  <LanguageSelector
                    languages={subjectKnownLangs}
                    value={subjectInterviewLang}
                    onChange={val => setSubjectInterviewLang(val === 'off' ? '' : val)}
                    theme={theme}
                    compact
                    label="Interview Language"
                    placeholder="Select language…"
                  />
                  <button
                    onClick={() => {
                      const lang = subjectInterviewLang.trim();
                      if (!lang) return;
                      setSubjectLangSaving(true);
                      // Derive root from selected video, or from working directory path
                      const root = selectedVideo
                        ? getProjectRoot(selectedVideo)
                        : (activeConfig?.active_subdir || '').split('/').filter(Boolean).pop() || '';
                      if (root) {
                        // Dismiss only once the language is actually stored. The
                        // overlay used to close synchronously, which hid the
                        // saving state and made a failed save look successful.
                        axios.put(`${BACKEND_URL}/pipeline/language`, {
                          project_root: root, language: lang, confirmed: true,
                        }).then(() => {
                          sessionStorage.setItem('gwv_dismissedNoSubtitles', '1'); setNoSubtitlesDismissed(true);
                        }).catch(e => {
                          const msg = e?.response?.data?.detail || e.message;
                          pushToast('subject-lang-save', 'error', `Could not save the interview language: ${msg}`);
                        }).finally(() => setSubjectLangSaving(false));
                      } else {
                        // No root derivable yet — dismiss overlay, language is saved in state
                        sessionStorage.setItem('gwv_dismissedNoSubtitles', '1'); setNoSubtitlesDismissed(true);
                        setSubjectLangSaving(false);
                      }
                    }}
                    disabled={subjectLangSaving || !subjectInterviewLang?.trim()}
                    style={{
                      padding: '4px 9px', borderRadius: '999px',
                      background: subjectInterviewLang?.trim() ? '#007aff' : 'transparent',
                      color: subjectInterviewLang?.trim() ? '#fff' : theme.textSub,
                      border: `1px solid ${subjectInterviewLang?.trim() ? '#007aff' : theme.border}`,
                      fontSize: '0.64rem', fontWeight: 700,
                      cursor: subjectInterviewLang?.trim() ? 'pointer' : 'default',
                      whiteSpace: 'nowrap', flexShrink: 0,
                    }}
                  >{subjectLangSaving ? '…' : 'Set'}</button>
                </div>
                <Button
                  theme={theme}
                  variant="outline"
                  onClick={() => { sessionStorage.setItem('gwv_dismissedNoSubtitles', '1'); setNoSubtitlesDismissed(true); setAppMode('home'); localStorage.setItem('appMode', 'home'); }}
                  style={{ marginTop: '4px' }}
                >Not Now</Button>
            </Modal>
          )}

          {/* Subtitle language selector + Theater button */}
          <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {renderSubtitleSelector(subjectVttLangs, editorSubLang, setEditorSubLang)}
            <span style={{ flex: 1 }} />
            <Button
              theme={theme}
              variant="outline"
              size="sm"
              icon={Maximize2}
              onClick={() => setTheaterMode(true)}
              title="Theater mode"
            >
              Theater
            </Button>
          </div>

          {/* Video player */}
          <div style={{ width: '100%', background: '#000', borderRadius: '12px', overflow: 'hidden', height: 'clamp(280px, 38vh, 52vh)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative' }}>
            {/* Clip player modal is rendered via portal below */}
            {!isVideoLoading && selectedVideo ? (
              <video
                key={`${selectedVideo}-${activeConfig?.active_subdir ?? ''}`}
                onLoadedData={() => {
                  setIsVideoReady(true);
                  // Ensure subtitle track is showing (default attr can be unreliable)
                  if (videoRef.current) {
                    const tracks = videoRef.current.textTracks;
                    for (let i = 0; i < tracks.length; i++) {
                      tracks[i].mode = (editorSubLang !== 'off' && tracks[i].language === editorSubLang) ? 'showing' : 'hidden';
                    }
                  }
                }}
                ref={videoRef}
                onTimeUpdate={() => {
                  const t = videoRef.current.currentTime;
                  currentTime.current = t;
                  const el = document.getElementById('timecode-display');
                  if (el) el.textContent = formatVTTTime(t);
                }}
                controls
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                crossOrigin="anonymous"
              >
                {/* MP4: try multiple stem derivations so any naming convention works.
                    "Chaim David Szpigler.pt1.sv" → "Chaim David Szpigler - pt1"
                    "Carole King.en"              → "Carole King"
                    "Interview"                   → "Interview"
                */}
                {(() => {
                  const s = selectedVideo;
                  const candidates = buildMp4Candidates(s);
                  // Deduplicate while preserving order
                  return candidates.map((c, i) => (
                    <source key={i} src={encodeVideoPath(activeConfig?.active_subdir ?? '', c + '.mp4')} type="video/mp4" />
                  ));
                })()}
                {/* Subtitle track — served through backend for all languages */}
                {editorSubLang !== 'off' && (() => {
                  const parts = selectedVideo.split('.');
                  const langSuffix = parts[parts.length - 1];
                  const tok = sessionStorage.getItem('gwv_token') || '';
                  const trackUrl = editorSubLang === langSuffix
                    ? `${BACKEND_URL}/vtt/${encodeURIComponent(selectedVideo)}?part=${selectedPart}&token=${encodeURIComponent(tok)}`
                    : `${BACKEND_URL}/vtt/${encodeURIComponent(selectedVideo)}?part=${selectedPart}&lang=${editorSubLang}&token=${encodeURIComponent(tok)}`;
                  return <track key={editorSubLang} label="Subtitles" kind="subtitles" src={trackUrl} srcLang={editorSubLang} default />;
                })()}
              </video>
            ) : videoList.length === 0 && mp4List.length > 0 ? (
              /* No VTTs yet but MP4s exist — show first MP4 in player */
              <video
                key={`mp4-${selectedPart}`}
                ref={videoRef}
                controls
                style={{ width: '100%', maxHeight: '100%', borderRadius: '12px' }}
                crossOrigin="anonymous"
              >
                <source src={(() => {
                  const mp4 = mp4List.find(m => m.part === selectedPart) || mp4List[0];
                  if (!mp4) return '';
                  const subdir = activeConfig?.active_subdir ?? '';
                  const segments = [...(subdir ? subdir.split('/') : []), mp4.filename].filter(Boolean).map(s => encodeURIComponent(s));
                  // Use the configured media base, not a baked-in domain, so this follows
                  // whatever host the app is served from. VIDEO_SERVER_URL is '/media' in prod.
                  return `${VIDEO_SERVER_URL}/${segments.join('/')}`;
                })()} type="video/mp4" />
              </video>
            ) : videoList.length === 0 ? (
              <div style={{ color: '#888', textAlign: 'center', fontFamily: 'monospace', fontSize: '0.9rem', lineHeight: '1.8' }}>
                <div style={{ marginBottom: '8px' }}><Icon icon={FolderOpen} size={36} /></div>
                No video or subtitle files found.<br/>
                <span style={{ fontSize: '0.75rem', color: '#555' }}>Switch to a folder containing media files.</span>
              </div>
            ) : (
              <div style={{ color: '#fff' }}>Loading Stream...</div>
            )}
          </div>

          {/* Clip quote text — under video when trimming */}
          {trimTarget && (trimTarget.quote_text_sv || trimTarget.quote_text_en || trimTarget.quote_text) && (
            <div style={{ marginTop: '6px', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${theme.border}`, flexShrink: 0 }}>
              {(trimTarget.quote_text_sv || trimTarget.quote_text) && (
                <div style={{ padding: '8px 12px', background: theme.card, borderLeft: '3px solid #38bdf8' }}>
                  <div style={{ fontSize: '0.58rem', fontWeight: 800, color: '#38bdf8', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '3px' }}>
                    {subjectInterviewLang ? (getLanguageMeta(subjectInterviewLang)?.label || subjectInterviewLang).toUpperCase() : 'ORIGINAL'}
                  </div>
                  <div dir="auto" style={{ fontSize: '0.72rem', color: theme.text, fontStyle: 'italic', lineHeight: 1.5 }}>
                    "{trimTarget.quote_text_sv || trimTarget.quote_text}"
                  </div>
                </div>
              )}
              {trimTarget.quote_text_en && (
                <div style={{ padding: '8px 12px', background: theme.card, borderLeft: '3px solid #10b98144', borderTop: `1px solid ${theme.border}` }}>
                  <div style={{ fontSize: '0.58rem', fontWeight: 800, color: '#10b981', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '3px' }}>ENGLISH</div>
                  <div dir="auto" style={{ fontSize: '0.68rem', color: theme.textSub, fontStyle: 'italic', lineHeight: 1.5 }}>
                    "{trimTarget.quote_text_en}"
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Waveform with resizable height */}
          {!trimTarget && (
            <div style={{ position: 'relative', marginTop: 'clamp(10px, 1.2vh, 15px)', flexShrink: 0 }}>
              <div
                ref={waveformRef}
                style={{ background: theme.sidebar, borderRadius: '8px', border: `1px solid ${theme.border}`, height: `${waveformHeight}px`, overflow: 'hidden' }}
              />
              {/* Drag handle to resize waveform height */}
              <div
                style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '48px', height: '6px', cursor: 'ns-resize', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  const startY = e.clientY;
                  const startH = waveformHeight;
                  const onMove = (ev) => {
                    const newH = Math.max(60, Math.min(400, startH + ev.clientY - startY));
                    setWaveformHeight(newH);
                    localStorage.setItem('gwv_waveform_height', String(newH));
                    if (wavesurfer.current) try { wavesurfer.current.setOptions({ height: 'auto' }); } catch (_) {}
                  };
                  const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                  document.addEventListener('mousemove', onMove);
                  document.addEventListener('mouseup', onUp);
                }}
              >
                <div style={{ width: '32px', height: '3px', borderRadius: '2px', background: theme.textSub, opacity: 0.5 }} />
              </div>
            </div>
          )}

          {/* Timecode + Zoom */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'clamp(6px, 0.9vh, 10px) 0', flexShrink: 0, gap: '12px' }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '6px' }}>
              {appMode === 'editor' && (() => {
                const langUnset = subjectLangLoaded && !subjectInterviewLang && !!selectedVideo && currentUser?.role === 'admin';
                return (
                  <>
                    <div
                      title={langUnset ? 'Interview language is not set — Lang Files steps and translation will use a fallback. Pick the source language here.' : undefined}
                      style={{
                        display: 'inline-flex', alignItems: 'center',
                        padding: langUnset ? '2px 4px' : 0,
                        borderRadius: '8px',
                        border: langUnset ? '1px solid #f59e0b88' : '1px solid transparent',
                        background: langUnset ? '#f59e0b18' : 'transparent',
                        animation: langUnset ? 'pulse-amber 1.8s ease-in-out infinite' : 'none',
                      }}
                    >
                      <LanguageSelector
                        languages={subjectKnownLangs}
                        value={subjectInterviewLang}
                        onChange={val => {
                          const v = val === 'off' ? '' : val;
                          setSubjectInterviewLang(v);
                          const r = selectedVideo ? getProjectRoot(selectedVideo) : '';
                          if (r) { setSubjectLangSaving(true); axios.put(`${BACKEND_URL}/pipeline/language`, { project_root: r, language: v.trim(), confirmed: true }).catch(() => {}).finally(() => setSubjectLangSaving(false)); }
                        }}
                        theme={theme}
                        compact
                        clearable
                        label="Interview Language"
                        placeholder={langUnset ? 'Set language…' : 'Select…'}
                      />
                    </div>
                    {subjectLangSaving && <Loader size={10} style={{ animation: 'spin 1s linear infinite', color: theme.textSub }} />}
                  </>
                );
              })()}
            </div>

            <div id="timecode-display" style={{ fontFamily: 'monospace', fontSize: '1.2rem', color: '#39FF14', backgroundColor: '#000', fontWeight: 'bold', border: '1px solid #1a1a1a', padding: '6px 16px', borderRadius: '8px', boxShadow: '0 0 10px #39FF1444' }}>
              00:00:00.000
            </div>

            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '16px', justifyContent: 'flex-end' }}>
              <button
                title="Regenerate waveform"
                disabled={isRegeneratingWaveform}
                onClick={() => regenerateWaveform()}
                style={{ background: 'none', border: `1px solid ${theme.border}`, borderRadius: '6px', padding: '4px 8px', cursor: isRegeneratingWaveform ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: '4px', color: theme.textSub, fontSize: '0.7rem', fontWeight: 'bold', opacity: isRegeneratingWaveform ? 0.5 : 1 }}
              >
                <RefreshCw size={11} style={{ color: theme.textSub, ...(isRegeneratingWaveform ? { animation: 'spin 1s linear infinite' } : {}) }} />
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '0.7rem', color: theme.textSub, fontWeight: 'bold', whiteSpace: 'nowrap' }}>HEIGHT</span>
                <input type="range" min="60" max="400" value={waveformHeight} onChange={(e) => { const v = Number(e.target.value); setWaveformHeight(v); localStorage.setItem('gwv_waveform_height', String(v)); if (wavesurfer.current) try { wavesurfer.current.setOptions({ height: 'auto' }); } catch (_) {} }} style={{ width: '80px' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '0.7rem', color: theme.textSub, fontWeight: 'bold', whiteSpace: 'nowrap' }}>ZOOM</span>
                <input type="range" min="10" max="200" value={zoomLevel} onChange={(e) => setZoomLevel(Number(e.target.value))} style={{ width: '80px' }} />
              </div>
            </div>
          </div>

          {/* ── Inline Trim Card — appears directly under timecode when a clip is being trimmed ── */}
          {trimTarget && (
            <div style={{ marginTop: '4px', background: theme.sidebar, border: `1px solid ${isDarkMode ? '#1e3a5f' : '#bfdbfe'}`, borderRadius: '12px', padding: '16px', flexShrink: 0, boxShadow: isDarkMode ? '0 4px 24px rgba(56,189,248,0.08)' : '0 4px 24px rgba(56,189,248,0.12)' }}>

              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Scissors size={14} strokeWidth={2.5} style={{ color: '#38bdf8' }} />
                  <span style={{ fontWeight: 700, fontSize: '0.85rem', color: theme.text }}>Trim Clip</span>
                  <span style={{ fontSize: '0.62rem', color: theme.textSub, fontFamily: 'monospace', display: 'inline-flex', alignItems: 'center', gap: '3px' }}><Icon icon={Film} size={9} color={theme.textSub} /> Tape {trimTarget.part_number} · #{trimTarget.id}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {/* duration badge */}
                  <span style={{ fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: 800, color: '#38bdf8' }}>
                    {Math.max(0, trimEnd - trimStart).toFixed(1)}s
                  </span>
                  <button onClick={() => { clearInterval(trimPreviewRef.current); setTrimTarget(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.textSub, display: 'flex', alignItems: 'center' }}>
                    <X size={15} />
                  </button>
                </div>
              </div>

              {/* Quote reminder — Swedish (primary) + English (secondary) */}
              {(trimTarget.quote_text_sv || trimTarget.quote_text_en) && (
                <div style={{ marginBottom: '12px' }}>
                  {(trimTarget.quote_text_sv || trimTarget.quote_text) && (
                    <div style={{ padding: '7px 10px', background: theme.card, borderRadius: trimTarget.quote_text_en ? '7px 7px 0 0' : '7px', borderLeft: '3px solid #38bdf8' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '5px' }}>
                        {renderClipLanguagePill('0.6rem')}
                      </div>
                      <div dir="auto" style={{ fontSize: '0.72rem', color: theme.text, fontStyle: 'italic', lineHeight: 1.5 }}>
                        "{trimTarget.quote_text_sv || trimTarget.quote_text}"
                      </div>
                    </div>
                  )}
                  {trimTarget.quote_text_en && (
                    <div dir="auto" style={{ fontSize: '0.67rem', color: theme.textSub, fontStyle: 'italic', lineHeight: 1.45, padding: '6px 10px', background: theme.card, borderRadius: '0 0 7px 7px', borderLeft: '3px solid #38bdf840', borderTop: `1px solid ${theme.border}` }}>
                      "{trimTarget.quote_text_en}"
                    </div>
                  )}
                </div>
              )}

              {/* Waveform with IN/OUT markers */}
              <div
                onClick={e => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const pct = (e.clientX - rect.left) / rect.width;
                  const BUFFER = 2;
                  const viewStart = Math.max(0, trimOrigStartRef.current - BUFFER);
                  const viewEnd = trimOrigEndRef.current + BUFFER;
                  const seekTime = viewStart + pct * (viewEnd - viewStart);
                  if (videoRef.current) { videoRef.current.currentTime = Math.max(0, seekTime); }
                }}
                style={{ position: 'relative', marginBottom: '12px', borderRadius: '7px', overflow: 'hidden', background: isDarkMode ? '#0a0a1a' : '#eff6ff', border: `1px solid ${isDarkMode ? '#1e3a5f' : '#bfdbfe'}`, cursor: 'crosshair' }}>
                <div ref={trimWaveRef} style={{ width: '100%', height: '60px', pointerEvents: 'none' }} />
                {(() => {
                  const BUFFER   = 2;
                  const viewStart = Math.max(0, trimViewBounds.start - BUFFER);
                  const viewEnd   = trimViewBounds.end + BUFFER;
                  const viewDur   = viewEnd - viewStart;
                  const inPct  = viewDur > 0 ? Math.max(0, Math.min(100, (trimStart - viewStart) / viewDur * 100)) : 0;
                  const outPct = viewDur > 0 ? Math.max(0, Math.min(100, (trimEnd   - viewStart) / viewDur * 100)) : 100;
                  return (
                    <>
                      <div style={{ position: 'absolute', top: 0, left: 0, width: `${inPct}%`, height: '100%', background: 'rgba(0,0,0,0.45)', pointerEvents: 'none', zIndex: 3 }} />
                      <div style={{ position: 'absolute', top: 0, left: `${outPct}%`, right: 0, height: '100%', background: 'rgba(0,0,0,0.45)', pointerEvents: 'none', zIndex: 3 }} />
                      <div style={{ position: 'absolute', top: 0, left: `${inPct}%`, width: '2px', height: '100%', background: '#4ade80', pointerEvents: 'none', zIndex: 4, boxShadow: '0 0 6px #4ade80aa' }}>
                        <span style={{ position: 'absolute', top: '2px', left: '3px', fontSize: '0.5rem', fontWeight: 800, color: '#4ade80' }}>IN</span>
                      </div>
                      <div style={{ position: 'absolute', top: 0, left: `${outPct}%`, width: '2px', height: '100%', background: '#f87171', pointerEvents: 'none', zIndex: 4, boxShadow: '0 0 6px #f87171aa' }}>
                        <span style={{ position: 'absolute', top: '2px', left: '3px', fontSize: '0.5rem', fontWeight: 800, color: '#f87171' }}>OUT</span>
                      </div>
                      {/* Playhead — positioned via ref for smooth 60fps updates */}
                      <div ref={trimPlayheadRef} style={{ position: 'absolute', top: 0, left: '0%', width: '2px', height: '100%', background: '#fff', pointerEvents: 'none', zIndex: 5, display: 'none' }}>
                        <div style={{ position: 'absolute', top: '-2px', left: '-3px', width: '8px', height: '8px', background: '#fff', borderRadius: '50%', boxShadow: '0 0 4px rgba(0,0,0,0.5)' }} />
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* IN / OUT rows — side by side */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
              {[
                { label: 'IN',  val: trimStart, setVal: setTrimStart, accentColor: '#4ade80', hint: 'start' },
                { label: 'OUT', val: trimEnd,   setVal: setTrimEnd,   accentColor: '#f87171', hint: 'end'   },
              ].map(({ label, val, setVal, accentColor, hint }) => (
                <div key={label} style={{ flex: 1, padding: '10px 12px', background: theme.card, borderRadius: '8px', border: `1px solid ${theme.border}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '0.58rem', fontWeight: 800, color: accentColor, background: `${accentColor}22`, padding: '2px 6px', borderRadius: '5px' }}>{label}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.88rem', fontWeight: 700, color: accentColor, flex: 1 }}>{formatVTTTime(val)}</span>
                    <button
                      onClick={() => setTrimPointFromPlayhead(hint)}
                      title={`Set ${hint} point to current position`}
                      style={{ padding: '3px 8px', background: accentColor, color: '#000', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.6rem', fontWeight: 800, whiteSpace: 'nowrap' }}
                    >Set here</button>
                  </div>
                  <div style={{ display: 'flex', gap: '3px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {[[-5,'−5s'],[-1,'−1s'],[-0.5,'−½s'],[-0.1,'−.1'],[0.1,'+.1'],[0.5,'+½s'],[1,'+1s'],[5,'+5s']].map(([d, lbl]) => (
                      <button key={lbl} onClick={() => setVal(p => Math.max(0, parseFloat((p + d).toFixed(3))))}
                        style={{ padding: '2px 5px', background: Math.abs(d) === 0.1 ? `${accentColor}18` : theme.inputBg, color: Math.abs(d) === 0.1 ? accentColor : theme.text, border: `1px solid ${Math.abs(d) === 0.1 ? `${accentColor}44` : theme.border}`, borderRadius: '4px', cursor: 'pointer', fontSize: '0.58rem', fontWeight: 600 }}
                      >{lbl}</button>
                    ))}
                    <button
                      onClick={() => { if (videoRef.current) { videoRef.current.currentTime = val; videoRef.current.pause(); } }}
                      style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '2px', padding: '2px 6px', background: theme.inputBg, color: theme.textSub, border: `1px solid ${theme.border}`, borderRadius: '4px', cursor: 'pointer', fontSize: '0.58rem' }}
                    ><Play size={8} /> Seek</button>
                  </div>
                </div>
              ))}
              </div>

              {/* Action row */}
              <div style={{ display: 'flex', gap: '7px' }}>
                <Button
                  theme={theme}
                  variant="secondary"
                  size="sm"
                  icon={Play}
                  onClick={() => {
                    if (!videoRef.current) return;
                    clearInterval(trimPreviewRef.current);
                    videoRef.current.currentTime = trimStart;
                    videoRef.current.play();
                    trimPreviewRef.current = setInterval(() => {
                      if (videoRef.current && videoRef.current.currentTime >= trimEnd) {
                        videoRef.current.pause();
                        videoRef.current.currentTime = trimStart;
                        clearInterval(trimPreviewRef.current);
                      }
                    }, 100);
                  }}
                  style={{ flex: 1 }}
                > Preview</Button>

                {/* Split at current playhead — first half stays, second goes to Essential Clips queue */}
                <button
                  disabled={splitSaving}
                  title="Split clip at current playhead position. First half stays here; second half is added to the Non-Negotiable assembly queue."
                  onClick={() => {
                    if (!videoRef.current || !trimTarget) return;
                    const at = videoRef.current.currentTime;
                    if (at <= trimTarget.start_time || at >= trimTarget.end_time) {
                      pushToast('clip-split-playhead', 'error', `Playhead must be inside the clip (${formatVTTTime(trimTarget.start_time)} – ${formatVTTTime(trimTarget.end_time)})`);
                      return;
                    }
                    setSplitSaving(true);
                    clearInterval(trimPreviewRef.current);
                    axios.post(`${BACKEND_URL}/clips/${trimTarget.id}/split`, { at })
                      .then(() => { setSplitSaving(false); setTrimTarget(null); loadEditorClips(); regenerateWaveform(true); })
                      .catch(e => { setSplitSaving(false); pushToast('clip-split-error', 'error', e?.response?.data?.detail || 'Split failed'); });
                  }}
                  style={{ flex: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '7px', background: splitSaving ? theme.card : '#f59e0b', color: splitSaving ? theme.textSub : '#000', border: 'none', borderRadius: '7px', cursor: splitSaving ? 'not-allowed' : 'pointer', fontWeight: 800, fontSize: '0.7rem' }}
                >
                  <GitFork size={11} /> {splitSaving ? 'Splitting…' : 'Split here'}
                </button>

                <Button
                  theme={theme}
                  variant="secondary"
                  size="sm"
                  onClick={() => { clearInterval(trimPreviewRef.current); setTrimTarget(null); }}
                  style={{ flex: 0.8 }}
                >Cancel</Button>
                <button
                  disabled={trimSaving || trimEnd <= trimStart}
                  onClick={() => {
                    if (trimEnd <= trimStart) return;
                    setTrimSaving(true);
                    clearInterval(trimPreviewRef.current);
                    axios.put(`${BACKEND_URL}/clips/${trimTarget.id}/trim`, { start_time: trimStart, end_time: trimEnd })
                      .then(r => { setTrimSaving(false); updateClipInPlace(r.data); clearInterval(trimPreviewRef.current); setTrimTarget(null); regenerateWaveform(true); })
                      .catch(e => { setTrimSaving(false); pushToast('clip-trim-error', 'error', e?.response?.data?.detail || 'Trim failed'); });
                  }}
                  style={{ flex: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '7px', background: trimSaving || trimEnd <= trimStart ? theme.card : '#38bdf8', color: trimSaving || trimEnd <= trimStart ? theme.textSub : '#000', border: 'none', borderRadius: '7px', cursor: trimEnd <= trimStart ? 'not-allowed' : 'pointer', fontWeight: 800, fontSize: '0.7rem' }}
                ><Save size={11} /> {trimSaving ? 'Saving…' : 'Save Trim'}</button>
              </div>

            </div>
          )}

          {/* Mark Error + Mark Quote buttons — editor mode only */}
          {appMode === 'editor' && (
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            {/* Migrated to the Button primitive — variant="accent" keeps the
                label legible on every scheme with no per-site contrast code. */}
            <Button
              theme={theme}
              variant="accent"
              onClick={markError}
              icon={Film}
              style={{ flex: 1, padding: 'clamp(10px, 1.4vh, 14px)', fontSize: theme.fsButton, letterSpacing: '0.06em' }}
            >
              {t('editor.markError').toUpperCase()} (Shift+E)
            </Button>
            <button
              onClick={markQuote}
              style={{ flex: 1, padding: 'clamp(10px, 1.4vh, 14px)', background: '#f472b6', color: '#000', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: theme.fsButton, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}
            >
              <span style={{ letterSpacing: '0.06em', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><MessageSquare size={11} strokeWidth={2.5} /> {t('editor.markQuote').toUpperCase()} (Shift+Q)</span>
            </button>
          </div>
          )}

          {/* Part dashboard — editor mode only, includes MP4-only tapes */}
          {appMode === 'editor' && (() => {
            // Merge VTT-based parts with MP4-only parts
            const vttParts = projectStats.breakdown;
            const vttPartNums = new Set(vttParts.map(b => b.part));
            const mp4OnlyParts = mp4List
              .filter(m => !vttPartNums.has(m.part))
              .map(m => ({ part: m.part, mp4: m, total: 0, done: 0, pending: 0, tapeLabel: null, videoId: null }));
            const allParts = [...vttParts, ...mp4OnlyParts].sort((a, b) => a.part - b.part);
            if (allParts.length === 0) return null;

            return (
            <div style={{ marginTop: 'clamp(12px, 1.4vh, 20px)', flexShrink: 0 }}>
              <div style={{ display: 'flex', gap: 'clamp(8px, 1vw, 12px)', flexWrap: 'wrap' }}>
                {allParts.map(b => {
                  const isActive = selectedPart === b.part;
                  const mp4 = b.mp4 || mp4List.find(m => m.part === b.part);
                  const hasVtt = vttPartNums.has(b.part);
                  const isTranscribing = !!transcribingParts[b.part];
                  const tapeAllowed = !allowedParts || allowedParts.includes(b.part);
                  const tapeRanges = taskAssignments.filter(a => a.part_number === null || a.part_number === b.part);
                  const hasPartialRange = tapeAllowed && tapeRanges.length > 0 && tapeRanges.some(a => a.tc_start !== null || a.tc_end !== null);

                  return (
                    <div
                      key={b.part}
                      onClick={() => {
                        if (!tapeAllowed) return;
                        if (hasVtt) { handlePartClick(b.part); }
                        else { setSelectedPart(b.part); }
                      }}
                      title={!tapeAllowed ? 'Not assigned to this tape' : hasPartialRange ? `Partial range assigned` : ''}
                      style={{
                        flex: 1, minWidth: '140px', cursor: tapeAllowed ? 'pointer' : 'not-allowed', transition: '0.2s',
                        background: isActive ? (isDarkMode ? '#1a1a1a' : '#ffffff') : theme.card,
                        borderRadius: '12px',
                        border: isActive ? `2px solid ${theme.activeBorder}` : `1px solid ${hasVtt ? theme.border : '#7c3aed33'}`,
                        padding: 'clamp(10px, 1.1vh, 14px) clamp(8px, 0.8vw, 10px) clamp(8px, 0.9vh, 12px)',
                        boxShadow: 'none',
                        opacity: tapeAllowed ? 1 : 0.35,
                      }}
                    >
                      <div style={{ fontFamily: 'monospace', fontSize: theme.fsPartLabel, color: isActive ? theme.text : theme.textSub, fontWeight: 'bold', letterSpacing: '0.06em', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
                        {!tapeAllowed && <Lock size={9} style={{ color: '#94a3b8' }} />}
                        <Icon icon={Film} size={9} color={isActive ? theme.text : theme.textSub} /> Tape {b.part}
                      </div>
                      {b.tapeLabel && (
                        <div style={{ fontSize: theme.fsPartName, color: theme.textSub, textAlign: 'center', marginTop: '2px', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {b.tapeLabel}
                        </div>
                      )}
                      {hasPartialRange && (
                        <div style={{ fontSize: '0.55rem', fontWeight: 600, color: theme.accent || '#3b82f6', textAlign: 'center', marginTop: '2px', marginBottom: '2px' }}>
                          Partial
                        </div>
                      )}
                      <div style={{ height: '1px', background: theme.border, margin: '0 5%', marginBottom: '8px' }} />

                      {hasVtt ? (
                        /* VTT exists — show mark stats + admin reset button */
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                          <div style={{ fontFamily: 'monospace', fontSize: theme.fsPartStats, fontWeight: 'bold', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0' }}>
                            <span style={{ color: b.pending > 0 ? theme.accent : theme.textSub }}>{b.pending} PENDING</span>
                            <span style={{ color: theme.border, margin: '0 8px' }}>|</span>
                            <span style={{ color: theme.textSub }}>{b.done} DONE</span>
                          </div>
                          {currentUser?.role === 'admin' && (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                const label = mp4?.stem || `Tape ${b.part}`;
                                if (!await confirm({
                                  message:
                                    `Reset transcription for ${label}?\n\n` +
                                    `This permanently deletes:\n` +
                                    `  • All VTT, SRT, TXT, JSON files for this tape\n` +
                                    `  • All timestamped backups in VTT_VERSIONS/\n` +
                                    `  • All ${b.pending + b.done} correction marks in the database\n\n` +
                                    `This cannot be undone.`,
                                  title: `Reset transcription for ${label}?`,
                                  confirmLabel: 'Reset',
                                  destructive: true,
                                })) return;
                                const svId = mp4?.stem || '_scan_';
                                axiosAuth.delete(`/transcribe/${encodeURIComponent(svId)}?part=${b.part}`)
                                  .then(res => {
                                    const d = res.data || {};
                                    pushToast(`reset-${b.part}`, 'success',
                                      `${label} reset — ${d.removed_files?.length || 0} files, ${d.removed_versions || 0} versions, ${d.deleted_marks || 0} marks`);
                                    refreshVideos();
                                    refreshMp4s();
                                  })
                                  .catch(err => {
                                    pushToast(`reset-${b.part}`, 'error',
                                      `Reset failed: ${err.response?.data?.detail || err.message}`);
                                  });
                              }}
                              title="Super admin: delete all transcription data for this tape"
                              style={{
                                padding: '2px 8px', borderRadius: '4px', fontSize: '0.55rem', fontWeight: 600,
                                background: 'transparent',
                                color: '#ef4444',
                                border: `1px solid rgba(239,68,68,0.35)`,
                                cursor: 'pointer',
                                display: 'inline-flex', alignItems: 'center', gap: '3px',
                              }}
                            >
                              <Icon icon={Trash2} size={9} /> Reset
                            </button>
                          )}
                        </div>
                      ) : (
                        /* MP4 only — show transcribe button */
                        <div style={{ textAlign: 'center' }}>
                          {mp4 && (
                            <div style={{ fontSize: '0.62rem', color: theme.textSub, marginBottom: '6px' }}>
                              {mp4.size_mb > 0 ? `${mp4.size_mb} MB` : 'MP4'}
                            </div>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); if (mp4) transcribePart(mp4, b.part); }}
                            disabled={isTranscribing || !subjectInterviewLang?.trim()}
                            style={{
                              padding: '4px 12px', borderRadius: '6px', fontSize: '0.65rem', fontWeight: 700,
                              background: isTranscribing ? theme.card : '#7c3aed',
                              color: isTranscribing ? theme.textSub : '#fff',
                              border: `1px solid ${isTranscribing ? theme.border : '#7c3aed'}`,
                              cursor: isTranscribing ? 'not-allowed' : 'pointer',
                              display: 'inline-flex', alignItems: 'center', gap: '4px',
                            }}
                          >
                            {isTranscribing
                              ? <><Icon icon={Loader} size={10} style={{ animation: 'spin 1s linear infinite' }} />Transcribing…</>
                              : <><Icon icon={Mic} size={10} />Transcribe</>
                            }
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            );
          })()}
        </div>
        )} {/* end editor/clips left column */}

        {/* ── RIGHT COLUMN — memoised, does not re-render on timecode/waveform changes ── */}
        {appMode === 'editor' && (
        <RightColumn
          marks={marks}
          marksLoading={marksLoading}
          selectedVideo={selectedVideo}
          selectedPart={selectedPart}
          availableLanguages={workingDirInfo?.available_languages || []}
          preferredEditingLanguage={subjectInterviewLang}
          sourceLanguage={subjectInterviewLang || ''}
          vttSourceLanguage={(() => { const m = (selectedVideo || '').match(/[._\- ]([a-z]{2})$/i); return m ? m[1].toLowerCase() : ''; })()}
          theme={theme}
          inputStyle={inputStyle}
          formatVTTTime={formatVTTTime}
          BACKEND_URL={BACKEND_URL}
          videoRef={videoRef}
          onRefresh={refreshMarks}
          onCompleteAllPart={completeAllCurrentPart}
          getProjectRoot={getProjectRoot}
          cleanLabel={cleanLabel}
          userRole={effectiveRole}
          allowedParts={allowedParts}
          taskAssignments={taskAssignments}
          currentUserId={currentUser?.id || currentUser?.sub ? parseInt(currentUser.id || currentUser.sub) : null}
          pushToast={pushToast}
          glossary={glossary}
          glossarySearch={glossarySearch}
          setGlossarySearch={setGlossarySearch}
          processedGlossary={processedGlossary}
          refreshGlossary={refreshGlossary}
          refreshMarks={refreshMarks}
          sortConfig={sortConfig}
          setSortConfig={setSortConfig}
          t={t}
          onSwitchTape={handlePartClick}
          vttVersions={vttVersions}
          reportLangs={reportLangs}
          subjectVttLangs={subjectVttLangs}
          translatingLangs={translatingLangs}
          rebuildingTranslations={rebuildingTranslations}
          onRebuildAllTranslations={async (targets) => {
            if (rebuildingTranslations) return;
            const r = selectedVideo ? getProjectRoot(selectedVideo) : (activeConfig?.active_subdir || '').split('/').filter(Boolean).pop() || '';
            if (!r || !targets?.length) return;
            const labelLookup = {ar:'Arabic',bg:'Bulgarian',hr:'Croatian',cs:'Czech',da:'Danish',nl:'Dutch',en:'English',et:'Estonian',fi:'Finnish',fr:'French',de:'German',el:'Greek',he:'Hebrew',hu:'Hungarian',it:'Italian',lv:'Latvian',lt:'Lithuanian',no:'Norwegian',pl:'Polish',pt:'Portuguese',ro:'Romanian',ru:'Russian',sr:'Serbian',sk:'Slovak',sl:'Slovenian',es:'Spanish',sv:'Swedish',tr:'Turkish',uk:'Ukrainian',yi:'Yiddish'};
            // Pivot pipeline: interview language → English → all other targets.
            // The interview language is the source-of-truth VTT. English is rebuilt first
            // (from the interview language), then every remaining target is rebuilt
            // from the fresh English VTT instead of the interview language.
            const interviewLang = (subjectInterviewLang || 'en').toLowerCase();
            const orderedTargets = [];
            // English must be first if interview language isn't already English —
            // every subsequent language pivots through it. Add it even if it wasn't
            // in the existing targets list (no English VTT yet) so the pivot works.
            if (interviewLang !== 'en') orderedTargets.push('en');
            for (const c of targets) {
              if (c === 'en') continue; // already pushed above (or is source)
              if (c === interviewLang) continue;
              orderedTargets.push(c);
            }
            if (!orderedTargets.length) return;
            const names = orderedTargets.map(c => labelLookup[c] || c.toUpperCase()).join(' → ');
            const pivotNote = interviewLang !== 'en'
              ? `\nPivot: ${labelLookup[interviewLang] || interviewLang.toUpperCase()} → English → others.`
              : '';
            if (!await confirm({
              message: `Rebuild all ${orderedTargets.length} translation${orderedTargets.length !== 1 ? 's' : ''} for this project?\n\n${names}${pivotNote}\n\nOld files back up to VTT_VERSIONS/. Each language calls the Gemini API — this can be slow and costs tokens.`,
              title: 'Rebuild translations?',
              confirmLabel: 'Rebuild',
              destructive: true,
            })) return;
            setRebuildingTranslations(true);
            const summary = { ok: [], failed: [] };
            for (const code of orderedTargets) {
              const label = labelLookup[code] || code.toUpperCase();
              // Source for this step: English is always rebuilt from interview language;
              // everything else is rebuilt from English (after English is refreshed).
              const sourceCode = (code === 'en') ? interviewLang : 'en';
              const sourceLabel = labelLookup[sourceCode] || sourceCode.toUpperCase();
              try {
                const res = await axios.post(`${BACKEND_URL}/translate/${encodeURIComponent(r)}?target=${code}&source=${sourceCode}`);
                const jid = res.data.job_id;
                pushToast(jid, 'running', `Rebuilding ${label} (from ${sourceLabel}) — ${res.data.source_files || 0} tape(s), ${res.data.total_cues || 0} cues`);
                logActivity(jid, 'translate', `Rebuild — ${label} (from ${sourceLabel})`, 'running');
                // Poll to completion before moving on
                await new Promise(resolve => {
                  const poll = setInterval(() => {
                    axios.get(`${BACKEND_URL}/jobs/${jid}`).then(jr => {
                      if (jr.data.status === 'complete') {
                        clearInterval(poll);
                        pushToast(jid, 'success', `${label} rebuilt — ${jr.data.result?.files_written?.length || 0} files`);
                        logActivity(jid, 'translate', `Rebuild — ${label}`, 'success');
                        summary.ok.push(label);
                        resolve();
                      } else if (jr.data.status === 'failed') {
                        clearInterval(poll);
                        pushToast(jid, 'error', `${label} rebuild failed: ${jr.data.error || 'Unknown'}`);
                        logActivity(jid, 'translate', `Rebuild — ${label}`, 'error', { message: jr.data.error });
                        summary.failed.push(label);
                        resolve();
                      }
                    }).catch(() => {});
                  }, 3000);
                });
                // If English just finished and there are more targets, stop the loop
                // if English failed — the rest would pivot through a broken English VTT.
                if (code === 'en' && summary.failed.includes(label)) {
                  pushToast(`rebuild-abort-${Date.now()}`, 'error', 'English rebuild failed — aborting remaining languages (they pivot through English).');
                  break;
                }
              } catch (err) {
                const detail = err?.response?.data?.detail || err.message;
                pushToast(`rebuild-err-${Date.now()}`, 'error', `${label} failed: ${detail}`);
                summary.failed.push(label);
                if (code === 'en') {
                  pushToast(`rebuild-abort-${Date.now()}`, 'error', 'English rebuild failed — aborting remaining languages (they pivot through English).');
                  break;
                }
              }
            }
            setRebuildingTranslations(false);
            refreshVideos();
            const ok = summary.ok.length, bad = summary.failed.length;
            pushToast(`rebuild-done-${Date.now()}`, bad ? 'error' : 'success',
              `Rebuild complete — ${ok} succeeded${bad ? `, ${bad} failed (${summary.failed.join(', ')})` : ''}`);
          }}
          onTranslateLanguage={async (langCode, langLabel) => {
            if (translatingLangs.has(langCode)) return;
            const r = selectedVideo ? getProjectRoot(selectedVideo) : (activeConfig?.active_subdir || '').split('/').filter(Boolean).pop() || '';
            if (!r) return;
            if (!await confirm({
              message: `Translate VTTs to ${langLabel}?\nThis will call the Gemini API.`,
              title: `Translate to ${langLabel}?`,
              confirmLabel: 'Translate',
              destructive: false,
            })) return;
            setTranslatingLangs(prev => new Set([...prev, langCode]));
            const actLabel = `Translate — ${langLabel}`;
            axios.post(`${BACKEND_URL}/translate/${encodeURIComponent(r)}?target=${langCode}`)
              .then(res => {
                const jid = res.data.job_id;
                const nFiles = res.data.source_files || 0;
                const nCues = res.data.total_cues || 0;
                pushToast(jid, 'running', `Translating to ${langLabel} — ${nFiles} tape${nFiles !== 1 ? 's' : ''}, ${nCues} subtitle blocks via Gemini`);
                logActivity(jid, 'translate', actLabel, 'running');
                const poll = setInterval(() => {
                  axios.get(`${BACKEND_URL}/jobs/${jid}`).then(jr => {
                    if (jr.data.status === 'complete') {
                      clearInterval(poll);
                      setTranslatingLangs(prev => { const n = new Set(prev); n.delete(langCode); return n; });
                      pushToast(jid, 'success', `Translation to ${langLabel} complete — ${jr.data.result?.files_written?.length || 0} files`);
                      logActivity(jid, 'translate', actLabel, 'success');
                      refreshVideos();
                      setSubjectVttLangs(prev => [...new Set([...prev, langCode])].sort());
                    } else if (jr.data.status === 'failed') {
                      clearInterval(poll);
                      setTranslatingLangs(prev => { const n = new Set(prev); n.delete(langCode); return n; });
                      pushToast(jid, 'error', `Translation failed: ${jr.data.error || 'Unknown'}`);
                      logActivity(jid, 'translate', actLabel, 'error', { message: jr.data.error });
                    }
                  }).catch(() => {});
                }, 3000);
              })
              .catch(err => {
                setTranslatingLangs(prev => { const n = new Set(prev); n.delete(langCode); return n; });
                const detail = err?.response?.data?.detail || err.message;
                pushToast(`translate-err-${Date.now()}`, 'error', `Translation failed: ${detail}`);
                logActivity(`translate-err-${Date.now()}`, 'translate', actLabel, 'error', { message: detail });
              });
          }}
          onDownloadAllParts={() => {
            axiosAuth.get(`/download-project/${encodeURIComponent(selectedVideo)}`, { responseType: 'blob' })
              .then(res => { const url = URL.createObjectURL(res.data); const a = document.createElement('a'); a.href = url; a.download = `${getProjectRoot(selectedVideo)}_all_parts_${_ts()}.zip`; a.click(); URL.revokeObjectURL(url); })
              .catch(err => pushToast('dl-err', 'error', `Download failed: ${err?.response?.status || err.message}`));
          }}
          onWriteCorrected={async (lang) => {
            const r = getProjectRoot(selectedVideo);
            if (!r) return;
            try {
              const check = await axios.get(`${BACKEND_URL}/translate/review-status/${encodeURIComponent(r)}`);
              const { unreviewed, total_edits, reviewed } = check.data;
              if (unreviewed > 0 && !await confirm({
                message: `${total_edits} corrections. ${reviewed} reviewed, ${unreviewed} pending.\n\nProceed?`,
                title: 'Unreviewed corrections',
                confirmLabel: 'Proceed',
                destructive: false,
              })) return;
            } catch {}
            if (!await confirm({
              message: `Write corrected VTTs?\n\nOriginals backed up to VTT_BACKUP/`,
              title: 'Write corrected VTTs?',
              confirmLabel: 'Write',
              destructive: true,
            })) return;
            axios.post(`${BACKEND_URL}/translate/write-corrected/${encodeURIComponent(r)}?lang=${lang || reportLangs.join(',') || 'all'}&force=true`)
              .then(res => { const d = res.data; pushToast('wc-ok', 'success', `Wrote ${d.files_written?.length || 0} corrected VTT files`); refreshVideos(); })
              .catch(err => pushToast('wc-err', 'error', `Write failed: ${err?.response?.data?.detail || err.message}`));
          }}
          onRefreshAllVTT={async () => {
            const r = getProjectRoot(selectedVideo);
            if (!r) return;
            if (!await confirm({
              message: `Refresh ALL VTT files?\n\nOriginals backed up to VTT_BACKUP/`,
              title: 'Refresh all VTT files?',
              confirmLabel: 'Refresh',
              destructive: true,
            })) return;
            axios.post(`${BACKEND_URL}/translate/write-corrected/${encodeURIComponent(r)}?lang=all&force=true&all_files=true`)
              .then(res => { pushToast('wc-all', 'success', `Refreshed ${res.data.files_written?.length || 0} VTT files`); refreshVideos(); })
              .catch(err => pushToast('wc-err', 'error', `Refresh failed: ${err?.response?.data?.detail || err.message}`));
          }}
          onRestoreVersion={async (v) => {
            const r = getProjectRoot(selectedVideo);
            if (!r) return;
            if (!await confirm({
              message: `Restore ${v.language_label} from ${v.timestamp}?`,
              title: 'Restore version?',
              confirmLabel: 'Restore',
              destructive: false,
            })) return;
            axios.post(`${BACKEND_URL}/translate/versions/${encodeURIComponent(r)}/restore?filename=${encodeURIComponent(v.filename)}`)
              .then(() => { pushToast(`vtt-r-${Date.now()}`, 'success', `Restored ${v.language_label}`); refreshVideos(); axios.get(`${BACKEND_URL}/translate/versions/${encodeURIComponent(r)}`).then(vr => setVttVersions(vr.data || [])).catch(() => {}); })
              .catch(err => pushToast(`vtt-r-err`, 'error', `Restore failed: ${err?.response?.data?.detail || err.message}`));
          }}
        />
        )}
        {/* ── CLIPS RIGHT PANEL ── */}
        {appMode === 'clips' && (() => {
          const root = getProjectRoot(selectedVideo);
          const introClips = editorClips.filter(c => !c.use_for_pronunciation);
          const voiceClips = editorClips.filter(c => c.use_for_pronunciation);
          const clipPool = editorClipsView === 'voice' ? voiceClips
            : editorClipsView === 'essentials' ? introClips.filter(c => c.is_essential)
            : editorClipsView === 'quotes' ? introClips.filter(c => c.category === 'memorable_quote')
            : editorClipsView === 'all' ? introClips
            : introClips.filter(c => !c.is_essential);
          const allParts = (() => {
            const pts = videoList.filter(v => getProjectRoot(v) === root).map(v => getPartNumber(v)).sort((a,b)=>a-b);
            return pts.length > 0 ? pts : [1];
          })();
          const filtered = clipPool.filter(c =>
            editorClipsFilter === 'all' ? true
            : editorClipsFilter === 'assembly' ? c.assembly_order != null
            : c.status === editorClipsFilter
          );
          // Group by part
          const byPart = allParts.map(p => ({
            part: p,
            clips: filtered.filter(c => c.part_number === p),
          })).filter(g => g.clips.length > 0);

          const actClip = (id, action, notes) => {
            const url = `${BACKEND_URL}/clips/${id}/${action}`;
            const req = notes !== undefined
              ? axios.put(url, { flag_notes: notes })
              : axios.put(url);
            req.then(r => updateClipInPlace(r.data)).catch(() => {});
          };

          return (<>
            {/* Horizontal resizer handle */}
            <div
              style={{ width: '6px', cursor: 'ew-resize', background: 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 5 }}
              onMouseDown={(e) => {
                e.preventDefault();
                const startX = e.clientX;
                const startW = clipsSidebarWidth || Math.min(450, Math.max(340, window.innerWidth * 0.32));
                document.body.style.cursor = 'ew-resize';
                document.body.style.userSelect = 'none';
                const onMove = (ev) => {
                  const newW = Math.max(280, Math.min(700, startW - (ev.clientX - startX)));
                  setClipsSidebarWidth(newW);
                  localStorage.setItem('gwv_clips_sidebar_width', String(newW));
                };
                const onUp = () => {
                  document.body.style.cursor = '';
                  document.body.style.userSelect = '';
                  document.removeEventListener('mousemove', onMove);
                  document.removeEventListener('mouseup', onUp);
                };
                document.addEventListener('mousemove', onMove);
                document.addEventListener('mouseup', onUp);
              }}
            >
              <div style={{ width: '3px', height: '36px', borderRadius: '2px', background: theme.textSub, opacity: 0.3 }} />
            </div>
            <div style={{ width: clipsSidebarWidth > 0 ? `${clipsSidebarWidth}px` : 'clamp(340px, 32vw, 450px)', background: theme.sidebar, borderLeft: `1px solid ${theme.border}`, display: 'flex', flexDirection: 'column', flexShrink: 0, overflow: 'hidden' }}>

              {/* Header */}
              <div style={{ padding: '12px 16px 10px', borderBottom: `1px solid ${theme.border}`, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>

                {/* ROW 1 — Title + help toggle */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon={Scissors} size={14} style={{ color: theme.glossaryAccent }} />
                  <span style={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.06em', color: theme.text }}>CLIP REVIEW</span>
                  <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '0.9rem', fontWeight: 400, fontStyle: 'italic', color: theme.textSub, letterSpacing: '0.03em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                    {subjects[root]?.display_name || cleanLabel(root)}
                  </span>
                  <button onClick={() => setShowClipsHelp(v => !v)} title="Clip Card Guide"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: showClipsHelp ? theme.glossaryAccent : theme.textSub, display: 'flex', padding: 0, flexShrink: 0 }}>
                    <HelpCircle size={13} strokeWidth={2.5} />
                  </button>
                </div>

                {/* Subtitle selector for clip review */}
                {renderSubtitleSelector(subjectVttLangs, editorSubLang, setEditorSubLang, { compact: true })}

                {/* Category & filter pills moved into Review Filters box below */}

                {/* ROW 2 — Essential Clips */}
                {editorClipsView === 'essentials' && (() => {
                  const hasEssentials = editorClips.filter(c => c.is_essential && !c.use_for_pronunciation).length;
                  return (
                <div style={{ borderRadius: '8px', background: isDarkMode ? '#1a1000' : '#fffbeb', border: `1px solid ${isDarkMode ? '#5a3a00' : '#fcd34d'}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px' }}>
                    <Pin size={11} strokeWidth={2.5} style={{ color: '#f59e0b', flexShrink: 0 }} />
                    <span style={{ fontSize: '0.63rem', color: isDarkMode ? '#fcd34d' : '#92400e', flex: 1, fontWeight: 600 }}>
                      10 Essential Clips — all parts
                    </span>
                    {/* ? help toggle */}
                    <button
                      onClick={() => setShowNonNegHelp(v => !v)}
                      title="View the 10 essential clip questions"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: showNonNegHelp ? '#f59e0b' : (isDarkMode ? '#a16207' : '#d97706'), padding: '0 2px', display: 'flex', alignItems: 'center' }}
                    ><HelpCircle size={13} strokeWidth={2} /></button>
                    <button
                      disabled={essentialsGenerating || clipsGenerating}
                      onClick={async () => {
                        const projectBase = getProjectRoot(selectedVideo);
                        if (hasEssentials > 0) {
                          if (!await confirm({
                            message: `${hasEssentials} essential clip(s) already exist.\n\nRe-generating will delete and replace them.\nVoice clips will be preserved.\n\nContinue?`,
                            title: 'Re-generate essential clips?',
                            confirmLabel: 'Re-generate',
                            destructive: true,
                          })) return;
                        }
                        const id = `essentials-${projectBase}`;
                        setEssentialsGenerating(true);
                        pushToast(id, 'running', `Generating 10 essential clips — ${projectBase}…`);
                        logActivity(id, 'clips', `Non-negotiable extraction — ${projectBase}`, 'running');
                        axios.post(`${BACKEND_URL}/clips/generate-essentials/${encodeURIComponent(projectBase)}?clear_existing=true`)
                          .then(r => {
                            const n = r.data.generated ?? 0;
                            setEssentialsGenerating(false);
                            pushToast(id, 'success', `✓ ${n} essential clip${n !== 1 ? 's' : ''} found — ${projectBase}`);
                            logActivity(id, 'clips', `Non-negotiable extraction — ${projectBase} · ${n} found`, 'success');
                            loadEditorClips();
                          })
                          .catch(e => {
                            const msg = e?.response?.data?.detail || e.message;
                            setEssentialsGenerating(false);
                            pushToast(id, 'error', `Non-negotiable generation failed: ${msg}`);
                            logActivity(id, 'clips', `Non-negotiable extraction — ${projectBase}`, 'error', { message: msg });
                          });
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '4px',
                        padding: '4px 10px',
                        background: essentialsGenerating ? (isDarkMode ? '#333' : '#e5e5e7') : '#f59e0b',
                        color: essentialsGenerating ? theme.textSub : '#fff',
                        border: 'none', borderRadius: '6px',
                        cursor: (essentialsGenerating || clipsGenerating) ? 'not-allowed' : 'pointer',
                        fontWeight: 700, fontSize: '0.63rem', flexShrink: 0,
                        opacity: clipsGenerating && !essentialsGenerating ? 0.5 : 1,
                      }}
                    >
                      <Icon icon={essentialsGenerating ? Loader : (hasEssentials > 0 ? RefreshCw : Pin)} size={10} style={essentialsGenerating ? { animation: 'spin 1s linear infinite' } : {}} />
                      {essentialsGenerating ? 'Searching…' : (hasEssentials > 0 ? 'Re-Generate' : 'Generate')}
                    </button>
                  </div>

                  {/* Inline help — the 10 non-negotiable questions */}
                  {showNonNegHelp && (
                    <div style={{ borderTop: `1px solid ${isDarkMode ? '#5a3a00' : '#fcd34d'}`, padding: '10px 12px 12px' }}>
                      <div style={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.08em', color: isDarkMode ? '#a16207' : '#92400e', marginBottom: '8px' }}>
                        USC SHOAH FOUNDATION · BIOGRAPHICAL ESSENTIALS
                      </div>
                      {[
                        ['1', 'Full name',                       'Including maiden name if applicable'],
                        ['2', 'Date of birth',                   ''],
                        ['3', 'Age at time of interview',        ''],
                        ['4', 'Place of birth',                  'City, region, country'],
                        ['5', 'Pre-war nationality / citizenship',''],
                        ['6', 'Religion / Jewish denomination',  'Orthodox, Reform, secular, etc.'],
                        ['7', 'Mother tongue',                   'Languages spoken at home'],
                        ['8', "Father's name and occupation",    ''],
                        ['9', "Mother's name and maiden name",   ''],
                        ['10','Siblings and birth order',        'Number of siblings and position'],
                      ].map(([n, title, sub]) => (
                        <div key={n} style={{ display: 'flex', gap: '8px', alignItems: 'baseline', marginBottom: '5px' }}>
                          <span style={{ fontFamily: 'monospace', fontSize: '0.58rem', fontWeight: 800, color: '#f59e0b', minWidth: '16px', textAlign: 'right' }}>{n}</span>
                          <div>
                            <span style={{ fontSize: '0.65rem', fontWeight: 700, color: theme.text }}>{title}</span>
                            {sub && <span style={{ fontSize: '0.58rem', color: theme.textSub, marginLeft: '5px' }}>{sub}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                  );
                })()}

                {/* ROW 3 — Tape selector + Propose */}
                {editorClipsView === 'intro' && (
                <div style={{ border: `1px solid ${theme.border}`, borderRadius: '10px', background: theme.card, padding: '10px 10px 8px' }}>
                  <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                    <div style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: theme.textSub }}>
                      Propose Clips
                    </div>
                    <div style={{ fontSize: '0.6rem', color: theme.textSub, marginTop: '1px' }}>
                      Choose a tape below, then run proposal discovery for that tape.
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '5px' }}>
                    {(() => {
                      const pts = videoList.filter(v => getProjectRoot(v) === root).map(v => getPartNumber(v)).sort((a,b)=>a-b);
                      return (pts.length > 0 ? pts : [1]).map(p => {
                        const partClipCount = editorClips.filter(c => c.part_number === p).length;
                        return (
                          <button key={p} onClick={() => setClipsTargetPart(p)} style={{
                            padding: '5px 8px', fontSize: '0.62rem', fontWeight: 700, border: `1px solid ${clipsTargetPart === p ? theme.glossaryAccent : theme.border}`,
                            borderRadius: '8px', cursor: 'pointer', position: 'relative',
                            background: clipsTargetPart === p ? `${theme.glossaryAccent}18` : theme.sidebar,
                            color: clipsTargetPart === p ? theme.glossaryAccent : theme.textSub,
                            minWidth: 0,
                          }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}><Icon icon={Film} size={9} /> Tape {p}</span>
                            {partClipCount > 0 && (
                              <span style={{
                                position: 'absolute', top: '-4px', right: '-4px',
                                background: '#34c759', color: '#fff',
                                borderRadius: '6px', fontSize: '0.5rem', fontWeight: 800,
                                padding: '0 3px', minWidth: '10px', textAlign: 'center', lineHeight: '12px',
                              }}>{partClipCount}</span>
                            )}
                          </button>
                        );
                      });
                    })()}
                  </div>
                  {/* Centered Propose button */}
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
                    <button
                      disabled={clipsGenerating}
                      onClick={() => {
                        const projectBase = getProjectRoot(selectedVideo);
                        const targetVideo = videoList.find(v => {
                          const vRoot = getProjectRoot(v);
                          return vRoot === projectBase && v.toLowerCase().includes(`pt${clipsTargetPart}`);
                        }) || selectedVideo;
                        const id = `clips-${projectBase}-pt${clipsTargetPart}`;
                        setClipsGenerating(true);
                        pushToast(id, 'running', `Proposing clips — ${projectBase} Tape ${clipsTargetPart}…`);
                        logActivity(id, 'clips', `Clip proposals — ${projectBase} Tape ${clipsTargetPart}`, 'running');
                        axios.post(`${BACKEND_URL}/clips/generate/${encodeURIComponent(targetVideo)}?part=${clipsTargetPart}&clear_existing=true`)
                          .then(r => {
                            const n = r.data.generated ?? 0;
                            setClipsGenerating(false);
                            pushToast(id, 'success', `✓ ${n} clip${n !== 1 ? 's' : ''} proposed — ${projectBase} Tape ${clipsTargetPart}`);
                            logActivity(id, 'clips', `Clip proposals — ${projectBase} Tape ${clipsTargetPart} · ${n} clip${n !== 1 ? 's' : ''} found`, 'success');
                            loadEditorClips();
                          })
                          .catch(e => {
                            const msg = e?.response?.data?.detail || e.message;
                            setClipsGenerating(false);
                            pushToast(id, 'error', `Clip generation failed: ${msg}`);
                            logActivity(id, 'clips', `Clip proposals — ${projectBase} Tape ${clipsTargetPart}`, 'error', { message: `Clip generation failed: ${msg}` });
                          });
                      }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        padding: '6px 16px',
                        background: clipsGenerating ? theme.sidebar : theme.glossaryAccent,
                        color: clipsGenerating ? theme.textSub : '#fff',
                        border: clipsGenerating ? `1px solid ${theme.border}` : 'none',
                        borderRadius: '8px',
                        cursor: clipsGenerating ? 'not-allowed' : 'pointer',
                        fontWeight: 700, fontSize: '0.64rem',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <Icon icon={clipsGenerating ? Loader : Scissors} size={11} style={clipsGenerating ? { animation: 'spin 1s linear infinite' } : {}} />
                      {clipsGenerating ? 'Proposing…' : `Propose for Tape ${clipsTargetPart}`}
                    </button>
                  </div>
                </div>
                )}

                {/* ROW 4 — Unified review filters box */}
                {(
                <div style={{ border: `1px solid ${theme.border}`, borderRadius: '10px', background: theme.card, padding: '8px 10px' }}>
                  <div style={{ fontSize: '0.58rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: theme.textSub, marginBottom: '6px' }}>
                    Review Filters
                  </div>
                  {/* Status filter pills (no assembly — it's separate below) */}
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {filterPillOrder.filter(k => k !== 'assembly').map((key, idx) => {
                    const PILL_META = {
                      all:       { label: 'ALL',      color: null },
                      proposed:  { label: 'PROPOSED', color: null },
                      approved:  { label: 'APPROVED', color: null },
                      flagged:   { label: 'FLAGGED',  color: null },
                      exported:  { label: 'EXPORTED', color: null },
                      rejected:  { label: 'REJECTED', color: null },
                    };
                    const meta = PILL_META[key] || { label: key.toUpperCase(), color: null };
                    const count = key === 'all'
                      ? clipPool.length
                      : clipPool.filter(c => c.status === key).length;
                    const active = editorClipsFilter === key;
                    const accentColor = meta.color || theme.glossaryAccent;
                    return (
                      <button
                        key={key}
                        draggable
                        onDragStart={() => { pillDragFrom.current = idx; }}
                        onDragOver={e => { e.preventDefault(); pillDragOver.current = idx; }}
                        onDrop={() => {
                          const from = pillDragFrom.current;
                          const to   = pillDragOver.current;
                          if (from === null || to === null || from === to) return;
                          const next = [...filterPillOrder];
                          const [moved] = next.splice(from, 1);
                          next.splice(to, 0, moved);
                          setFilterPillOrder(next);
                          localStorage.setItem('filterPillOrder', JSON.stringify(next));
                          pillDragFrom.current = null;
                          pillDragOver.current = null;
                        }}
                        onDragEnd={() => { pillDragFrom.current = null; pillDragOver.current = null; }}
                        onClick={() => setEditorClipsFilter(key)}
                        title="Drag to reorder"
                        style={{
                          display: 'flex', alignItems: 'center', gap: '4px',
                          padding: '3px 8px', fontSize: '0.62rem', fontWeight: 700,
                          border: 'none', borderRadius: '10px',
                          cursor: 'grab',
                          background: active ? accentColor : theme.card,
                          color: active ? '#fff' : (meta.color || theme.textSub),
                          userSelect: 'none',
                        }}
                      >
                        {meta.label}
                        <span style={{
                          background: active ? 'rgba(255,255,255,0.25)' : (isDarkMode ? '#333' : '#d8d8dc'),
                          color: active ? '#fff' : theme.text,
                          borderRadius: '8px', padding: '0 5px', fontSize: '0.58rem',
                          fontWeight: 800, minWidth: '14px', textAlign: 'center',
                        }}>{count}</span>
                      </button>
                    );
                  })}
                  </div>

                  {/* HR + Category pills */}
                  <div style={{ height: '1px', background: theme.border, margin: '8px 0' }} />
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {[
                      { key: 'essentials', label: 'Essential Clips', count: introClips.filter(c => c.is_essential).length },
                      { key: 'intro', label: 'Intro Clips', count: introClips.filter(c => !c.is_essential).length },
                      { key: 'quotes', label: 'Memorable Quotes', count: introClips.filter(c => c.category === 'memorable_quote').length },
                      { key: 'voice', label: 'Voice Clips', count: voiceClips.length },
                    ].map(tab => {
                      const active = editorClipsView === tab.key;
                      return (
                        <button
                          key={tab.key}
                          onClick={() => { setEditorClipsView(tab.key); setEditorClipsFilter('all'); }}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '5px',
                            padding: '3px 8px', borderRadius: '10px',
                            border: `1px solid ${active ? theme.glossaryAccent : 'transparent'}`,
                            background: active ? `${theme.glossaryAccent}18` : 'none',
                            color: active ? theme.glossaryAccent : theme.textSub,
                            fontSize: '0.62rem', fontWeight: 700, cursor: 'pointer',
                          }}
                        >
                          {tab.label}
                          <span style={{
                            background: active ? `${theme.glossaryAccent}22` : (isDarkMode ? '#333' : '#d8d8dc'),
                            color: active ? theme.glossaryAccent : theme.text,
                            borderRadius: '8px', padding: '0 5px', fontSize: '0.58rem',
                            fontWeight: 800, minWidth: '14px', textAlign: 'center',
                          }}>{tab.count}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* HR + Assembly */}
                  <div style={{ height: '1px', background: theme.border, margin: '8px 0' }} />
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {(() => {
                      const assemblyCount = editorClips.filter(c => c.assembly_order != null).length;
                      const active = editorClipsFilter === 'assembly';
                      return (
                        <button
                          onClick={() => { setEditorClipsFilter('assembly'); setEditorClipsView('all'); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '4px',
                            padding: '3px 8px', fontSize: '0.62rem', fontWeight: 700,
                            border: 'none', borderRadius: '10px', cursor: 'pointer',
                            background: active ? '#f59e0b' : 'none',
                            color: active ? '#fff' : '#f59e0b',
                            userSelect: 'none',
                          }}
                        >
                          <ListOrdered size={9} strokeWidth={2.5} />
                          ASSEMBLY
                          <span style={{
                            background: active ? 'rgba(255,255,255,0.25)' : (isDarkMode ? '#333' : '#d8d8dc'),
                            color: active ? '#fff' : theme.text,
                            borderRadius: '8px', padding: '0 5px', fontSize: '0.58rem',
                            fontWeight: 800, minWidth: '14px', textAlign: 'center',
                          }}>{assemblyCount}</span>
                        </button>
                      );
                    })()}
                  </div>
                </div>
                )}

                {/* Clip Card Help — toggle is in the header row */}
                {showClipsHelp && (
                  <div style={{ marginTop: '6px', padding: '10px 12px', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '8px', fontSize: '0.62rem', lineHeight: 1.7, color: theme.textSub }}>
                    <div style={{ fontWeight: 800, fontSize: '0.58rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: theme.glossaryAccent, marginBottom: '6px' }}>Clip Card Actions</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: '3px 10px' }}>
                      <span style={{ fontWeight: 700, color: theme.text }}>Trim</span>
                      <span>Adjust IN/OUT points with nudge controls, waveform, and playhead</span>
                      <span style={{ fontWeight: 700, color: theme.text }}>Name Edit</span>
                      <span>Duplicate clip as a voice clip for isolating name pronunciation</span>
                      <span style={{ fontWeight: 700, color: theme.text }}>Unapprove</span>
                      <span>Revert from Approved back to Proposed, remove from assembly</span>
                      <span style={{ fontWeight: 700, color: theme.text }}>Approve</span>
                      <span>Mark clip as approved for inclusion in final assembly</span>
                      <span style={{ fontWeight: 700, color: theme.text }}>Reject</span>
                      <span>Mark clip as rejected — hidden from assembly</span>
                      <span style={{ fontWeight: 700, color: theme.text }}>Use for Name</span>
                      <span>Toggle this clip as the pronunciation clip (without duplicating)</span>
                      <span style={{ fontWeight: 700, color: theme.text }}>Flag</span>
                      <span>Flag clip for review with notes</span>
                      <span style={{ fontWeight: 700, color: theme.text }}>Export MP4</span>
                      <span>Extract clip as a standalone MP4 file with optional subtitles</span>
                    </div>
                    <div style={{ marginTop: '8px', borderTop: `1px solid ${theme.border}`, paddingTop: '6px' }}>
                      <div style={{ fontWeight: 800, fontSize: '0.58rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: theme.glossaryAccent, marginBottom: '4px' }}>Card Info</div>
                      <span style={{ fontWeight: 700, color: theme.text }}>Timecode</span> — click to seek video and play clip range. Shows start → end and duration.<br/>
                      <span style={{ fontWeight: 700, color: theme.text }}>Category pill</span> — color-coded topic (reflection, family, historical, etc.)<br/>
                      <span style={{ fontWeight: 700, color: theme.text }}>Status badge</span> — current state: Proposed, Approved, Flagged, Rejected, Exported
                    </div>
                  </div>
                )}

                {/* ── EXPORTED CLIPS BROWSER ── auto-loads, hidden when empty */}
                {(() => {
                  // Auto-load clip files for the project currently open. Keyed on
                  // root, not on "clipFiles is still null": without that, opening
                  // a second interview kept showing the FIRST one's exported clips
                  // while the download links pointed at the new root.
                  if (root && clipFilesRoot.current !== root) {
                    clipFilesRoot.current = root;
                    const forRoot = root;
                    axios.get(`${BACKEND_URL}/clips/files/${encodeURIComponent(forRoot)}`)
                      .then(r => { if (clipFilesRoot.current === forRoot) setClipFiles(r.data); })
                      .catch(() => { if (clipFilesRoot.current === forRoot) setClipFiles({ files: [], total_size: 0 }); });
                  }
                  if (!clipFiles || clipFiles.files.length === 0) return null;
                  return (
                    <div style={{ border: `1px solid ${theme.border}`, borderRadius: '10px', background: theme.card, padding: '8px 10px', marginTop: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                        <Icon icon={FolderOpen} size={11} color={theme.glossaryAccent} />
                        <span style={{ fontSize: '0.58rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: theme.textSub }}>
                          Exported Clips
                        </span>
                        <span style={{ fontSize: '0.55rem', color: theme.textSub }}>
                          {clipFiles.files.length} file{clipFiles.files.length !== 1 ? 's' : ''} · {(clipFiles.total_size / (1024 * 1024)).toFixed(1)} MB
                        </span>
                        <button onClick={() => {
                          axios.get(`${BACKEND_URL}/clips/files/${encodeURIComponent(root)}`).then(r => setClipFiles(r.data)).catch(() => setClipFiles({ files: [], total_size: 0 }));
                        }} title="Refresh" style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: theme.textSub, display: 'flex', padding: 0 }}>
                          <Icon icon={RefreshCw} size={9} />
                        </button>
                      </div>
                      <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                        {[
                          { label: 'Assembly', filter: f => f.type === 'assembly' },
                          { label: 'Clips', filter: f => f.type !== 'assembly' && !f.name.toLowerCase().includes('quote') },
                          { label: 'Quotes', filter: f => f.type !== 'assembly' && f.name.toLowerCase().includes('quote') },
                        ].map(group => {
                          const items = clipFiles.files.filter(group.filter);
                          if (items.length === 0) return null;
                          return (
                            <React.Fragment key={group.label}>
                              {clipFiles.files.length > 2 && (
                                <div style={{ fontSize: '0.48rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '4px 8px 1px', marginTop: '2px' }}>{group.label}</div>
                              )}
                              {items.map(f => (
                                <div key={f.name} style={{
                                  display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 8px',
                                  background: f.type === 'assembly' ? (isDarkMode ? '#1a1000' : '#fffbeb') : 'transparent',
                                  border: f.type === 'assembly' ? '1px solid #f59e0b40' : 'none',
                                  borderRadius: '6px', marginBottom: '2px', fontSize: '0.60rem',
                                }}>
                                  <Icon icon={f.type === 'assembly' ? Film : Video} size={10} color={f.type === 'assembly' ? '#f59e0b' : theme.textSub} style={{ flexShrink: 0 }} />
                                  <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: theme.text, fontWeight: f.type === 'assembly' ? 700 : 400 }}>
                                    {f.name}
                                  </div>
                                  <span style={{ fontSize: '0.50rem', color: theme.textSub, flexShrink: 0 }}>{(f.size / (1024 * 1024)).toFixed(1)}MB</span>
                                  {f.has_subs.length > 0 && <span style={{ fontSize: '0.48rem', color: theme.glossaryAccent, flexShrink: 0 }}>{f.has_subs.map(s => s.toUpperCase()).join('·')}</span>}
                                  <button onClick={async () => {
                                    const n = await prompt({
                                      title: 'Rename clip',
                                      defaultValue: f.name.replace('.mp4', ''),
                                      confirmLabel: 'Rename',
                                    });
                                    if (!n || n === f.name.replace('.mp4', '')) return;
                                    axios.post(`${BACKEND_URL}/clips/files/${encodeURIComponent(root)}/rename`, null, { params: { old_name: f.name, new_name: n } })
                                      .then(() => axios.get(`${BACKEND_URL}/clips/files/${encodeURIComponent(root)}`).then(r => setClipFiles(r.data)))
                                      .catch(e => pushToast('clip-rename-error', 'error', 'Rename failed: ' + (e.response?.data?.detail || e.message)));
                                  }} title="Rename" style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.textSub, display: 'flex', padding: '1px', flexShrink: 0 }}>
                                    <Icon icon={Edit3} size={9} />
                                  </button>
                                  <button onClick={() => {
                                    const rootSlug = root.replace(/ /g, '_');
                                    const subdir = activeConfig?.active_subdir || '';
                                    const base = [VIDEO_SERVER_URL, ...subdir.split('/').filter(Boolean).map(s => encodeURIComponent(s)), 'clips', encodeURIComponent(rootSlug)].join('/');
                                    const streamUrl = `${base}/${encodeURIComponent(f.name)}`;
                                    const subs = f.has_subs.map(lang => ({ lang, url: `${base}/${encodeURIComponent(f.name.replace('.mp4', `_${lang}.vtt`))}` }));
                                    setClipPlayerSrc({ url: streamUrl, subs, name: f.name });
                                  }} title="Play" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#38bdf8', display: 'flex', padding: '1px', flexShrink: 0 }}>
                                    <Play size={11} />
                                  </button>
                                  <a href={(() => { const rootSlug = root.replace(/ /g, '_'); const subdir = activeConfig?.active_subdir || ''; return [VIDEO_SERVER_URL, ...subdir.split('/').filter(Boolean).map(s => encodeURIComponent(s)), 'clips', encodeURIComponent(rootSlug), encodeURIComponent(f.name)].join('/'); })()}
                                    download={f.name} title="Download" style={{ color: theme.textSub, display: 'flex', padding: '1px', flexShrink: 0 }}>
                                    <Download size={10} />
                                  </a>
                                </div>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

              </div>

              {/* Clip list / Assembly view */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px' }}>
                {(() => {
                  if (editorClipsLoading) return (
                    <div style={{ textAlign: 'center', padding: '40px', color: theme.textSub, fontSize: '0.78rem' }}>Loading clips…</div>
                  );

                  if (editorClipsFilter === 'assembly') {
                  /* ── ASSEMBLY VIEW ── non-negotiables on top, queue below ── */
                  const approved = editorClips.filter(c => c.status === 'approved');

                  // Split approved+in-assembly into two groups
                  const assembledNN  = approved
                    .filter(c => c.assembly_order != null && c.is_essential)
                    .slice().sort((a, b) => a.assembly_order - b.assembly_order);
                  const assembledReg = approved
                    .filter(c => c.assembly_order != null && !c.is_essential)
                    .slice().sort((a, b) => a.assembly_order - b.assembly_order);
                  const notInQueue   = approved.filter(c => c.assembly_order == null);

                  // Save combined order: NN get 1..n, regular get n+1..m
                  const saveOrderCombined = (nnItems, regItems) => {
                    const payload = [
                      ...nnItems.map((c, i) => ({ id: c.id, assembly_order: i + 1 })),
                      ...regItems.map((c, i) => ({ id: c.id, assembly_order: nnItems.length + i + 1 })),
                    ];
                    axios.put(`${BACKEND_URL}/clips/assembly-order`, payload)
                      .then(() => loadEditorClips())
                      .catch(e => pushToast('asm-err', 'error', `Save order failed: ${e.message}`));
                  };

                  const removeFromAssembly = (clip) => {
                    setEditorClips(prev => prev.map(c => c.id === clip.id ? { ...c, assembly_order: null } : c));
                    axios.put(`${BACKEND_URL}/clips/assembly-order`, [{ id: clip.id, assembly_order: null }])
                      .then(() => loadEditorClips())
                      .catch(e => pushToast('asm-err', 'error', `Remove failed: ${e.message}`));
                  };

                  const toggleEssential = (clip, val) => {
                    setEditorClips(prev => prev.map(c => c.id === clip.id ? { ...c, is_essential: val } : c));
                    axios.put(`${BACKEND_URL}/clips/${clip.id}/essential?value=${val}`)
                      .then(() => loadEditorClips())
                      .catch(e => pushToast('asm-err', 'error', `Toggle failed: ${e.message}`));
                  };

                  const handleDropFromApproved = (e, makeEssential) => {
                    e.preventDefault();
                    const clipId = window._dragClipId;
                    if (!clipId) return;
                    window._dragClipId = null;
                    const clip = editorClips.find(c => c.id === clipId);
                    if (!clip) return;
                    const nextOrder = assembledNN.length + assembledReg.length + 1;
                    setEditorClips(prev => prev.map(c => c.id === clipId ? { ...c, assembly_order: nextOrder, is_essential: makeEssential } : c));
                    const updates = [axios.put(`${BACKEND_URL}/clips/assembly-order`, [{ id: clipId, assembly_order: nextOrder }])];
                    if (makeEssential) updates.push(axios.put(`${BACKEND_URL}/clips/${clipId}/essential?value=true`));
                    Promise.all(updates).then(() => loadEditorClips()).catch(e => pushToast('asm-err', 'error', `Drop failed: ${e.message}`));
                  };

                  const addToAssembly = (clip) => {
                    const nextOrder = assembledNN.length + assembledReg.length + 1;
                    setEditorClips(prev => prev.map(c => c.id === clip.id ? { ...c, assembly_order: nextOrder } : c));
                    axios.put(`${BACKEND_URL}/clips/assembly-order`, [{ id: clip.id, assembly_order: nextOrder }])
                      .then(() => loadEditorClips())
                      .catch(e => pushToast('asm-err', 'error', `Add failed: ${e.message}`));
                  };

                  // Drag handlers for NN group
                  const onDragStartNN  = (idx) => { dragFromIdxNN.current = idx; };
                  const onDragOverNN   = (e, idx) => { e.preventDefault(); dragOverIdxNN.current = idx; };
                  const onDropNN       = () => {
                    const from = dragFromIdxNN.current, to = dragOverIdxNN.current;
                    if (from === null || to === null || from === to) return;
                    const next = [...assembledNN];
                    const [moved] = next.splice(from, 1);
                    next.splice(to, 0, moved);
                    const orderMap = {};
                    next.forEach((c, i) => { orderMap[c.id] = i + 1; });
                    setEditorClips(prev => prev.map(c => c.id in orderMap ? { ...c, assembly_order: orderMap[c.id] } : c));
                    saveOrderCombined(next, assembledReg);
                    dragFromIdxNN.current = null; dragOverIdxNN.current = null;
                  };

                  // Drag handlers for regular group
                  const onDragStart  = (idx) => { dragFromIdx.current = idx; };
                  const onDragOver   = (e, idx) => { e.preventDefault(); dragOverIdx.current = idx; };
                  const onDrop       = () => {
                    const from = dragFromIdx.current, to = dragOverIdx.current;
                    if (from === null || to === null || from === to) return;
                    const next = [...assembledReg];
                    const [moved] = next.splice(from, 1);
                    next.splice(to, 0, moved);
                    const orderMap = {};
                    next.forEach((c, i) => { orderMap[c.id] = assembledNN.length + i + 1; });
                    setEditorClips(prev => prev.map(c => c.id in orderMap ? { ...c, assembly_order: orderMap[c.id] } : c));
                    saveOrderCombined(assembledNN, next);
                    dragFromIdx.current = null; dragOverIdx.current = null;
                  };

                  // Reusable card renderer. Called as a plain function, NOT as
                  // <AssemblyCard/>: it is declared inside render, so as a JSX
                  // element type it was a brand-new component identity on every
                  // render and React unmounted + remounted every card on each
                  // pin / essential-toggle / reorder. The list key therefore
                  // lives on the returned root div below.
                  const AssemblyCard = ({ clip, idx, isNN, onDragStartFn, onDragOverFn, onDropFn }) => {
                    const cat = CLIP_CATEGORIES[clip.category] || CLIP_CATEGORIES.reflection;
                    const dur = Math.round(clip.end_time - clip.start_time);
                    const questionLabel = clip.use_for_pronunciation
                      ? 'Used for Name Pronunciation'
                      : (/^Question\s+\d+\b.*$/i.test(clip.reason || '') ? clip.reason : '');
                    return (
                      <div
                        key={clip.id}
                        draggable
                        onDragStart={() => onDragStartFn(idx)}
                        onDragOver={e => onDragOverFn(e, idx)}
                        onDrop={onDropFn}
                        style={{
                          background: isNN ? (isDarkMode ? '#1a0a00' : '#fffbeb') : theme.card,
                          border: `1px solid ${isNN ? '#f59e0b' : theme.border}`,
                          borderLeft: `4px solid ${isNN ? '#f59e0b' : cat.color}`,
                          borderRadius: '8px', padding: '8px 10px', marginBottom: '6px',
                          display: 'flex', alignItems: 'flex-start', gap: '8px',
                          cursor: 'grab', userSelect: 'none',
                        }}
                      >
                        {/* Number + grip handle below */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
                          <div style={{
                            minWidth: '20px', height: '20px', borderRadius: '50%',
                            background: isNN ? '#f59e0b' : theme.border,
                            color: isNN ? '#fff' : theme.text,
                            fontSize: '0.65rem', fontWeight: 800, fontFamily: 'monospace',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>{idx + 1}</div>
                          <GripVertical size={14} strokeWidth={1.5} style={{ color: theme.textSub, opacity: 0.5 }} />
                        </div>

                        {/* Content */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {questionLabel && (
                            <div style={{ marginBottom: '3px' }}>
                              <span style={{ fontSize: '0.58rem', fontWeight: 800, letterSpacing: '0.06em', color: cat.color, background: `${cat.color}16`, padding: '2px 6px', borderRadius: '999px' }}>
                                {questionLabel}
                              </span>
                            </div>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '3px', flexWrap: 'wrap' }}>
                            <span
                              onClick={() => {
                                if (trimTarget) {
                                  // During trim: just seek and pause
                                  if (videoRef.current) { videoRef.current.currentTime = clip.start_time; videoRef.current.pause(); }
                                } else {
                                  playVideoClipRange(clip);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                              aria-label={trimTarget ? 'Seek to clip start' : 'Play clip range'}
                              onKeyDown={ev => {
                                if (ev.key !== 'Enter' && ev.key !== ' ') return;
                                ev.preventDefault();
                                if (trimTarget) {
                                  if (videoRef.current) { videoRef.current.currentTime = clip.start_time; videoRef.current.pause(); }
                                } else {
                                  playVideoClipRange(clip);
                                }
                              }}
                              onFocus={ev => { ev.currentTarget.style.outline = `2px solid ${theme.accent}`; ev.currentTarget.style.outlineOffset = '2px'; }}
                              onBlur={ev => { ev.currentTarget.style.outline = 'none'; }}
                              style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: trimTarget ? '#f87171' : theme.accent, cursor: 'pointer', fontWeight: 700 }}
                            >{formatVTTTime(clip.start_time)}</span>
                            <span style={{ fontSize: '0.6rem', color: theme.textSub, display: 'inline-flex', alignItems: 'center', gap: '2px' }}><Icon icon={Film} size={8} color={theme.textSub} /> Tape {clip.part_number} · {formatDurHuman(dur)}</span>
                            <span style={{ fontSize: '0.6rem', fontWeight: 700, color: cat.color, background: `${cat.color}20`, padding: '1px 5px', borderRadius: '6px' }}>{clip.category}</span>
                          </div>
                          {clip.quote_text_sv && (
                            <div style={{ marginBottom: clip.quote_text_en ? '4px' : 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                                {renderClipLanguagePill('0.6rem')}
                              </div>
                              <div style={{ fontSize: '0.72rem', color: theme.text, fontStyle: 'italic', lineHeight: 1.4 }}>
                                "{clip.quote_text_sv}"
                              </div>
                            </div>
                          )}
                          {clip.quote_text_en && (
                            <div style={{ fontSize: '0.68rem', color: theme.textSub, lineHeight: 1.4, paddingLeft: '6px', borderLeft: `2px solid ${isDarkMode ? '#333' : '#ddd'}` }}>
                              {clip.quote_text_en}
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', flexShrink: 0 }}>
                          {/* Trim */}
                          <button
                            onClick={e => { e.stopPropagation(); setTrimTarget(clip); setTrimStart(clip.start_time); setTrimEnd(clip.end_time); if (videoRef.current) { videoRef.current.currentTime = clip.start_time; videoRef.current.pause(); } }}
                            title="Trim clip in/out points"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#38bdf8', borderRadius: '4px', display: 'flex', alignItems: 'center' }}
                          >
                            <Scissors size={12} strokeWidth={2.5} />
                          </button>
                          {/* Pin / unpin */}
                          <button
                            onClick={e => { e.stopPropagation(); toggleEssential(clip, !isNN); }}
                            title={isNN ? 'Move to regular queue' : 'Mark as essential'}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
                              color: isNN ? '#f59e0b' : theme.textSub, borderRadius: '4px',
                              display: 'flex', alignItems: 'center',
                            }}
                          >
                            <Pin size={12} strokeWidth={2.5} />
                          </button>
                          {/* Remove */}
                          <button
                            onClick={e => { e.stopPropagation(); removeFromAssembly(clip); }}
                            title="Remove from assembly"
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
                              color: theme.textSub, borderRadius: '4px', display: 'flex', alignItems: 'center',
                            }}
                            onMouseEnter={e => e.currentTarget.style.color = theme.accent}
                            onMouseLeave={e => e.currentTarget.style.color = theme.textSub}
                          >
                            <X size={12} strokeWidth={2.5} />
                          </button>
                        </div>
                      </div>
                    );
                  };

                  const totalSecs = [...assembledNN, ...assembledReg].reduce((s, c) => s + (c.end_time - c.start_time), 0);

                  return (
                    <div>
                      {/* Export + Download */}
                      {(assembledNN.length + assembledReg.length) > 0 && (
                        <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
                          <button
                            disabled={assemblyExporting}
                            onClick={() => {
                              setAssemblyExporting(true); setAssemblyReady(false);
                              const toastId = `asm-export-${Date.now()}`;
                              pushToast(toastId, 'running', `Rendering assembly — ${root}…`);
                              logActivity(toastId, 'clips', `Assembly export — ${root}`, 'running');
                              axios.post(`${BACKEND_URL}/clips/export-assembly/${encodeURIComponent(root)}`)
                                .then(r => {
                                  const jobId = r.data.job_id;
                                  const poll = setInterval(() => {
                                    axios.get(`${BACKEND_URL}/jobs/${jobId}`).then(jr => {
                                      const job = jr.data;
                                      if (job.status === 'complete') {
                                        clearInterval(poll);
                                        setAssemblyExporting(false); setAssemblyReady(true);
                                        pushToast(toastId, 'success', `Assembly exported — ${job.result?.segments || 0} segments`);
                                        logActivity(toastId, 'clips', `Assembly export — ${root}`, 'success');
                                        axios.get(`${BACKEND_URL}/clips/files/${encodeURIComponent(root)}`).then(fr => setClipFiles(fr.data)).catch(() => {});
                                      } else if (job.status === 'failed') {
                                        clearInterval(poll);
                                        setAssemblyExporting(false);
                                        pushToast(toastId, 'error', `Assembly export failed: ${job.error || 'Unknown error'}`);
                                        logActivity(toastId, 'clips', `Assembly export — ${root}`, 'error', { message: job.error });
                                      }
                                    }).catch(() => {});
                                  }, 3000);
                                })
                                .catch(e => { setAssemblyExporting(false); const msg = e?.response?.data?.detail || e.message; pushToast(toastId, 'error', `Assembly export failed: ${msg}`); logActivity(toastId, 'clips', `Assembly export — ${root}`, 'error', { message: msg }); });
                            }}
                            style={{ flex: 1, padding: '6px 10px', border: 'none', borderRadius: '6px', fontWeight: 700, fontSize: '0.68rem', cursor: assemblyExporting ? 'not-allowed' : 'pointer', background: assemblyExporting ? theme.card : '#f59e0b', color: assemblyExporting ? theme.textSub : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}
                          >
                            <Icon icon={assemblyExporting ? Loader : Video} size={11} style={assemblyExporting ? { animation: 'spin 1s linear infinite' } : {}} />
                            {assemblyExporting ? 'Rendering…' : 'Export Assembly'}
                          </button>
                          {assemblyReady && (
                            <a href={`${BACKEND_URL}/clips/download-assembly/${encodeURIComponent(root)}`} download
                              style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: `1px solid #f59e0b`, fontWeight: 700, fontSize: '0.68rem', background: 'transparent', color: '#f59e0b', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                              <Download size={11} strokeWidth={2} /> Download MP4
                            </a>
                          )}
                        </div>
                      )}

                      {/* ── ESSENTIAL CLIPS section ── */}
                      <div onDragOver={e => e.preventDefault()} onDrop={e => handleDropFromApproved(e, true)}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', padding: '2px 4px', borderRadius: '6px', transition: 'background 0.15s' }}>
                        <Pin size={11} strokeWidth={2.5} style={{ color: '#f59e0b' }} />
                        <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#f59e0b', letterSpacing: '0.06em' }}>
                          ESSENTIAL CLIPS — {assembledNN.length} clip{assembledNN.length !== 1 ? 's' : ''}
                        </span>
                        <span style={{ fontSize: '0.6rem', color: theme.textSub, marginLeft: 'auto' }}>
                          {assembledNN.length > 0 && `≈ ${Math.round(assembledNN.reduce((s, c) => s + (c.end_time - c.start_time), 0))}s`}
                        </span>
                      </div>

                      {assembledNN.length === 0 && (
                        <div onDragOver={e => e.preventDefault()} onDrop={e => handleDropFromApproved(e, true)}
                          style={{ textAlign: 'center', padding: '12px', color: theme.textSub, fontSize: '0.72rem', background: isDarkMode ? '#1a0a00' : '#fffbeb', border: '1px dashed #f59e0b', borderRadius: '8px', marginBottom: '10px', opacity: 0.7 }}>
                          No essential clips yet.<br />
                          <span style={{ fontSize: '0.65rem' }}>Drag approved clips here or click <Pin size={10} style={{ display: 'inline', verticalAlign: 'middle' }} /> to pin.</span>
                        </div>
                      )}
                      {assembledNN.map((clip, idx) => AssemblyCard({
                        clip, idx, isNN: true,
                        onDragStartFn: onDragStartNN, onDragOverFn: onDragOverNN, onDropFn: onDropNN,
                      }))}

                      {/* ── QUEUE section ── */}
                      <div onDragOver={e => e.preventDefault()} onDrop={e => handleDropFromApproved(e, false)}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '12px 0 6px', padding: '2px 4px', borderRadius: '6px', transition: 'background 0.15s' }}>
                        <ListOrdered size={11} strokeWidth={2} style={{ color: theme.textSub }} />
                        <span style={{ fontSize: '0.65rem', fontWeight: 700, color: theme.textSub, letterSpacing: '0.06em' }}>
                          QUEUE — {assembledReg.length} clip{assembledReg.length !== 1 ? 's' : ''}
                        </span>
                        <span style={{ fontSize: '0.6rem', color: theme.textSub, marginLeft: 'auto' }}>
                          {totalSecs > 0 && `total ≈ ${formatDurHuman(totalSecs)}`}
                        </span>
                      </div>

                      {assembledReg.length === 0 && (
                        <div onDragOver={e => e.preventDefault()} onDrop={e => handleDropFromApproved(e, false)}
                          style={{ textAlign: 'center', padding: '12px', color: theme.textSub, fontSize: '0.72rem', background: theme.card, border: `1px dashed ${theme.border}`, borderRadius: '8px', marginBottom: '10px', opacity: 0.7 }}>
                          No clips in queue.<br />
                          <span style={{ fontSize: '0.65rem' }}>Drag approved clips here or use + ADD below.</span>
                        </div>
                      )}
                      {assembledReg.map((clip, idx) => AssemblyCard({
                        clip, idx, isNN: false,
                        onDragStartFn: onDragStart, onDragOverFn: onDragOver, onDropFn: onDrop,
                      }))}

                      {/* ── Approved, not yet in assembly ── */}
                      {notInQueue.length > 0 && (
                        <>
                          <div style={{ fontSize: '0.62rem', fontWeight: 700, color: theme.textSub, letterSpacing: '0.06em', margin: '14px 0 6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            APPROVED — NOT IN ASSEMBLY
                            <span style={{ fontSize: '0.55rem', fontWeight: 500, opacity: 0.6 }}>drag to add</span>
                          </div>
                          {notInQueue.map(clip => {
                            const cat = CLIP_CATEGORIES[clip.category] || CLIP_CATEGORIES.reflection;
                            const dur = Math.round(clip.end_time - clip.start_time);
                            return (
                              <div key={clip.id} draggable
                                onDragStart={() => { window._dragClipId = clip.id; }}
                                onDragEnd={() => { window._dragClipId = null; }}
                                style={{ background: theme.card, border: `1px solid ${theme.border}`, borderLeft: `3px solid ${cat.color}`, borderRadius: '8px', padding: '7px 10px', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '8px', opacity: 0.75, cursor: 'grab' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: theme.accent, fontWeight: 700 }}>{formatVTTTime(clip.start_time)}</span>
                                    <span style={{ fontSize: '0.6rem', color: theme.textSub, display: 'inline-flex', alignItems: 'center', gap: '2px' }}><Icon icon={Film} size={8} color={theme.textSub} /> Tape {clip.part_number} · {formatDurHuman(dur)} · {clip.category}</span>
                                  </div>
                                  {clip.quote_text_sv && <div style={{ fontSize: '0.68rem', color: theme.textSub, fontStyle: 'italic', marginTop: '2px' }}>"{clip.quote_text_sv.slice(0, 80)}…"</div>}
                                </div>
                                <button onClick={() => addToAssembly(clip)}
                                  style={{ background: '#f59e0b20', border: `1px solid #f59e0b`, color: '#f59e0b', borderRadius: '5px', padding: '3px 8px', fontSize: '0.6rem', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                                  + ADD
                                </button>
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  );
                  }  // end assembly

                  if (editorClipsError) return (
                    <LoadFailure theme={theme} what="clips for this interview" onRetry={loadEditorClips} />
                  );
                  if (byPart.length === 0) return (
                    <div style={{ textAlign: 'center', padding: '40px', color: theme.textSub, fontSize: '0.78rem' }}>
                      {editorClipsView === 'voice' ? 'No voice clips yet.' : 'No clips yet.'}<br />
                      <span style={{ fontSize: '0.7rem', opacity: 0.6 }}>
                        {editorClipsView === 'voice' ? 'Use Name Edit or Use for Name to create one.' : 'Select a part and click Propose.'}
                      </span>
                    </div>
                  );

                  return byPart.map(({ part, clips: partClips }) => {
                  const approved = partClips.filter(c => c.status === 'approved').length;
                  return (
                    <div key={part} style={{ marginBottom: '18px' }}>
                      {/* Part header */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', paddingBottom: '4px', borderBottom: `1px solid ${theme.border}` }}>
                        <span style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', color: theme.textSub, fontFamily: 'monospace' }}>PART {part}</span>
                        <span style={{ fontSize: '0.6rem', color: theme.textSub }}>{partClips.length} clip{partClips.length !== 1 ? 's' : ''}</span>
                        {approved > 0 && (
                          <span style={{ fontSize: '0.6rem', color: '#4ade80', marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <Icon icon={Check} size={10} />
                            {approved} approved
                          </span>
                        )}
                      </div>

                      {partClips.map(clip => {
                        const cat = CLIP_CATEGORIES[clip.category] || CLIP_CATEGORIES.reflection;
                        const statusStyle = CLIP_STATUS_STYLE[clip.status] || CLIP_STATUS_STYLE.proposed;
                        const dur = Math.round(clip.end_time - clip.start_time);
                        const questionLabel = clip.use_for_pronunciation
                          ? 'Used for Name Pronunciation'
                          : (/^Question\s+\d+\b.*$/i.test(clip.reason || '') ? clip.reason : '');
                        return (
                          <div key={clip.id} style={{
                            background: theme.card, borderRadius: '10px', marginBottom: '8px',
                            border: `1px solid ${clip.status === 'approved' ? '#22c55e40' : clip.status === 'flagged' ? '#ef444440' : theme.border}`,
                            overflow: 'hidden',
                          }}>
                            {/* Card top bar — category colour */}
                            <div style={{ height: '3px', background: cat.color, opacity: clip.status === 'rejected' ? 0.2 : 1 }} />

                            <div style={{ padding: '10px 12px' }}>
                              {questionLabel && (
                                <div style={{ marginBottom: '5px' }}>
                                  <span style={{ fontSize: '0.58rem', fontWeight: 800, letterSpacing: '0.06em', color: cat.color, background: `${cat.color}16`, padding: '2px 6px', borderRadius: '999px' }}>
                                    {questionLabel}
                                  </span>
                                </div>
                              )}
                              {/* Row 1: timecode + badges + status */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', flexWrap: 'wrap' }}>
                                <span
                                  onClick={() => {
                                    if (trimTarget) {
                                      if (videoRef.current) { videoRef.current.currentTime = clip.start_time; videoRef.current.pause(); }
                                    } else {
                                      playVideoClipRange(clip);
                                    }
                                  }}
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={ev => {
                                    if (ev.key !== 'Enter' && ev.key !== ' ') return;
                                    ev.preventDefault();
                                    if (trimTarget) {
                                      if (videoRef.current) { videoRef.current.currentTime = clip.start_time; videoRef.current.pause(); }
                                    } else {
                                      playVideoClipRange(clip);
                                    }
                                  }}
                                  onFocus={ev => { ev.currentTarget.style.outline = `2px solid ${theme.accent}`; ev.currentTarget.style.outlineOffset = '2px'; }}
                                  onBlur={ev => { ev.currentTarget.style.outline = 'none'; }}
                                  style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: trimTarget ? '#f87171' : theme.accent, fontWeight: 700, cursor: 'pointer' }}
                                  title={trimTarget ? "Seek to clip start" : "Play clip range"}
                                  aria-label={trimTarget ? "Seek to clip start" : "Play clip range"}
                                >
                                  {fmtSecs(clip.start_time)}
                                </span>
                                <span style={{ fontSize: '0.62rem', color: theme.textSub }}>→ {fmtSecs(clip.end_time)}</span>
                                <span style={{ fontSize: '0.6rem', color: theme.textSub }}>({dur}s)</span>
                                <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '1px 6px', borderRadius: '8px', background: `${cat.color}22`, color: cat.color }}>{clip.category}</span>
                                {clip.use_for_pronunciation && (
                                  <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '1px 6px', borderRadius: '8px', background: `${theme.glossaryAccent}18`, color: theme.glossaryAccent, border: `1px solid ${theme.glossaryAccent}40` }}>
                                    Name Clip
                                  </span>
                                )}
                                <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '1px 6px', borderRadius: '8px', background: statusStyle.bg, color: statusStyle.color, marginLeft: 'auto' }}>{clip.status.toUpperCase()}</span>
                              </div>
                              {/* IN/OUT precise timestamps */}
                              <div style={{ display: 'flex', gap: '12px', marginBottom: '6px', fontFamily: 'monospace', fontSize: '0.58rem' }}>
                                <span><span style={{ fontWeight: 800, color: '#4ade80' }}>IN</span> <span style={{ color: theme.textSub }}>{formatVTTTime(clip.start_time)}</span></span>
                                <span><span style={{ fontWeight: 800, color: '#f87171' }}>OUT</span> <span style={{ color: theme.textSub }}>{formatVTTTime(clip.end_time)}</span></span>
                              </div>

                              {/* Swedish quote */}
                              {(clip.quote_text_sv || clip.quote_text) && (
                                <div style={{ marginBottom: '3px', paddingLeft: '7px', borderLeft: `2px solid ${cat.color}` }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                                    {renderClipLanguagePill()}
                                  </div>
                                  <div style={{ fontSize: '0.78rem', color: theme.text, fontStyle: 'italic', lineHeight: 1.5 }}>
                                    "{clip.quote_text_sv || clip.quote_text}"
                                  </div>
                                </div>
                              )}
                              {/* English quote */}
                              {clip.quote_text_en && (
                                <div style={{ fontSize: '0.7rem', color: theme.textSub, fontStyle: 'italic', lineHeight: 1.4, marginBottom: '6px', paddingLeft: '7px', borderLeft: `2px solid ${cat.color}30` }}>
                                  "{clip.quote_text_en}"
                                </div>
                              )}
                              {/* Reason */}
                              {clip.reason && !questionLabel && (
                                <div style={{ fontSize: '0.68rem', color: theme.textSub, marginBottom: '6px' }}>{clip.reason}</div>
                              )}
                              {/* Flag notes */}
                              {clip.status === 'flagged' && clip.flag_notes && (
                                <div style={{ fontSize: '0.68rem', color: '#ef4444', background: '#ef444415', borderRadius: '5px', padding: '4px 8px', marginBottom: '6px', display: 'flex', gap: '5px' }}>
                                  <Icon icon={Flag} size={10} style={{ flexShrink: 0, marginTop: '1px' }} />{clip.flag_notes}
                                </div>
                              )}

                              {/* Actions */}
                              <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginTop: '4px' }}>
                                {/* Trim */}
                                <button
                                  onClick={() => { setTrimTarget(clip); setTrimStart(clip.start_time); setTrimEnd(clip.end_time); if (videoRef.current) { videoRef.current.currentTime = clip.start_time; videoRef.current.pause(); } }}
                                  style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', background: theme.card, color: '#38bdf8', border: '1px solid #38bdf840', borderRadius: '5px', cursor: 'pointer', fontSize: '0.65rem', fontWeight: 700 }}
                                >
                                  <Scissors size={10} /> Trim
                                </button>
                                {!clip.use_for_pronunciation && (
                                  <button
                                    onClick={() => {
                                      axios.post(`${BACKEND_URL}/clips/${clip.id}/duplicate?for_pronunciation=true`)
                                        .then(r => {
                                          const dup = r.data;
                                          setTrimTarget(dup);
                                          setTrimStart(dup.start_time);
                                          setTrimEnd(dup.end_time);
                                          if (videoRef.current) {
                                            videoRef.current.currentTime = dup.start_time;
                                            videoRef.current.pause();
                                          }
                                          loadEditorClips();
                                        });
                                    }}
                                    style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', background: `${theme.glossaryAccent}12`, color: theme.glossaryAccent, border: `1px solid ${theme.glossaryAccent}40`, borderRadius: '5px', cursor: 'pointer', fontSize: '0.65rem', fontWeight: 700 }}
                                  >
                                    <Icon icon={Play} size={10} /> Name Edit
                                  </button>
                                )}
                                {clip.status !== 'approved' && clip.status !== 'exported' && (
                                  <button onClick={() => actClip(clip.id, 'approve')} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', background: '#052e16', color: '#4ade80', border: '1px solid #22c55e40', borderRadius: '5px', cursor: 'pointer', fontSize: '0.65rem', fontWeight: 700 }}>
                                    <Icon icon={ThumbsUp} size={10} /> Approve
                                  </button>
                                )}
                                {clip.status === 'approved' && (
                                  <button onClick={() => actClip(clip.id, 'unapprove')} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', background: isDarkMode ? '#1a1a00' : '#fefce8', color: '#eab308', border: '1px solid #eab30840', borderRadius: '5px', cursor: 'pointer', fontSize: '0.65rem', fontWeight: 700 }}>
                                    <Icon icon={RotateCcw} size={10} /> Unapprove
                                  </button>
                                )}
                                {clip.status !== 'rejected' && (
                                  <button onClick={() => actClip(clip.id, 'reject')} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', background: theme.card, color: theme.textSub, border: `1px solid ${theme.border}`, borderRadius: '5px', cursor: 'pointer', fontSize: '0.65rem' }}>
                                    <Icon icon={ThumbsDown} size={10} /> Reject
                                  </button>
                                )}
                                <button
                                  onClick={() => axios.put(`${BACKEND_URL}/clips/${clip.id}/pronunciation?value=${clip.use_for_pronunciation ? 'false' : 'true'}`)
                                    .then(r => updateClipInPlace(r.data))
                                    .catch(e => pushToast('pronunciation-err', 'error', `Update failed: ${e?.response?.data?.detail || e.message}`))}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '3px 8px',
                                    background: clip.use_for_pronunciation ? `${theme.glossaryAccent}18` : theme.card,
                                    color: clip.use_for_pronunciation ? theme.glossaryAccent : theme.textSub,
                                    border: `1px solid ${clip.use_for_pronunciation ? `${theme.glossaryAccent}55` : theme.border}`,
                                    borderRadius: '5px',
                                    cursor: 'pointer',
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                  }}
                                >
                                  <Icon icon={clip.use_for_pronunciation ? Check : Play} size={10} /> {clip.use_for_pronunciation ? 'Name Clip' : 'Use for Name'}
                                </button>
                                <button onClick={() => {
                                  if (clip.status === 'flagged') {
                                    axios.put(`${BACKEND_URL}/clips/${clip.id}/unflag`).then(() => loadEditorClips());
                                  } else {
                                    setEditorClipsFlagTarget(clip); setEditorClipsFlagNotes(clip.flag_notes || '');
                                  }
                                }} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', background: clip.status === 'flagged' ? '#f9731620' : theme.card, color: '#f97316', border: `1px solid ${clip.status === 'flagged' ? '#f97316' : '#f9731640'}`, borderRadius: '5px', cursor: 'pointer', fontSize: '0.65rem', fontWeight: clip.status === 'flagged' ? 700 : 400 }}>
                                  <Icon icon={Flag} size={10} /> {clip.status === 'flagged' ? 'Unflag' : 'Flag'}
                                </button>
                                {(clip.status === 'approved' && !clip.use_for_pronunciation) && (
                                  <button onClick={async () => { const n = await prompt({ title: 'Export clip as MP4', message: 'Clip name (leave blank for default):', defaultValue: clip.reason || clip.category || '', confirmLabel: 'Export', allowEmpty: true }); if (n === null) return; axios.post(`${BACKEND_URL}/clips/${clip.id}/export`, null, { params: n.trim() ? { name: n.trim() } : {} }).then(r => { updateClipInPlace(r.data); axios.get(`${BACKEND_URL}/clips/files/${encodeURIComponent(getProjectRoot(selectedVideo))}`).then(fr => setClipFiles(fr.data)).catch(() => {}); }); }} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', background: '#1a1a2e', color: '#a78bfa', border: '1px solid #7c3aed40', borderRadius: '5px', cursor: 'pointer', fontSize: '0.65rem', fontWeight: 700 }}>
                                    <Icon icon={Video} size={10} /> Export MP4
                                  </button>
                                )}
                                {(clip.status === 'approved' && clip.category === 'memorable_quote') && (
                                  <button onClick={() => { axios.post(`${BACKEND_URL}/clips/${clip.id}/overlay`).then(r => { updateClipInPlace(r.data); pushToast('overlay-ok', 'success', 'Quote overlay video exported'); }).catch(e => pushToast('overlay-err', 'error', e?.response?.data?.detail || 'Overlay export failed')); }} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', background: '#1a0a2e', color: '#f472b6', border: '1px solid #f472b640', borderRadius: '5px', cursor: 'pointer', fontSize: '0.65rem', fontWeight: 700 }}>
                                    <Icon icon={Film} size={10} /> Overlay
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                }); })()}
              </div>

              {/* Flag modal — migrated to the Modal primitive. zIndex kept at
                  the original 20000 so its stacking is unchanged until the
                  full §03 z-index sweep. */}
              <Modal
                open={!!editorClipsFlagTarget}
                onClose={() => setEditorClipsFlagTarget(null)}
                title="Flag clip for review"
                width={400}
                showClose={false}
                zIndex={20000}
                theme={theme}
                footer={editorClipsFlagTarget && (<>
                  {editorClipsFlagTarget.status === 'flagged' && (
                    <Button variant="danger" size="sm" theme={theme} onClick={() => {
                      axios.put(`${BACKEND_URL}/clips/${editorClipsFlagTarget.id}/unflag`)
                        .then(() => { setEditorClipsFlagTarget(null); loadEditorClips(); });
                    }} style={{ marginRight: 'auto' }}>Remove Flag</Button>
                  )}
                  <Button variant="secondary" size="sm" theme={theme} onClick={() => setEditorClipsFlagTarget(null)}>Cancel</Button>
                  <button onClick={() => {
                    axios.put(`${BACKEND_URL}/clips/${editorClipsFlagTarget.id}/flag`, { flag_notes: editorClipsFlagNotes })
                      .then(() => { setEditorClipsFlagTarget(null); loadEditorClips(); });
                  }} style={{ padding: '6px 14px', background: '#f97316', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 700, fontSize: '0.75rem' }}>Submit Flag</button>
                </>)}
              >
                {editorClipsFlagTarget && (
                  <div style={{ fontSize: '0.75rem', color: theme.textSub, fontStyle: 'italic', marginBottom: '12px' }}>
                    "{(editorClipsFlagTarget.quote_text_sv || editorClipsFlagTarget.quote_text_en || '')?.slice(0, 100)}…"
                  </div>
                )}
                <Input
                  multiline
                  theme={theme}
                  value={editorClipsFlagNotes}
                  onChange={e => setEditorClipsFlagNotes(e.target.value)}
                  placeholder="Describe why this clip needs review…"
                  style={{ minHeight: '80px', fontSize: '0.8rem' }}
                />
              </Modal>
            </div>
          </>);
        })()}

        <ConversionQueue
          BACKEND_URL={BACKEND_URL}
          theme={theme}
          isDarkMode={isDarkMode}
          onJobDone={onConversionDone}
        />

        {/* ── GLOSSARY PALETTE (draggable floating) ── */}
        {showGlossary && (
          <div style={{ position: 'absolute', left: pos.x, top: pos.y, width: '360px', maxHeight: '75vh', background: theme.sidebar, border: `1px solid ${theme.border}`, borderRadius: '12px', zIndex: Z.overlay, display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>

            {/* Drag handle */}
            <div
              onMouseDown={onMouseDown}
              style={{ background: theme.card, padding: '12px', color: theme.text, cursor: 'grab', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', borderRadius: '10px 10px 0 0' }}
            >
              <b>Glossary</b>
              <button onClick={() => setShowGlossary(false)} style={{ background: 'none', border: 'none', color: theme.text, cursor: 'pointer', display:'flex', alignItems:'center' }}><Icon icon={X} size={16} /></button>
            </div>

            <div style={{ padding: '15px', overflowY: 'auto' }}>

              {/* Search */}
              <input
                placeholder="Search glossary..."
                value={glossarySearch}
                onChange={e => setGlossarySearch(e.target.value)}
                style={{ ...inputStyle, marginBottom: '10px' }}
              />

              {/* Add rule */}
              <div style={{ background: theme.card, padding: '10px', borderRadius: '8px', marginBottom: '10px' }}>
                <input placeholder="Error / misspelling" value={newMis} onChange={e => setNewMis(e.target.value)} style={{ ...inputStyle, marginBottom: '5px' }} />
                <input placeholder="Correction / fix"   value={newCor} onChange={e => setNewCor(e.target.value)} style={{ ...inputStyle, marginBottom: '8px' }} />
                {/* Scope toggle */}
                <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                  {['project', 'global'].map(s => {
                    const on = newScope === s;
                    const scopeBg = on ? (s === 'global' ? theme.accent : theme.glossaryAccent) : theme.inputBg;
                    return (
                    <button key={s} onClick={() => setNewScope(s)} style={{
                      flex: 1, padding: '5px', fontSize: '0.7rem', fontWeight: 'bold',
                      background: scopeBg,
                      color: on ? contrastText(scopeBg) : theme.textSub,
                      border: `1px solid ${on ? 'transparent' : theme.border}`,
                      borderRadius: '5px', cursor: 'pointer', textTransform: 'uppercase'
                    }}>
                      {s === 'project' ? 'This project' : <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.3 }}><span>Global</span><span style={{ fontSize: '0.6rem', opacity: 0.85 }}>(all projects)</span></span>}
                    </button>
                    );
                  })}
                </div>
                <Button
                  variant="secondary"
                  theme={theme}
                  fullWidth
                  loading={addingRule}
                  icon={Check}
                  onClick={() => {
                    if (addingRule) return;
                    const root = newScope === 'project' ? getProjectRoot(selectedVideo) : '';
                    setAddingRule(true);
                    axios.post(`${BACKEND_URL}/glossary`, { misspelling: newMis, correction: newCor, project_root: root })
                      .then(() => { refreshGlossary(); setNewMis(""); setNewCor(""); })
                      .catch(e => pushToast('glossary-add-err', 'error', `Add rule failed: ${e?.response?.data?.detail || e.message}`))
                      .finally(() => setAddingRule(false));
                  }}
                >
                  {addingRule ? 'Adding…' : 'Add Rule'}
                </Button>
              </div>

              {/* Batch actions */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                <Button
                  variant="success"
                  theme={theme}
                  icon={RefreshCw}
                  onClick={() => {
                    const root = getProjectRoot(selectedVideo);
                    axios.post(`${BACKEND_URL}/glossary/apply`, null, { params: { project_root: root } })
                      .then(res => { pushToast('glossary-apply-all', 'success', `Updated ${res.data.updated_rows} rows`); refreshMarks(); refreshGlossary(); });
                  }}
                  style={{ flex: 1 }}
                >
                  APPLY ALL
                </Button>
                <Button
                  variant="accent"
                  theme={theme}
                  icon={RotateCcw}
                  onClick={async () => {
                    const root = getProjectRoot(selectedVideo);
                    if (!await confirm({
                      message: 'Revert all auto-fixed pending marks for this project?',
                      title: 'Revert auto-fixes?',
                      confirmLabel: 'Revert',
                      destructive: true,
                    })) return;
                    axios.post(`${BACKEND_URL}/glossary/undo`, null, { params: { project_root: root } })
                      .then(res => { pushToast('glossary-undo', 'success', `Reverted ${res.data.reverted_rows} rows`); refreshMarks(); });
                  }}
                  style={{ flex: 1 }}
                >
                  UNDO AUTO
                </Button>
              </div>

              {/* Sort controls */}
              <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
                {['misspelling', 'correction'].map(k => (
                  <button
                    key={k}
                    onClick={() => setSortConfig(prev => ({ key: k, direction: prev.key === k && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                    style={{ flex: 1, padding: '5px', fontSize: theme.fsBadge, background: sortConfig.key === k ? theme.activeBorder : theme.card, color: sortConfig.key === k ? contrastText(theme.activeBorder) : theme.text, border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      {k.toUpperCase()}
                      {sortConfig.key === k && <Icon icon={sortConfig.direction === 'asc' ? ChevronUp : ChevronDown} size={11} />}
                    </span>
                  </button>
                ))}
              </div>

              {/* Rule list — grouped header */}
              {['project', 'global'].map(scope => {
                const currentRoot = getProjectRoot(selectedVideo);
                const scopeRules = processedGlossary.filter(g =>
                  scope === 'global' ? g.project_root === '' : g.project_root === currentRoot
                );
                if (scopeRules.length === 0) return null;
                return (
                  <div key={scope}>
                    <div style={{ fontSize: '0.65rem', fontWeight: 'bold', color: scope === 'global' ? theme.accent : theme.glossaryAccent, letterSpacing: '0.08em', padding: '8px 0 4px', textTransform: 'uppercase' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                        <Icon icon={scope === 'global' ? Globe : Folder} size={11} />
                        {scope === 'global' ? 'Global rules' : cleanLabel(currentRoot)}
                      </span>
                    </div>
                    {scopeRules.map(g => (
                      <div key={g.id} style={{ display: 'flex', background: theme.card, padding: '8px 10px', marginBottom: '4px', borderRadius: '6px', fontSize: theme.fsOriginalText, alignItems: 'center', borderLeft: `3px solid ${scope === 'global' ? theme.accent : theme.glossaryAccent}` }}>
                        <div style={{ flex: 1 }}>
                          <span style={{ color: theme.accent }}>{g.misspelling}</span>
                          <span style={{ color: theme.textSub }}> → </span>
                          <span>{g.correction}</span>
                        </div>
                        <Button
                          variant="danger-ghost"
                          size="sm"
                          theme={theme}
                          icon={Trash2}
                          onClick={async () => {
                            if (!await confirm({
                              message: `Delete rule "${g.misspelling} → ${g.correction}"?`,
                              title: 'Delete glossary rule?',
                              confirmLabel: 'Delete',
                              destructive: true,
                            })) return;
                            axios.delete(`${BACKEND_URL}/glossary/${g.id}`).then(refreshGlossary);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                );
              })}

              {processedGlossary.length === 0 && (
                <div style={{ color: theme.textSub, fontSize: theme.fsOriginalText, textAlign: 'center', padding: '10px' }}>
                  No glossary rules found.
                </div>
              )}
            </div>
          </div>
        )}

        {/* QR CODE DESIGNER — now full-screen via appMode (see QRDesignerPanel.jsx) */}
        {showShortcutsPalette && (
          <div
            style={{
              position: 'fixed',
              left: `${shortcutPalettePos.x}px`,
              top: `${shortcutPalettePos.y}px`,
              width: '300px',
              background: theme.sidebar,
              border: `1px solid ${theme.border}`,
              borderRadius: '14px',
              zIndex: Z.palette,
              boxShadow: '0 18px 48px rgba(0,0,0,0.22)',
              overflow: 'hidden',
            }}
          >
            <div
              onMouseDown={onShortcutPaletteMouseDown}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                padding: '10px 12px',
                background: theme.card,
                borderBottom: `1px solid ${theme.border}`,
                cursor: 'grab',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                <Icon icon={GripVertical} size={13} color={theme.textSub} />
                <Icon icon={Keyboard} size={14} color={theme.text} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '0.76rem', fontWeight: 700, color: theme.text }}>Shortcuts</div>
                  <div style={{ fontSize: '0.62rem', color: theme.textSub }}>Saved in your user preferences</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                <button
                  onMouseDown={e => e.stopPropagation()}
                  onClick={() => { setListeningShortcutId(null); resetShortcutBindings(); }}
                  style={{ padding: '4px 8px', background: theme.card, color: theme.textSub, border: `1px solid ${theme.border}`, borderRadius: '999px', cursor: 'pointer', fontSize: '0.65rem', fontWeight: 700 }}
                >
                  Reset
                </button>
                <button
                  onMouseDown={e => e.stopPropagation()}
                  onClick={() => { setListeningShortcutId(null); setShowShortcutsPalette(false); }}
                  style={{ background: 'none', border: 'none', color: theme.text, cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}
                >
                  <Icon icon={X} size={15} />
                </button>
              </div>
            </div>
            <div style={{ padding: '10px 12px 12px' }}>
              <div style={{ fontSize: '0.64rem', color: theme.textSub, marginBottom: '10px' }}>
                Click a binding to remap it.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {SHORTCUT_ACTIONS.map(action => (
                  <div
                    key={action.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '10px',
                      padding: '8px 0',
                      borderBottom: `1px solid ${theme.border}`,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '0.74rem', fontWeight: 700, color: theme.text }}>{action.label}</div>
                      <div style={{ fontSize: '0.62rem', color: theme.textSub }}>{action.hint}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                      <button
                        onClick={() => setListeningShortcutId(action.id)}
                        style={{
                          minWidth: '104px',
                          padding: '6px 10px',
                          background: listeningShortcutId === action.id ? `${theme.glossaryAccent}18` : theme.card,
                          color: listeningShortcutId === action.id ? theme.glossaryAccent : theme.text,
                          border: `1px solid ${listeningShortcutId === action.id ? `${theme.glossaryAccent}66` : theme.border}`,
                          borderRadius: '999px',
                          cursor: 'pointer',
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          fontFamily: 'monospace',
                        }}
                      >
                        {listeningShortcutId === action.id ? 'Press keys' : formatShortcutBinding(shortcutBindings[action.id])}
                      </button>
                      <button
                        onClick={() => assignShortcutBinding(action.id, null)}
                        title="Clear binding"
                        style={{
                          width: '26px',
                          height: '26px',
                          background: 'none',
                          color: theme.textSub,
                          border: `1px solid ${theme.border}`,
                          borderRadius: '999px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: 0,
                        }}
                      >
                        <Icon icon={X} size={11} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {listeningShortcutId && (
                <div style={{ marginTop: '10px', fontSize: '0.63rem', color: theme.textSub, lineHeight: 1.4 }}>
                  Press the new key combination. `Esc` cancels.
                </div>
              )}
            </div>
          </div>
        )}
        {showDirectoryPalette && (
          <div
            style={{
              position: 'fixed',
              left: `${directoryPalettePos.x}px`,
              top: `${directoryPalettePos.y}px`,
              width: 'min(760px, calc(100vw - 24px))',
              maxHeight: `calc(100vh - ${directoryPalettePos.y + 12}px)`,
              background: theme.sidebar,
              border: `1px solid ${theme.border}`,
              borderRadius: '14px',
              zIndex: Z.palette,
              boxShadow: '0 18px 48px rgba(0,0,0,0.22)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              onMouseDown={onDirectoryPaletteMouseDown}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                padding: '10px 12px',
                background: theme.card,
                borderBottom: `1px solid ${theme.border}`,
                cursor: 'grab',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                <Icon icon={GripVertical} size={13} color={theme.textSub} />
                <Icon icon={Folder} size={14} color={theme.text} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '0.76rem', fontWeight: 700, color: theme.text }}>Working Directory</div>
                  <div style={{ fontSize: '0.62rem', color: theme.textSub }}>
                    {activeConfig?.active_subdir ? `/${activeConfig.active_subdir.replace(/^\/+/, '')}` : 'Choose a folder and import subtitle files'}
                  </div>
                </div>
              </div>
              <button
                onMouseDown={e => e.stopPropagation()}
                onClick={() => { setShowDirectoryPalette(false); setConfigMessage(null); }}
                style={{ background: 'none', border: 'none', color: theme.text, cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}
              >
                <Icon icon={X} size={15} />
              </button>
            </div>
            <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
              {directorySelectorContent}
            </div>
          </div>
        )}
        {showSettings && (
          <div
            style={{
              position: 'fixed',
              left: `${settingsPalettePos.x}px`,
              top: `${settingsPalettePos.y}px`,
              width: 'min(960px, calc(100vw - 24px))',
              maxHeight: `calc(100vh - ${appShellOffset + 20}px)`,
              background: theme.sidebar,
              border: `1px solid ${theme.border}`,
              borderRadius: '14px',
              zIndex: Z.palette,
              boxShadow: '0 18px 48px rgba(0,0,0,0.22)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              onMouseDown={onSettingsPaletteMouseDown}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                padding: '10px 12px',
                background: theme.card,
                borderBottom: `1px solid ${theme.border}`,
                cursor: 'grab',
                flexShrink: 0,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                <Icon icon={GripVertical} size={13} color={theme.textSub} />
                <Icon icon={Settings} size={14} color={theme.text} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '0.76rem', fontWeight: 700, color: theme.text }}>Workspace Settings</div>
                  <div style={{ fontSize: '0.62rem', color: theme.textSub }}>Theme and text sizing</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                {[
                  { id: 'textsize',  label: <><Icon icon={Type} size={13} style={{marginRight:'5px'}} />Text Size</> },
                  { id: 'theme',     label: <><Icon icon={Palette} size={13} style={{marginRight:'5px'}} />Theme</> },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onMouseDown={e => e.stopPropagation()}
                    onClick={() => setSettingsTab(tab.id)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '999px',
                      border: `1px solid ${settingsTab === tab.id ? theme.activeBorder : theme.border}`,
                      cursor: 'pointer',
                      fontWeight: 700,
                      fontSize: '0.72rem',
                      background: settingsTab === tab.id ? `${theme.activeBorder}22` : theme.card,
                      color: settingsTab === tab.id ? theme.activeBorder : theme.text,
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
                <button
                  onMouseDown={e => e.stopPropagation()}
                  onClick={() => setShowSettings(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.text, display: 'flex', alignItems: 'center', padding: 0 }}
                >
                  <Icon icon={X} size={15} />
                </button>
              </div>
            </div>

            {/* ── Tab: Text Size ── */}
            {settingsTab === 'textsize' && (
              <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, padding: '12px', paddingRight: '8px' }}>

                {/* Two-column slider grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0 16px' }}>
                {[
                  { key: 'timestamp',    label: 'Timestamp',     hint: 'Timecode + badges', min: 10, max: 28 },
                  { key: 'originalText', label: 'Original Text', hint: 'Source text below timecode',        min: 10, max: 28 },
                  { key: 'editorText',   label: 'Editor Text',   hint: 'Editable correction textarea',                   min: 10, max: 28 },
                  { key: 'cardText',     label: 'Card Text',     hint: 'Dashboard, QA, timeline cards', min: 10, max: 22 },
                  { key: 'cardPadding',  label: 'Card Padding',  hint: 'Inner padding of mark cards',                   min: 6,  max: 30 },
                  { key: 'buttonSize',   label: 'Button Text',   hint: 'Buttons, filter tabs',  min: 10, max: 22 },
                  { key: 'partCard',     label: 'Tape Cards',    hint: 'Tape number and status text',               min: 8,  max: 22 },
                ].map(({ key, label, hint, min, max }) => (
                  <div key={key} style={{ padding: '12px 0', borderBottom: `1px solid ${theme.border}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
                      <div>
                        <span style={{ fontWeight: 'bold', fontSize: '0.88rem' }}>{label}</span>
                        <span style={{ fontSize: '0.72rem', color: theme.textSub, marginLeft: '10px' }}>{hint}</span>
                      </div>
                      <span style={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '0.9rem', color: theme.text, minWidth: '38px', textAlign: 'right' }}>
                        {fontSizes[key]}px
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '0.7rem', color: theme.textSub, minWidth: '24px' }}>{min}</span>
                      <input
                        type="range" min={min} max={max} value={fontSizes[key]}
                        onChange={e => updateFontSize(key, Number(e.target.value))}
                        style={{ flex: 1 }}
                      />
                      <span style={{ fontSize: '0.7rem', color: theme.textSub, minWidth: '24px', textAlign: 'right' }}>{max}</span>
                    </div>
                    {/* Live preview */}
                    <div style={{ marginTop: '10px', padding: '10px', background: theme.card, borderRadius: '8px', border: `1px solid ${theme.border}` }}>
                      {key === 'timestamp' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ color: theme.accent, fontWeight: 'bold', fontFamily: 'monospace', fontSize: `${fontSizes.timestamp}px` }}>00:12:34.000</span>
                          <span style={{ background: theme.card, color: theme.textSub, padding: '2px 8px', borderRadius: '12px', border: `1px solid ${theme.border}`, fontSize: `${Math.max(10, fontSizes.timestamp - 2)}px`, fontWeight: 'bold' }}><Icon icon={Wand2} size={11} style={{marginRight:'3px'}} />SMART FIX</span>
                          <span style={{ background: theme.card, color: theme.textSub, padding: '2px 8px', borderRadius: '12px', border: `1px solid ${theme.border}`, fontSize: `${Math.max(10, fontSizes.timestamp - 2)}px`, fontWeight: 'bold' }}><Icon icon={PenLine} size={11} style={{marginRight:'3px'}} />MANUAL</span>
                        </div>
                      )}
                      {key === 'originalText' && (
                        <div style={{ fontSize: `${fontSizes.originalText}px`, color: theme.textSub, fontStyle: 'italic' }}>
                          och det var fruktansvärt en lärdag kommer jag ihåg
                        </div>
                      )}
                      {key === 'editorText' && (
                        <textarea
                          readOnly
                          value="och det var fruktansvärt, en lördag kommer jag ihåg"
                          style={{ width: '100%', fontSize: `${fontSizes.editorText}px`, background: theme.inputBg, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '6px', padding: '8px', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                          rows={2}
                        />
                      )}
                      {key === 'cardPadding' && (
                        <div style={{ background: theme.card, padding: `${fontSizes.cardPadding}px`, borderRadius: '8px', border: `1px solid ${theme.border}` }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                            <span style={{ color: theme.accent, fontWeight: 'bold', fontFamily: 'monospace', fontSize: `${fontSizes.timestamp}px` }}>00:12:34.000</span>
                            <span style={{ background: theme.card, color: theme.textSub, padding: '2px 8px', borderRadius: '12px', border: `1px solid ${theme.border}`, fontSize: `${Math.max(10, fontSizes.timestamp - 2)}px`, fontWeight: 'bold' }}><Icon icon={Wand2} size={11} style={{marginRight:'3px'}} />SMART FIX</span>
                          </div>
                          <div style={{ fontSize: `${fontSizes.originalText}px`, color: theme.textSub, fontStyle: 'italic', marginBottom: '6px' }}>och det var fruktansvärt en lärdag</div>
                          <div style={{ height: '32px', background: theme.inputBg, border: `1px solid ${theme.border}`, borderRadius: '6px' }} />
                        </div>
                      )}
                      {key === 'cardText' && (
                        <div style={{ background: theme.card, padding: '12px', borderRadius: '8px', border: `1px solid ${theme.border}`, fontSize: `${fontSizes.cardText}px` }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <div style={{ fontWeight: 700, fontSize: `calc(${fontSizes.cardText}px + 1px)` }}>Historical term</div>
                            <span style={{ background: theme.card, color: theme.textSub, padding: '2px 8px', borderRadius: '12px', border: `1px solid ${theme.border}`, fontSize: `${Math.max(10, fontSizes.cardText - 1)}px`, fontWeight: 'bold' }}>HIGH</span>
                          </div>
                          <div style={{ color: theme.textSub, lineHeight: 1.5 }}>
                            This size applies to card bodies across the main workspace views.
                          </div>
                        </div>
                      )}
                      {key === 'partCard' && (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {/* single mock card */}
                          {[{ pending: 3, done: 7, active: false }, { pending: 0, done: 12, active: true }].map((c, i) => (
                            <div key={i} style={{
                              flex: 1, padding: '14px 10px 12px',
                              background: c.active ? (isDarkMode ? '#1a1a1a' : '#ffffff') : theme.card,
                              borderRadius: '12px',
                              border: c.active ? `2px solid ${theme.glossaryAccent}` : `1px solid ${theme.border}`,
                              boxShadow: c.active ? `0 0 0 3px ${theme.glossaryAccent}22` : 'none',
                            }}>
                              <div style={{ fontFamily: 'monospace', fontSize: `${fontSizes.partCard}px`, color: theme.textSub, fontWeight: 'bold', marginBottom: '6px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                Chaim David Szpigler
                              </div>
                              <div style={{ fontFamily: 'monospace', fontSize: `${fontSizes.partCard + 1}px`, color: c.active ? theme.glossaryAccent : theme.textSub, fontWeight: 'bold', letterSpacing: '0.06em', marginBottom: '8px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                <Icon icon={Film} size={10} color={c.active ? theme.glossaryAccent : theme.textSub} /> Tape {i + 1}
                              </div>
                              <div style={{ height: '1px', background: theme.border, margin: '0 5%', marginBottom: '8px' }} />
                              <div style={{ fontFamily: 'monospace', fontSize: `${fontSizes.partCard + 1}px`, fontWeight: 'bold', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0' }}>
                                <span style={{ color: c.pending > 0 ? theme.accent : theme.textSub }}>{c.pending} PENDING</span>
                                <span style={{ color: theme.border, margin: '0 6px' }}>|</span>
                                <span style={{ color: theme.textSub }}>{c.done} DONE</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {key === 'buttonSize' && (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button style={{ padding: '6px 12px', background: theme.success, color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 'bold', fontSize: `${fontSizes.buttonSize}px`, fontFamily: 'monospace' }}><Icon icon={CheckSquare} size={13} style={{marginRight:'5px'}} />COMPLETE ALL TAPE 1</button>
                          <button style={{ padding: '6px 12px', background: theme.glossaryAccent, color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 'bold', fontSize: `${fontSizes.buttonSize}px` }}>DOWNLOAD VTT</button>
                          <button style={{ padding: '6px', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '6px', fontSize: `${fontSizes.buttonSize}px`, color: theme.text }}>ALL</button>
                          <button style={{ padding: '6px', background: theme.accent, border: 'none', borderRadius: '6px', fontSize: `${fontSizes.buttonSize}px`, color: contrastText(theme.accent), fontWeight: 'bold' }}>PENDING</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                </div>

                {/* Reset */}
                <div style={{ paddingTop: '14px' }}>
                  <Button
                    variant="secondary"
                    theme={theme}
                    onClick={() => {
                      const defaults = { timestamp: 14, originalText: 13, editorText: 14, cardText: 12, cardPadding: 15, buttonSize: 13, partCard: 11 };
                      setFontSizes(defaults);
                      localStorage.setItem('editorFontSizes', JSON.stringify(defaults));
                    }}
                  >
                    Reset to defaults
                  </Button>
                </div>
              </div>
            )}

            {/* ── Tab: Theme ── */}
            {settingsTab === 'theme' && (
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0, padding: '12px' }}>

                {(
                  /* ── SCHEME PICKER ── three validated schemes, no user authoring */
                  <div style={{ overflowY: 'auto', paddingRight: '4px' }}>

                    <div style={{ fontSize: '0.65rem', color: theme.textSub, fontWeight: 'bold', letterSpacing: '0.09em', marginBottom: '10px' }}>COLOUR SCHEME</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '18px' }}>
                      {Object.entries(COLOR_SCHEMES).map(([key, scheme]) => {
                        const isActive = activeScheme === key;
                        const t = isDarkMode ? scheme.dark : scheme.light;
                        return (
                          <div
                            key={key}
                            onClick={() => setScheme(key)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '10px',
                              padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
                              background: isActive ? `${t.highlight}18` : theme.card,
                              border: isActive ? `2px solid ${t.highlight}` : `1px solid ${theme.border}`,
                              transition: 'all 0.15s',
                            }}
                          >
                            {/* Mini preview in the scheme's OWN colours (current mode):
                                its background, a text line, a card strip and its accent.
                                The two 16px squares before this were near-identical across
                                schemes; this shows warm vs cool vs neutral and the accent
                                at a glance. */}
                            <div style={{ width: '56px', height: '38px', borderRadius: '6px', background: t.bg, border: `1px solid ${t.border}`, flexShrink: 0, padding: '5px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', overflow: 'hidden', boxSizing: 'border-box' }}>
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: t.accent, flexShrink: 0 }} />
                                <div style={{ flex: 1, height: '4px', borderRadius: '2px', background: t.text, opacity: 0.75 }} />
                              </div>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <div style={{ flex: 1, height: '11px', borderRadius: '3px', background: t.card, border: `1px solid ${t.border}` }} />
                                <div style={{ width: '16px', height: '11px', borderRadius: '3px', background: t.highlight }} />
                              </div>
                            </div>
                            <div>
                              <div style={{ fontWeight: 'bold', fontSize: '0.82rem', color: theme.text }}>{scheme.label}</div>
                              {isActive && <div style={{ fontSize: '0.62rem', color: t.highlight, fontWeight: 'bold', letterSpacing: '0.05em', marginTop: '1px' }}>ACTIVE</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Preview of active scheme */}
                    <div style={{ borderTop: `1px solid ${theme.border}`, paddingTop: '14px' }}>
                      <div style={{ fontSize: '0.65rem', color: theme.textSub, fontWeight: 'bold', letterSpacing: '0.09em', marginBottom: '10px' }}>ACTIVE PREVIEW</div>
                      <div style={{ background: theme.bg, borderRadius: '8px', padding: '12px', border: `1px solid ${theme.border}`, fontFamily: theme.fontFamily }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                          <div style={{ display: 'flex', gap: '7px' }}>
                            <div style={{ flex: 1, background: theme.card, borderRadius: '5px', padding: '7px 10px', fontSize: '0.75rem', color: theme.text, border: `1px solid ${theme.border}` }}>Card</div>
                            <div style={{ flex: 1, background: theme.sidebar, borderRadius: '5px', padding: '7px 10px', fontSize: '0.75rem', color: theme.text, border: `1px solid ${theme.border}` }}>Sidebar</div>
                          </div>
                          <div style={{ display: 'flex', gap: '7px' }}>
                            <button style={{ flex: 1, padding: '6px', background: theme.accent, color: contrastText(theme.accent), border: 'none', borderRadius: '5px', fontWeight: 'bold', fontSize: '0.72rem', cursor: 'default', fontFamily: theme.fontFamily }}>MARK ERROR</button>
                            <button style={{ flex: 1, padding: '6px', background: theme.highlight, color: theme.highlightText, border: 'none', borderRadius: '5px', fontWeight: 'bold', fontSize: '0.72rem', cursor: 'default', fontFamily: theme.fontFamily }}>Action</button>
                            <button style={{ flex: 1, padding: '6px', background: theme.card, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '5px', fontSize: '0.72rem', cursor: 'default', fontFamily: theme.fontFamily }}>Button</button>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: theme.timecode, background: '#000', padding: '3px 9px', borderRadius: '5px', display: 'inline-block', boxShadow: `0 0 7px ${theme.timecode}44` }}>00:12:34.500</div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              {[Download, CheckSquare, Trash2].map((Ic, i) => (
                                <Icon key={i} icon={Ic} size={16} />
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                  </div>
                )}
              </div>
            )}

          </div>
        )}

        {/* ─────────── ACCESS REQUEST NOTIFICATION MODAL ─────────── */}

        {/* ─────────── IDLE WARNING MODAL ─────────── */}
        {showIdleWarning && (
          // The session-timeout warning sits ABOVE any open modal via Z.idle — it
          // is the one thing that interrupts. No onClose / closeOnBackdrop /
          // showClose: the countdown must not be dismissable by Esc or a backdrop
          // click, only by the Continue / Sign Out buttons below.
          <Modal
            open
            theme={theme}
            width={360}
            zIndex={Z.idle}
            showClose={false}
            closeOnBackdrop={false}
            backdropStyle={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
            bodyStyle={{ background: isDarkMode ? '#1a1a2e' : '#ffffff', border: `1.5px solid ${isDarkMode ? '#2d2d4e' : '#e5e7eb'}`, borderRadius: '16px', padding: '32px 36px', textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,0.4)' }}
          >
              {/* Icon + title */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '14px' }}>
                <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: 'rgba(245,158,11,0.12)', border: '2px solid rgba(245,158,11,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon icon={Timer} size={24} color="#f59e0b" />
                </div>
              </div>
              <div style={{ fontWeight: 800, fontSize: '1.1rem', color: isDarkMode ? '#f1f5f9' : '#111827', marginBottom: '8px' }}>
                Session Idle
              </div>
              <div style={{ fontSize: '0.82rem', color: isDarkMode ? '#94a3b8' : '#6b7280', marginBottom: '20px', lineHeight: 1.5 }}>
                You've been inactive for {idleMinutes} minute{idleMinutes !== 1 ? 's' : ''}.<br />
                You'll be signed out automatically in:
              </div>
              {/* Countdown ring — turns red at ≤10, flashes each second */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
                <div style={{ position: 'relative', width: '80px', height: '80px' }}>
                  <svg width="80" height="80" style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx="40" cy="40" r="34" fill="none"
                      stroke={isDarkMode ? '#2d2d4e' : '#e5e7eb'} strokeWidth="6" />
                    <circle cx="40" cy="40" r="34" fill="none"
                      stroke={idleCountdown <= 10 ? '#ef4444' : '#f59e0b'}
                      strokeWidth="6"
                      strokeDasharray={`${2 * Math.PI * 34}`}
                      strokeDashoffset={`${2 * Math.PI * 34 * (1 - idleCountdown / 60)}`}
                      strokeLinecap="round"
                      style={{ transition: 'stroke-dashoffset 0.9s linear, stroke 0.3s' }}
                    />
                  </svg>
                  <div style={{
                    position: 'absolute', inset: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: '1.4rem',
                    color: idleCountdown <= 10 ? '#ef4444' : '#f59e0b',
                    animation: idleCountdown <= 10 ? 'gwv-flash 1s step-start infinite' : 'none',
                    transition: 'color 0.3s',
                  }}>
                    {idleCountdown}
                  </div>
                </div>
              </div>
              <style>{`
                @keyframes gwv-flash {
                  0%, 100% { opacity: 1; }
                  50%       { opacity: 0.15; }
                }
              `}</style>
              {/* Buttons */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <Button variant="success" size="lg" theme={theme} onClick={() => {
                  if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
                  warningActiveRef.current = false;
                  setShowIdleWarning(false);
                  setIdleCountdown(60);
                  // Re-arm idle timer
                  if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
                  idleTimerRef.current = setTimeout(startCountdown, idleMinutes * 60 * 1000);
                }} style={{ flex: 1 }}>
                  Continue
                </Button>
                <Button variant="danger-ghost" size="lg" theme={theme} onClick={() => { setShowIdleWarning(false); doLogout(); }}
                  style={{ flex: 1 }}>
                  Sign Out
                </Button>
              </div>
          </Modal>
        )}

        {/* ─────────── LOGIN MODAL ─────────── */}
        {showLogin && (
          <LoginModal
            BACKEND_URL={BACKEND_URL}
            googleClientId={googleClientId}
            theme={theme}
            onLogin={doLogin}
            isDarkMode={isDarkMode}
            onToggleDark={() => { const next = !isDarkMode; setIsDarkMode(next); localStorage.setItem('darkMode', next); }}
          />
        )}

      {/* ── TOAST NOTIFICATIONS ── */}
      {/* Confirm dialog — rendered at app level so it overlays everything
          and survives the unmount of whatever asked for it. */}
      {pendingPrompt && (
        <PromptDialog
          message={pendingPrompt.message}
          title={pendingPrompt.title}
          defaultValue={pendingPrompt.defaultValue}
          placeholder={pendingPrompt.placeholder}
          choices={pendingPrompt.choices}
          confirmLabel={pendingPrompt.confirmLabel}
          cancelLabel={pendingPrompt.cancelLabel}
          theme={theme}
          onResolve={resolvePrompt}
        />
      )}

      {pendingConfirm && (
        <ConfirmDialog
          message={pendingConfirm.message}
          title={pendingConfirm.title}
          confirmLabel={pendingConfirm.confirmLabel}
          cancelLabel={pendingConfirm.cancelLabel}
          destructive={pendingConfirm.destructive}
          theme={theme}
          onResolve={resolveConfirm}
        />
      )}

      <ToastBar toasts={toasts} onDismiss={dismissToast} history={toastHistory} onClearHistory={() => setToastHistory([])} />

      {/* ── PROFILE MODAL ── */}
      {showProfileModal && (
        <ProfileModal
          theme={theme}
          currentUser={currentUser}
          axiosAuth={axiosAuth}
          onClose={() => setShowProfileModal(false)}
          onUpdated={(updated) => setCurrentUser(u => ({ ...u, ...updated }))}
        />
      )}

      {/* ── USERS MODAL ── */}
      {showUsersModal && currentUser?.role === 'admin' && (
        <Modal
          open
          onClose={() => setShowUsersModal(false)}
          theme={theme}
          width={680}
          zIndex={Z.modal}
          maxHeight="85vh"
          backdropStyle={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)' }}
          header={(
            <div style={{ flex: 1, fontWeight: 'bold', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon icon={Users} size={16} color={theme.glossaryAccent} />Manage Users
            </div>
          )}
        >
              <UsersPanel axiosAuth={axiosAuth} currentUser={currentUser} theme={theme} BACKEND_URL={BACKEND_URL} videoList={videoList} />
        </Modal>
      )}

    </div>
    </div>
    </PromptProvider>
    </ConfirmProvider>
    </ToastProvider>
    </IconStrokeContext.Provider>
  );
}

export default App;
